<template>
  <div class="client-dashboard">
    <!-- Access Control Check -->
    <div
      v-if="!hasAccess"
      class="access-denied-container d-flex align-items-center justify-content-center"
    >
      <div class="access-denied-content text-center px-2">
        <div class="access-denied-icon mb-2">
          <feather-icon icon="LockIcon" size="48" />
        </div>
        <h4 class="mb-1">Access Denied</h4>
        <p class="text-muted mb-1">
          You don't have permission to access the Client Dashboard.
        </p>
        <p class="text-muted small mb-2">
          Required: <code>read client_dashboard</code>, <code>manage client_dashboard</code>, or <code>manage all</code>
        </p>
        <b-button variant="primary" class="dashboard-action-btn" @click="$router.go(-1)">
          <feather-icon icon="ArrowLeftIcon" size="16" class="mr-1" />
          Go Back
        </b-button>
      </div>
    </div>

    <!-- Dashboard Content -->
    <b-container v-else fluid class="px-0">
      <!-- Page Header (client name lives beside "CLIENT DASHBOARD" in navbar PageTitle) -->
      <div class="dashboard-page-header d-flex flex-wrap align-items-center justify-content-end mb-2 pb-2">
        <div class="dashboard-page-header__actions d-flex flex-wrap align-items-center">
          <div
            v-if="canManageDashboardSettings"
            class="dashboard-page-header__admin mr-1 mb-1 mb-sm-0"
          >
            <b-form-checkbox
              v-model="documentCheckEnabled"
              switch
              @change="toggleDocumentCheck"
            >
              <span class="text-nowrap small">Require Documents</span>
            </b-form-checkbox>
          </div>
          <b-button
            variant="outline-primary"
            size="sm"
            class="btn-icon mr-1 mb-1 mb-sm-0"
            aria-label="Refresh"
            v-b-tooltip.hover
            title="Refresh"
            :disabled="poolBusy"
            @click="refreshDashboard"
          >
            <feather-icon icon="RefreshCwIcon" size="16" />
          </b-button>
          <b-button
            variant="outline-secondary"
            size="sm"
            class="btn-icon mb-1 mb-sm-0"
            aria-label="Back"
            v-b-tooltip.hover
            title="Back"
            @click="goToClientSelection"
          >
            <feather-icon icon="ArrowLeftIcon" size="16" />
          </b-button>
        </div>
      </div>

      <!-- Initial load -->
      <div
        v-if="loading && !reportsCandidates.length"
        class="dashboard-loading text-center py-4 mb-2"
      >
        <b-spinner variant="primary" label="Loading..." />
        <p class="mt-2 mb-0 text-muted small">
          Loading {{ selectedClientName || 'client' }} candidates...
        </p>
        <p v-if="loadingProgress" class="small text-muted mt-1 mb-0">{{ loadingProgress }}</p>
      </div>

      <b-alert
        v-else-if="apiError"
        variant="danger"
        dismissible
        class="mb-2"
        @dismissed="apiError = null"
      >
        <feather-icon icon="AlertCircleIcon" size="16" class="mr-1" />
        {{ apiError }}
      </b-alert>

      <!-- Summary Metrics -->
      <div
        v-else
        class="metrics-strip d-flex flex-nowrap mb-2"
      >
        <div
          v-for="metric in summaryMetrics"
          :key="metric.label"
          class="metric-pill"
        >
          <span class="metric-pill__value">{{ metric.value }}</span>
          <span class="metric-pill__label">{{ metric.label }}</span>
        </div>
      </div>

      <!-- Assignment Modal -->
    <b-modal
      v-model="showBusinessModal"
      title="Assign Candidate to POC"
      size="md"
      modal-class="cd-modal"
      @hidden="resetAssignmentForm"
    >
      <div v-if="selectedCandidate">
        <div class="mb-2">
          <strong>{{ selectedCandidate.candidate_name }}</strong>
          <p class="text-muted mb-0 small">
            ID {{ selectedCandidate.id }}
            <span v-if="selectedCandidate.position_name"> · {{ selectedCandidate.position_name }}</span>
          </p>
        </div>

        <b-form @submit.prevent="assignCandidate">
          <b-form-group label="Select POC" label-for="poc-select" class="mb-0">
            <v-select
              id="poc-select"
              v-model="assignmentForm.poc"
              :options="pocOptions"
              label="text"
              :reduce="option => option.value"
              placeholder="Select POC"
              required
            />
          </b-form-group>
        </b-form>
      </div>

      <template #modal-footer>
        <b-button size="sm" variant="secondary" class="dashboard-action-btn" @click="showBusinessModal = false">
          Cancel
        </b-button>
        <b-button
          size="sm"
          variant="primary"
          class="dashboard-action-btn"
          :disabled="!assignmentForm.poc"
          @click="assignCandidate"
        >
          Assign to POC
        </b-button>
      </template>
    </b-modal>

    <!-- Bulk Assignment Modal -->
    <b-modal
      v-model="showPOCModal"
      title="Bulk Assign Candidates to POC"
      size="md"
      modal-class="cd-modal"
      @hidden="resetBulkAssignmentForm"
    >
      <p class="mb-2">
        <strong>{{ selectedCandidates.length }}</strong> candidates selected for assignment
      </p>

      <b-form @submit.prevent="bulkAssignCandidates">
        <b-form-group label="Select POC" label-for="bulk-poc-select" class="mb-0">
          <v-select
            id="bulk-poc-select"
            v-model="bulkAssignmentForm.poc"
            :options="pocOptions"
            label="text"
            :reduce="option => option.value"
            placeholder="Select POC"
            required
          />
        </b-form-group>
      </b-form>

      <template #modal-footer>
        <b-button size="sm" variant="secondary" class="dashboard-action-btn" @click="showPOCModal = false">
          Cancel
        </b-button>
        <b-button
          size="sm"
          variant="primary"
          class="dashboard-action-btn"
          :disabled="!bulkAssignmentForm.poc"
          @click="bulkAssignCandidates"
        >
          Assign All Candidates
        </b-button>
      </template>
    </b-modal>

    <!-- Offer Letter Generation Modal -->
    <b-modal
      v-model="showOfferLetterModal"
      title="Generate Offer Letter"
      size="lg"
      modal-class="offer-letter-modal cd-modal"
      dialog-class="modal-dialog-scrollable"
      @hidden="resetOfferLetterForm"
    >
      <div v-if="selectedCandidate" class="offer-letter-form">
        <div class="offer-flow-steps mb-2">
          <div class="offer-flow-step offer-flow-step--active">
            <span class="offer-flow-step__num">1</span>
            <span class="offer-flow-step__label">Details</span>
          </div>
          <div class="offer-flow-step-divider" />
          <div class="offer-flow-step offer-flow-step--active">
            <span class="offer-flow-step__num">2</span>
            <span class="offer-flow-step__label">Site &amp; Role</span>
          </div>
          <div class="offer-flow-step-divider" />
          <div class="offer-flow-step">
            <span class="offer-flow-step__num">3</span>
            <span class="offer-flow-step__label">Generate</span>
          </div>
        </div>

        <div class="offer-candidate-banner mb-2">
          <strong class="d-block">{{ selectedCandidate.candidate_name }}</strong>
          <span class="text-muted small">
            ID {{ selectedCandidate.id }}
            <span v-if="selectedCandidate.position_name"> · {{ selectedCandidate.position_name }}</span>
          </span>
        </div>

        <!-- Document Validation -->
        <div class="mb-2">
          <h6 class="offer-section-title mb-1">Document Verification</h6>
          <div class="d-flex flex-wrap">
            <b-badge
              v-for="doc in ['aadhaar', 'pan', 'photo', 'bankProof']"
              :key="doc"
              :variant="(selectedCandidate.documents && selectedCandidate.documents[doc]) || (doc === 'aadhaar' && hasAadhaarDocument(selectedCandidate)) ? 'success' : 'danger'"
              class="mr-1 mb-1 text-capitalize"
            >
              {{ doc.replace(/([A-Z])/g, ' $1') }}
              ·
              {{ (selectedCandidate.documents && selectedCandidate.documents[doc]) || (doc === 'aadhaar' && hasAadhaarDocument(selectedCandidate)) ? 'OK' : 'Missing' }}
            </b-badge>
          </div>
          <b-alert
            v-if="documentCheckEnabled && !checkDocumentsAvailable(selectedCandidate)"
            variant="warning"
            show
            class="mt-2 mb-0 py-2"
          >
            <feather-icon icon="AlertTriangleIcon" size="14" class="mr-1" />
            All required documents must be available before generating.
          </b-alert>
        </div>

        <b-form @submit.prevent="generateOfferLetter">
          <h6 class="offer-section-title mb-1">Candidate Details</h6>
          <b-row>
            <b-col cols="12" md="6">
              <b-form-group label="Candidate Name" label-for="candidate-name" class="mb-2">
                <b-form-input
                  id="candidate-name"
                  v-model="offerLetterForm.candidateName"
                  size="sm"
                  placeholder="Enter candidate name"
                  required
                />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="Email ID" label-for="email-id" class="mb-2">
                <b-form-input
                  id="email-id"
                  v-model="offerLetterForm.emailId"
                  type="email"
                  size="sm"
                  placeholder="Enter email address"
                  required
                />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group
                label="Joining Date"
                label-for="joining-date"
                class="mb-2"
                :invalid-feedback="joiningDateError"
                :state="joiningDateError ? false : null"
              >
                <b-form-datepicker
                  id="joining-date"
                  v-model="offerLetterForm.joiningDate"
                  size="sm"
                  :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
                  locale="en-GB"
                  :state="joiningDateError ? false : null"
                  placeholder="DD-MM-YYYY"
                  required
                />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="Offered Date" label-for="offered-date" class="mb-2">
                <b-form-datepicker
                  id="offered-date"
                  v-model="offerLetterForm.offeredDate"
                  size="sm"
                  :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
                  locale="en-GB"
                  placeholder="DD-MM-YYYY"
                  required
                />
              </b-form-group>
            </b-col>
          </b-row>

          <h6 class="offer-section-title mb-1">Site &amp; Role</h6>
          <b-row>
            <b-col cols="12" md="6">
              <b-form-group label="Site Name *" label-for="site" class="mb-2">
                <v-select
                  id="site"
                  v-model="offerLetterForm.site"
                  :options="siteOptions"
                  label="text"
                  :reduce="option => option.value"
                  placeholder="Select Site Name"
                  required
                  @input="onSiteChange"
                />
                <small
                  v-if="!siteOptions.length"
                  class="form-text text-danger d-flex align-items-center flex-wrap"
                >
                  No sites loaded for this client.
                  <b-button
                    variant="link"
                    size="sm"
                    class="p-0 ml-1 align-baseline"
                    @click="loadSiteAndLanguageOptions"
                  >
                    Retry
                  </b-button>
                </small>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="Language" label-for="offer-language" class="mb-2">
                <b-form-input
                  id="offer-language"
                  value="English"
                  size="sm"
                  plaintext
                  readonly
                />
                <small class="form-text text-muted">Generated in English.</small>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group
                label="Reporting Manager *"
                label-for="reporting-manager-name"
                class="mb-2"
                :invalid-feedback="reportingManagerNameError"
                :state="reportingManagerNameState"
              >
                <v-select
                  id="reporting-manager-name"
                  v-model="offerLetterForm.reportingManagerName"
                  :options="reportingManagerOptions"
                  label="text"
                  :reduce="option => option.value"
                  placeholder="Select reporting manager"
                  required
                  :clearable="true"
                  :searchable="true"
                />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="Job Title *" label-for="job-title" class="mb-2">
                <v-select
                  id="job-title"
                  v-model="offerLetterForm.jobTitle"
                  :options="jobTitleOptions"
                  label="text"
                  :reduce="option => option.value"
                  placeholder="Select Job Title"
                  required
                  :disabled="!offerLetterForm.site || jobTitleOptions.length === 0"
                />
                <small
                  v-if="offerLetterForm.site && jobTitleOptions.length === 0"
                  class="form-text text-muted"
                >
                  No job titles for this site yet.
                </small>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group
                label="Location *"
                label-for="location"
                class="mb-2"
                :invalid-feedback="locationError"
                :state="locationState"
              >
                <b-form-input
                  id="location"
                  v-model="offerLetterForm.location"
                  size="sm"
                  placeholder="Enter work location"
                  required
                  :state="locationState"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </b-form>
      </div>

      <template #modal-footer>
        <div class="w-100">
          <!-- Step 3: Recruiter Confirmation (compact, above Generate) -->
          <div class="recruiter-confirmation-strip mb-2">
            <div class="offer-section-title recruiter-confirmation-strip__label">Confirm</div>
            <div class="recruiter-confirmation-strip__checks">
              <b-form-checkbox v-model="recruiterConfirmed" size="sm">
                Candidate details verified and accurate
              </b-form-checkbox>
              <b-form-checkbox v-model="documentsVerified" size="sm">
                Required documents available and verified
              </b-form-checkbox>
              <b-form-checkbox v-model="offerDetailsConfirmed" size="sm">
                Offer details correct and approved
              </b-form-checkbox>
            </div>
          </div>
          <div class="d-flex justify-content-between align-items-center flex-wrap">
            <small class="text-muted mb-1 mb-sm-0 d-none d-sm-inline">Review, then generate PDF.</small>
            <div class="d-flex flex-wrap justify-content-end">
              <b-button
                variant="outline-secondary"
                size="sm"
                class="dashboard-action-btn mr-1 mb-1 mb-sm-0"
                @click="showOfferLetterModal = false"
              >
                Cancel
              </b-button>
              <b-button
                variant="primary"
                size="sm"
                class="dashboard-action-btn mb-1 mb-sm-0"
                :disabled="!isOfferLetterFormValid || !offerLetterForm.joiningDate || isGeneratingOfferLetter"
                @click="generateOfferLetter"
              >
                <b-spinner v-if="isGeneratingOfferLetter" small class="mr-1" />
                <feather-icon v-else icon="FileTextIcon" size="14" class="mr-1" />
                {{ isGeneratingOfferLetter ? 'Generating...' : 'Generate Offer Letter' }}
              </b-button>
            </div>
          </div>
        </div>
      </template>
    </b-modal>

    <!-- View Offer Letter Modal -->
    <b-modal
      v-model="showViewOfferLetterModal"
      title="Offer Letter"
      size="xl"
      @hidden="resetViewOfferLetter"
    >
      <div v-if="selectedOfferLetter">
        <div class="offer-letter-header mb-4">
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <h5 class="mb-1">{{ selectedOfferLetter.candidate_name }}</h5>
              <p class="text-muted mb-0">Candidate ID: {{ selectedOfferLetter.id }}</p>
            </div>
            <div class="text-right">
              <b-badge variant="success" class="mb-2">OFFER LETTER</b-badge>
              <p class="text-muted mb-0 small">Generated on {{ formatDate(selectedOfferLetter.offerDetails.offeredDate) }}</p>
            </div>
          </div>
        </div>

        <div class="offer-letter-content">
          <!-- Display PDF from S3 -->
          <div v-if="offerLetterPDFBase64" class="letter-body">
            <div class="d-flex justify-content-end mb-2">
              <b-button size="sm" variant="outline-secondary" @click="openOfferLetterPdfInNewTab">
                <feather-icon icon="ExternalLinkIcon" size="14" class="mr-1" />
                Open in new tab
              </b-button>
            </div>
            <!-- blob: URL — Chrome leaves data:application/pdf iframes blank -->
            <iframe
              :key="offerLetterPDFBase64"
              :src="offerLetterPDFBase64"
              type="application/pdf"
              title="Offer letter PDF"
              frameborder="0"
              height="800px"
              width="100%"
              style="border: 1px solid #ddd; background: #f5f5f5;"
            ></iframe>
          </div>
          
          <div v-else class="letter-body text-center">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Loading...</span>
            </div>
            <p class="mt-2">Loading offer letter PDF from S3...</p>
          </div>
        </div>
      </div>

      <template #modal-footer>
        <b-button variant="secondary" @click="resetViewOfferLetter">
          Close
        </b-button>
        <b-button
          v-if="canDownloadOffer"
          variant="primary"
          @click="downloadOfferLetter(selectedOfferLetter)"
        >
          <feather-icon icon="DownloadIcon" size="16" class="mr-1" />
          Download PDF
        </b-button>
      </template>
    </b-modal>

    <!-- Offer Letter Generation Error Modal -->
    <b-modal
      v-model="showOfferLetterErrorModal"
      title="Offer Letter Generation Failed"
      size="md"
      @hidden="showOfferLetterErrorModal = false"
      hide-footer
    >
      <div class="text-center">
        <feather-icon icon="XCircleIcon" size="48" class="text-danger mb-3" />
        <h5 class="mb-3">Failed to Generate Offer Letter</h5>
        <p class="text-muted mb-4">{{ offerLetterErrorMessage || 'An error occurred while generating the offer letter. Please try again.' }}</p>
        <b-button variant="primary" @click="showOfferLetterErrorModal = false">
          Close
        </b-button>
      </div>
    </b-modal>

    <!-- Post-Generation Offer Letter Actions Modal -->
    <b-modal
      v-model="showPostGenerationModal"
      title="Offer Letter Generated"
      size="md"
      modal-class="cd-modal"
      @hidden="resetPostGenerationModal"
    >
      <div v-if="generatedOfferLetterData">
        <div class="mb-2 small">
          <p class="mb-1"><strong>Candidate:</strong> {{ generatedOfferLetterData.offerDetails.candidateName }}</p>
          <p class="mb-1"><strong>Email:</strong> {{ generatedOfferLetterData.offerDetails.emailId }}</p>
          <p class="mb-0"><strong>Site:</strong> {{ generatedOfferLetterData.offerDetails.site }}</p>
        </div>

        <hr class="my-2">

        <p class="mb-2 small font-weight-bold">What would you like to do next?</p>
        <div class="d-flex flex-column flex-sm-row flex-wrap">
          <b-button
            v-if="canSendOfferLetterEmail"
            variant="success"
            size="sm"
            class="dashboard-action-btn flex-fill mr-sm-1 mb-1"
            @click="openSendEmailModalFromPostGeneration"
          >
            <feather-icon icon="MailIcon" size="14" class="mr-1" />
            Send Email
          </b-button>
          <b-button
            v-if="canViewOffer"
            variant="info"
            size="sm"
            class="dashboard-action-btn flex-fill mr-sm-1 mb-1"
            @click="previewGeneratedOfferLetter"
          >
            <feather-icon icon="EyeIcon" size="14" class="mr-1" />
            View Offer
          </b-button>
          <b-button
            v-if="canDownloadOffer"
            variant="primary"
            size="sm"
            class="dashboard-action-btn flex-fill mr-sm-1 mb-1"
            @click="downloadOfferLetterPDF"
          >
            <feather-icon icon="DownloadIcon" size="14" class="mr-1" />
            Download PDF
          </b-button>
          <b-button
            v-if="canGenerateOfferLetter"
            variant="warning"
            size="sm"
            class="dashboard-action-btn flex-fill mb-1"
            @click="regenerateOfferLetter"
          >
            <feather-icon icon="RefreshCwIcon" size="14" class="mr-1" />
            Re-generate
          </b-button>
        </div>
      </div>

      <template #modal-footer>
        <b-button size="sm" variant="secondary" class="dashboard-action-btn" @click="resetPostGenerationModal">
          Close
        </b-button>
      </template>
    </b-modal>

    <!-- Email Composition Modal -->
    <b-modal
      v-model="showSendEmailModal"
      title="Send Offer Letter via Email"
      size="lg"
      modal-class="cd-modal"
      dialog-class="modal-dialog-scrollable"
      @hidden="resetEmailModal"
      @shown="loadEmailTemplate"
    >
      <b-form @submit.prevent="sendComposedEmail">
        <!-- To Field -->
        <b-form-group 
          label="To *" 
          label-for="email-to"
        >
          <b-form-input
            id="email-to"
            v-model="emailForm.to"
            type="email"
            placeholder="Enter recipient email"
            required
          />
        </b-form-group>

        <!-- CC Field -->
        <b-form-group 
          label="CC" 
          label-for="email-cc"
        >
          <b-form-input
            id="email-cc"
            v-model="emailForm.cc"
            type="email"
            placeholder="Enter CC email addresses (comma separated)"
          />
        </b-form-group>

        <!-- BCC Field -->
        <b-form-group 
          label="BCC" 
          label-for="email-bcc"
        >
          <b-form-input
            id="email-bcc"
            v-model="emailForm.bcc"
            type="email"
            placeholder="Enter BCC email addresses (comma separated)"
          />
        </b-form-group>

        <!-- Subject Field -->
        <b-form-group 
          label="Subject *" 
          label-for="email-subject"
          :invalid-feedback="emailFormErrors.subject"
          :state="emailFormErrors.subject ? false : null"
        >
          <b-form-input
            id="email-subject"
            v-model="emailForm.subject"
            placeholder="Enter email subject"
            required
          />
        </b-form-group>

        <!-- Email Body with Quill Editor -->
        <b-form-group 
          label="Email Body *" 
          label-for="email-body"
          :invalid-feedback="emailFormErrors.body"
          :state="emailFormErrors.body ? false : null"
        >
          <quill-editor
            ref="quillEditor"
            id="email-body"
            v-model="emailForm.body"
            :options="quillEditorOptions"
            class="border-bottom-0"
          />
          <div
            id="quill-toolbar"
            class="d-flex justify-content-end border-top-0"
          >
            <button class="ql-bold" />
            <button class="ql-italic" />
            <button class="ql-underline" />
            <button class="ql-align" />
            <button class="ql-link" />
          </div>
        </b-form-group>

        <!-- Attach Offer Letter Checkbox -->
        <b-form-group>
          <b-form-checkbox
            v-model="emailForm.attachOfferLetter"
          >
            Attach Offer Letter (PDF)
          </b-form-checkbox>
        </b-form-group>
      </b-form>

      <template #modal-footer>
        <b-button 
          variant="secondary" 
          @click="resetEmailModal" 
          class="mr-2"
        >
          Cancel
        </b-button>
        <b-button 
          variant="success" 
          @click="sendComposedEmail"
          :disabled="!isEmailFormValid || isSendingEmail"
        >
          <b-spinner v-if="isSendingEmail" small class="mr-1"></b-spinner>
          <feather-icon v-else icon="SendIcon" size="16" class="mr-1" />
          {{ isSendingEmail ? 'Sending...' : 'Send Email' }}
        </b-button>
      </template>
    </b-modal>

    <!-- Bank Details Modal -->
    <b-modal
      v-model="showBankDetailsModal"
      title="Upload Bank Proof / Add Bank Details"
      size="lg"
      @hidden="resetBankDetailsModal"
      modal-class="cd-modal"
      dialog-class="modal-dialog-scrollable"
    >
      <div v-if="selectedBankCandidate">
        <div class="mb-2 small">
          <strong>{{ selectedBankCandidate.candidate_name }}</strong>
          <span class="text-muted"> · ID {{ selectedBankCandidate.id }}</span>
        </div>

        <!-- Show existing bank details if available -->
        <div v-if="isBankAccountDetailsAvailable(selectedBankCandidate)" class="mb-4">
          <b-alert variant="success" show>
            <feather-icon icon="CheckCircleIcon" size="16" class="mr-2" />
            <strong>Bank Details Already Available:</strong>
            <div class="mt-2">
              <p class="mb-1"><strong>Account Number:</strong> {{ getBankAccountNumber(selectedBankCandidate) }}</p>
              <p class="mb-0"><strong>IFSC Code:</strong> {{ getBankIFSCCode(selectedBankCandidate) }}</p>
            </div>
          </b-alert>
        </div>

        <!-- Account Details Form Fields -->
        <!-- Show when: Status = Shortlisted AND account details are missing in BOTH metadata AND extra_details -->
        <!-- If exists in one location, it will be automatically copied to the other without showing form -->
        <div v-if="selectedBankCandidate && (selectedBankCandidate.candidateStatus === 'Shortlisted' || selectedBankCandidate.candidateStatus === 'Offered' || selectedBankCandidate.candidateStatus === 'Joined') && ((needsAccountNumberInMetadata(selectedBankCandidate) && needsAccountNumberInExtraDetails(selectedBankCandidate)) || (needsIFSCCodeInMetadata(selectedBankCandidate) && needsIFSCCodeInExtraDetails(selectedBankCandidate)))">
          <b-form @submit.prevent="saveBankDetails">
            <!-- Show Account Number field only if missing in BOTH locations -->
            <b-form-group 
              v-if="needsAccountNumberInMetadata(selectedBankCandidate) && needsAccountNumberInExtraDetails(selectedBankCandidate)"
              label="Account Number *" 
              label-for="accountNumber"
            >
              <b-form-input
                id="accountNumber"
                v-model="bankDetailsForm.accountNumber"
                type="password"
                placeholder="Enter account number"
                required
                :class="{ 'is-invalid': bankDetailsForm.accountNumber && bankDetailsForm.confirmAccountNumber && bankDetailsForm.accountNumber !== bankDetailsForm.confirmAccountNumber }"
                @paste.prevent="preventPaste"
                @copy.prevent="preventCopy"
                @cut.prevent="preventCut"
                @contextmenu.prevent
                @keydown.ctrl.c.prevent
                @keydown.ctrl.v.prevent
                @keydown.ctrl.x.prevent
                @keydown.meta.c.prevent
                @keydown.meta.v.prevent
                @keydown.meta.x.prevent
                autocomplete="off"
              ></b-form-input>
              <small class="form-text text-muted">
                Please type the account number (copy-paste is disabled)
              </small>
            </b-form-group>

            <!-- Confirm Account Number field -->
            <b-form-group 
              v-if="needsAccountNumberInMetadata(selectedBankCandidate) && needsAccountNumberInExtraDetails(selectedBankCandidate)"
              label="Confirm Account Number *" 
              label-for="confirmAccountNumber"
            >
              <b-form-input
                id="confirmAccountNumber"
                v-model="bankDetailsForm.confirmAccountNumber"
                type="text"
                placeholder="Re-enter account number to confirm"
                required
                :class="{ 'is-invalid': bankDetailsForm.accountNumber && bankDetailsForm.confirmAccountNumber && bankDetailsForm.accountNumber !== bankDetailsForm.confirmAccountNumber, 'is-valid': bankDetailsForm.accountNumber && bankDetailsForm.confirmAccountNumber && bankDetailsForm.accountNumber === bankDetailsForm.confirmAccountNumber }"
                @paste.prevent="preventPaste"
                @copy.prevent="preventCopy"
                @cut.prevent="preventCut"
                @contextmenu.prevent
                @keydown.ctrl.c.prevent
                @keydown.ctrl.v.prevent
                @keydown.ctrl.x.prevent
                @keydown.meta.c.prevent
                @keydown.meta.v.prevent
                @keydown.meta.x.prevent
                autocomplete="off"
              ></b-form-input>
              <div v-if="bankDetailsForm.accountNumber && bankDetailsForm.confirmAccountNumber && bankDetailsForm.accountNumber !== bankDetailsForm.confirmAccountNumber" class="invalid-feedback">
                Account numbers do not match
              </div>
              <div v-if="bankDetailsForm.accountNumber && bankDetailsForm.confirmAccountNumber && bankDetailsForm.accountNumber === bankDetailsForm.confirmAccountNumber" class="valid-feedback">
                Account numbers match ✓
              </div>
              <small class="form-text text-muted">
                Please re-enter the account number (copy-paste is disabled)
              </small>
            </b-form-group>

            <!-- Show IFSC Code field only if missing in BOTH locations -->
            <b-form-group 
              v-if="needsIFSCCodeInMetadata(selectedBankCandidate) && needsIFSCCodeInExtraDetails(selectedBankCandidate)"
              label="IFSC Code *" 
              label-for="ifscCode"
            >
              <b-form-input
                id="ifscCode"
                v-model="bankDetailsForm.ifscCode"
                placeholder="Enter IFSC code"
                required
                maxlength="11"
                style="text-transform: uppercase;"
                @paste.prevent="preventPaste"
                @copy.prevent="preventCopy"
                @cut.prevent="preventCut"
                @contextmenu.prevent
                @keydown.ctrl.c.prevent
                @keydown.ctrl.v.prevent
                @keydown.ctrl.x.prevent
                @keydown.meta.c.prevent
                @keydown.meta.v.prevent
                @keydown.meta.x.prevent
                autocomplete="off"
              ></b-form-input>
              <small class="form-text text-muted">
                Please type the IFSC code (copy-paste is disabled)
              </small>
            </b-form-group>
          </b-form>
        </div>

        <!-- Bank Proof Upload -->
        <!-- Show only when bankProof document is NOT already available -->
        <div v-if="!selectedBankCandidate.documents || !selectedBankCandidate.documents.bankProof">
          <b-form-group label="Bank Proof Document" label-for="bankProofFile">
            <b-form-file
              id="bankProofFile"
              v-model="bankDetailsForm.bankProofFile"
              placeholder="Choose file or drop it here..."
              drop-placeholder="Drop file here..."
              accept="image/*,.pdf"
              :state="bankDetailsForm.bankProofFile ? null : null"
              @change="handleBankProofFileChange"
            >
              <template #file-name="{ names }">
                <b-badge variant="secondary" class="file-name-badge">{{ names[0] }}</b-badge>
              </template>
            </b-form-file>
            <small class="form-text text-muted">
              Upload bank passbook, cheque book, or bank statement (PDF, JPG, PNG)
            </small>
            <div v-if="bankDetailsForm.bankProofFile" class="mt-2">
              <b-badge variant="success" class="file-selected-badge">
                <feather-icon icon="CheckIcon" size="14" class="mr-1" />
                <span class="file-selected-text">
                  File selected: <span class="file-name-text">{{ bankDetailsForm.bankProofFile.name }}</span>
                </span>
              </b-badge>
            </div>
          </b-form-group>
        </div>
        
        <!-- Show message if Bank Proof is already available -->
        <div v-else class="mb-3">
          <b-alert variant="info" show>
            <feather-icon icon="InfoIcon" size="16" class="mr-2" />
            <strong>Bank Proof Document Already Available:</strong>
          </b-alert>
        </div>
      </div>

      <template #modal-footer>
        <b-button variant="secondary" @click="resetBankDetailsModal">
          {{ (selectedBankCandidate && selectedBankCandidate.candidateStatus === 'Shortlisted' && ((needsAccountNumberInMetadata(selectedBankCandidate) || needsAccountNumberInExtraDetails(selectedBankCandidate) || needsIFSCCodeInMetadata(selectedBankCandidate) || needsIFSCCodeInExtraDetails(selectedBankCandidate)) || (!selectedBankCandidate.documents || !selectedBankCandidate.documents.bankProof))) ? 'Cancel' : 'Close' }}
        </b-button>
        <b-button 
          v-if="selectedBankCandidate && selectedBankCandidate.candidateStatus === 'Shortlisted' && ((needsAccountNumberInMetadata(selectedBankCandidate) || needsAccountNumberInExtraDetails(selectedBankCandidate) || needsIFSCCodeInMetadata(selectedBankCandidate) || needsIFSCCodeInExtraDetails(selectedBankCandidate)) || (!selectedBankCandidate.documents || !selectedBankCandidate.documents.bankProof))"
          variant="primary" 
          @click="saveBankDetails"
          :disabled="((needsAccountNumberInMetadata(selectedBankCandidate) && needsAccountNumberInExtraDetails(selectedBankCandidate) && (!bankDetailsForm.accountNumber || !bankDetailsForm.confirmAccountNumber || bankDetailsForm.accountNumber !== bankDetailsForm.confirmAccountNumber)) || ((needsIFSCCodeInMetadata(selectedBankCandidate) && needsIFSCCodeInExtraDetails(selectedBankCandidate)) && !bankDetailsForm.ifscCode))"
        >
          <feather-icon icon="SaveIcon" size="16" class="mr-1" />
          Save Bank Details
        </b-button>
      </template>
    </b-modal>

    <!-- View Account Details Modal -->
    <b-modal
      v-model="showViewAccountDetailsModal"
      title="Account Details"
      size="md"
      @hidden="resetViewAccountDetailsModal"
    >
      <div v-if="selectedAccountDetailsCandidate">
        <div class="mb-3">
          <p><strong>Candidate:</strong> {{ selectedAccountDetailsCandidate.candidate_name }}</p>
          <p><strong>ID:</strong> {{ selectedAccountDetailsCandidate.id }}</p>
        </div>

        <div v-if="isBankAccountDetailsAvailable(selectedAccountDetailsCandidate)">
          <b-card class="mb-3">
            <h6 class="mb-3">Bank Account Information</h6>
            
            <!-- Account Number -->
            <div class="mb-3">
              <label class="form-label font-weight-bold">Account Number</label>
              <div class="d-flex align-items-center">
                <div class="flex-grow-1">
                  <b-form-input
                    :value="getBankAccountNumber(selectedAccountDetailsCandidate)"
                    readonly
                    class="bg-light"
                  ></b-form-input>
                </div>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  class="ml-2"
                  @click="copyToClipboard(getBankAccountNumber(selectedAccountDetailsCandidate), 'Account Number')"
                >
                  <feather-icon icon="CopyIcon" size="14" class="mr-1"></feather-icon>
                  Copy
                </b-button>
              </div>
            </div>

            <!-- IFSC Code -->
            <div class="mb-3">
              <label class="form-label font-weight-bold">IFSC Code</label>
              <div class="d-flex align-items-center">
                <div class="flex-grow-1">
                  <b-form-input
                    :value="getBankIFSCCode(selectedAccountDetailsCandidate)"
                    readonly
                    class="bg-light"
                  ></b-form-input>
                </div>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  class="ml-2"
                  @click="copyToClipboard(getBankIFSCCode(selectedAccountDetailsCandidate), 'IFSC Code')"
                >
                  <feather-icon icon="CopyIcon" size="14" class="mr-1"></feather-icon>
                  Copy
                </b-button>
              </div>
            </div>
          </b-card>
        </div>

        <div v-else>
          <b-alert variant="warning" show>
            <feather-icon icon="AlertCircleIcon" size="16" class="mr-2" />
            No account details available for this candidate.
          </b-alert>
        </div>
      </div>

      <template #modal-footer>
        <b-button variant="secondary" @click="resetViewAccountDetailsModal">
          Close
        </b-button>
      </template>
    </b-modal>

    <!-- Preview Offer Letter and Documents Modal -->
    <b-modal
      v-model="showPreviewOfferLetterAndDocumentsModal"
      title="Preview Offer Letter & Documents"
      size="xl"
      @hidden="resetPreviewOfferLetterAndDocuments"
    >
      <div v-if="selectedOfferLetter">
        <div class="offer-letter-header mb-4">
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <h5 class="mb-1">{{ selectedOfferLetter.candidate_name }}</h5>
              <p class="text-muted mb-0">Candidate ID: {{ selectedOfferLetter.id }}</p>
            </div>
            <div class="text-right">
              <b-badge variant="success" class="mb-2">OFFER LETTER & DOCUMENTS</b-badge>
              <p class="text-muted mb-0 small">Generated on {{ formatDate(selectedOfferLetter.offerDetails.offeredDate) }}</p>
            </div>
          </div>
        </div>

        <!-- Documents Section -->
        <div class="documents-preview-section mb-4">
          <h6 class="mb-3">
            <feather-icon icon="FileIcon" size="18" class="mr-2" />
            Candidate Documents
          </h6>
          <div class="document-grid">
            <!-- Aadhaar Document (with front/back support) -->
            <div 
              :class="['document-card', { 'document-card-missing': !hasAadhaarDocument(selectedOfferLetter) }]"
            >
              <div class="document-card-header">
                <feather-icon 
                  :icon="hasAadhaarDocument(selectedOfferLetter) ? 'CheckCircleIcon' : 'XCircleIcon'" 
                  :class="hasAadhaarDocument(selectedOfferLetter) ? 'text-success' : 'text-danger'"
                  size="20" 
                  class="mr-2" 
                />
                <strong>Aadhaar</strong>
                <b-badge 
                  v-if="hasAadhaarDocument(selectedOfferLetter)" 
                  variant="success" 
                  class="ml-2"
                >
                  Available
                </b-badge>
                <b-badge 
                  v-else 
                  variant="danger" 
                  class="ml-2"
                >
                  Missing
                </b-badge>
              </div>
              
              <!-- Show front/back if available -->
              <div v-if="selectedOfferLetter.documents && selectedOfferLetter.documents.aadhaar_front" class="document-preview">
                <a 
                  :href="selectedOfferLetter.documents.aadhaar_front" 
                  target="_blank" 
                  class="btn btn-sm btn-outline-primary mb-1"
                >
                  <feather-icon icon="ExternalLinkIcon" size="14" class="mr-1" />
                  View Front
                </a>
              </div>
              <div v-if="selectedOfferLetter.documents && selectedOfferLetter.documents.aadhaar_back" class="document-preview">
                <a 
                  :href="selectedOfferLetter.documents.aadhaar_back" 
                  target="_blank" 
                  class="btn btn-sm btn-outline-primary"
                >
                  <feather-icon icon="ExternalLinkIcon" size="14" class="mr-1" />
                  View Back
                </a>
              </div>
              
              <!-- Fallback to legacy single file -->
              <div v-else-if="selectedOfferLetter.documents && selectedOfferLetter.documents.aadhaar" class="document-preview">
                <a 
                  :href="selectedOfferLetter.documents.aadhaar" 
                  target="_blank" 
                  class="btn btn-sm btn-outline-primary"
                >
                  <feather-icon icon="ExternalLinkIcon" size="14" class="mr-1" />
                  View Document
                </a>
              </div>
              
              <div v-else class="document-missing-message">
                <p class="text-danger mb-0">
                  <feather-icon icon="AlertCircleIcon" size="16" class="mr-1" />
                  Document not available
                </p>
              </div>
            </div>
            
            <!-- Other Documents -->
            <div 
              v-for="doc in ['pan', 'photo', 'bankProof']" 
              :key="doc" 
              :class="['document-card', { 'document-card-missing': !selectedOfferLetter.documents || !selectedOfferLetter.documents[doc] }]"
            >
              <div class="document-card-header">
                <feather-icon 
                  :icon="(selectedOfferLetter.documents && selectedOfferLetter.documents[doc]) ? 'CheckCircleIcon' : 'XCircleIcon'" 
                  :class="(selectedOfferLetter.documents && selectedOfferLetter.documents[doc]) ? 'text-success' : 'text-danger'"
                  size="20" 
                  class="mr-2" 
                />
                <strong class="text-capitalize">{{ doc.replace(/([A-Z])/g, ' $1') }}</strong>
                <b-badge 
                  v-if="selectedOfferLetter.documents && selectedOfferLetter.documents[doc]" 
                  variant="success" 
                  class="ml-2"
                >
                  Available
                </b-badge>
                <b-badge 
                  v-else 
                  variant="danger" 
                  class="ml-2"
                >
                  Missing
                </b-badge>
              </div>
              <div v-if="selectedOfferLetter.documents && selectedOfferLetter.documents[doc]" class="document-preview">
                <a 
                  :href="selectedOfferLetter.documents[doc]" 
                  target="_blank" 
                  class="btn btn-sm btn-outline-primary"
                >
                  <feather-icon icon="ExternalLinkIcon" size="14" class="mr-1" />
                  View Document
                </a>
              </div>
              <div v-else class="document-missing-message">
                <p class="text-danger mb-0">
                  <feather-icon icon="AlertCircleIcon" size="16" class="mr-1" />
                  Document not available
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Offer Letter Section -->
        <div class="offer-letter-content">
          <h6 class="mb-3">
            <feather-icon icon="FileTextIcon" size="18" class="mr-2" />
            Generated Offer Letter
          </h6>
          <!-- Display PDF from S3 -->
          <div v-if="offerLetterPDFBase64" class="letter-body">
            <div class="d-flex justify-content-end mb-2">
              <b-button size="sm" variant="outline-secondary" @click="openOfferLetterPdfInNewTab">
                <feather-icon icon="ExternalLinkIcon" size="14" class="mr-1" />
                Open in new tab
              </b-button>
            </div>
            <iframe
              :key="offerLetterPDFBase64"
              :src="offerLetterPDFBase64"
              type="application/pdf"
              title="Offer letter PDF"
              frameborder="0"
              height="800px"
              width="100%"
              style="border: 1px solid #ddd; background: #f5f5f5;"
            ></iframe>
          </div>
          
          <div v-else class="letter-body text-center">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Loading...</span>
            </div>
            <p class="mt-2">Loading offer letter PDF from S3...</p>
          </div>
        </div>
      </div>

      <template #modal-footer>
        <b-button variant="secondary" @click="resetPreviewOfferLetterAndDocuments">
          Close
        </b-button>
        <b-button variant="primary" @click="downloadOfferLetter(selectedOfferLetter)">
          <feather-icon icon="DownloadIcon" size="16" class="mr-1" />
          Download Offer Letter PDF
        </b-button>
      </template>
    </b-modal>

    <!-- Pool Section -->
    <b-card no-body class="pool-card mb-2">
      <b-card-header class="pool-header py-2 px-2 px-md-3">
        <div class="d-flex justify-content-between align-items-center flex-wrap w-100">
          <div class="d-flex align-items-center flex-wrap pr-2">
            <div class="pr-2">
              <h5 class="mb-0 d-flex align-items-center">
                <feather-icon icon="UsersIcon" size="18" class="mr-1" />
                Candidate Pool
              </h5>
              <p class="text-muted mb-0 small">
                Shortlisted, Offered, Joined · {{ selectedClientName }}
              </p>
            </div>
            <b-badge v-if="!poolBusy" variant="light-primary" class="pool-count-badge">
              {{ filteredPoolTotalRows }} candidates
            </b-badge>
            <b-spinner v-if="poolBusy" small />
          </div>
          <div class="pool-header__actions ml-auto">
            <b-input-group size="sm" class="pool-header__search">
              <b-form-input
                id="candidate-search"
                v-model="filters.search"
                placeholder="Search candidates..."
                autocomplete="off"
                aria-label="Search candidates"
                :class="{ 'pool-header__search-input--clearable': filters.search }"
                @input="onHeaderSearchInput"
                @keyup.enter="applyHeaderSearch"
              />
              <button
                v-if="filters.search"
                type="button"
                class="pool-header__search-clear"
                aria-label="Clear search"
                @click="clearSearch"
              >
                <feather-icon icon="XIcon" size="14" />
              </button>
            </b-input-group>
            <b-button
              size="sm"
              variant="link"
              class="pool-filters-btn"
              aria-label="Open candidate pool filters"
              @click="openPoolFilters"
            >
              <feather-icon icon="FilterIcon" size="14" />
              <span class="ml-50 d-none d-md-inline">Filters</span>
              <span
                v-if="hasActiveModalFilters"
                class="pool-filters-btn__dot"
                aria-hidden="true"
              />
            </b-button>
          </div>
        </div>
      </b-card-header>

      <!-- Candidate Pool filters modal -->
      <b-modal
        id="pool-filters-modal"
        v-model="showPoolFiltersModal"
        title="Filters"
        size="md"
        hide-footer
        modal-class="cd-modal pool-filters-modal"
      >
        <div class="pool-filters-modal__body">
          <b-row class="mx-n1">
            <b-col cols="12" sm="6" class="px-1 mb-2">
              <label class="pool-toolbar__label" for="candidate-status-filter">Status</label>
              <b-form-select
                id="candidate-status-filter"
                v-model="filters.status"
                size="sm"
                :options="statusOptions"
              />
            </b-col>
            <b-col cols="12" sm="6" class="px-1 mb-2">
              <label class="pool-toolbar__label" for="offer-letter-status-filter">Offer Letter</label>
              <b-form-select
                id="offer-letter-status-filter"
                v-model="filters.offerLetterStatus"
                size="sm"
                :options="offerLetterStatusOptions"
              />
            </b-col>
            <b-col cols="12" sm="6" class="px-1 mb-2">
              <label class="pool-toolbar__label" for="role-filter">Role</label>
              <b-form-select
                id="role-filter"
                v-model="filters.role"
                size="sm"
                :options="roleOptions"
              />
            </b-col>
            <b-col cols="12" sm="6" class="px-1 mb-2">
              <label class="pool-toolbar__label" for="location-filter">Location</label>
              <b-form-select
                id="location-filter"
                v-model="filters.location"
                size="sm"
                :options="locationOptions"
              />
            </b-col>
          </b-row>
          <div class="d-flex flex-wrap align-items-center mt-1">
            <b-button
              variant="primary"
              size="sm"
              class="dashboard-action-btn mr-1 mb-1"
              :disabled="poolBusy"
              @click="onSearchInput"
            >
              <feather-icon icon="FilterIcon" size="14" class="mr-1" />
              Apply
            </b-button>
            <b-button
              variant="outline-secondary"
              size="sm"
              class="dashboard-action-btn mb-1"
              :disabled="!hasActiveFilters && !hasDraftFilters"
              @click="clearFilters"
            >
              <feather-icon icon="XIcon" size="14" class="mr-1" />
              Clear
            </b-button>
          </div>
        </div>
      </b-modal>

      <b-card-body class="p-0">
        <b-alert
          v-if="reportsError"
          variant="danger"
          dismissible
          class="m-2 mb-0"
          @dismissed="reportsError = null"
        >
          <feather-icon icon="AlertCircleIcon" size="16" class="mr-1" />
          {{ reportsError }}
        </b-alert>

        <!-- Pool Bulk Actions -->
        <div
          v-if="selectedPoolCandidates.length > 0 && canAssignCandidates"
          class="bulk-actions mx-2 mb-2"
        >
          <div class="d-flex align-items-center flex-wrap">
            <div class="selection-indicator mr-2 mb-1">
              <feather-icon icon="CheckCircleIcon" size="16" class="text-success mr-1" />
              <span class="selection-count">{{ selectedPoolCandidates.length }} selected</span>
            </div>
            <b-button
              size="sm"
              variant="primary"
              class="dashboard-action-btn mr-1 mb-1"
              @click="openPoolBulkAssignmentModal"
            >
              <feather-icon icon="UsersIcon" size="14" class="mr-1" />
              Bulk Assign
            </b-button>
            <b-button
              size="sm"
              variant="outline-secondary"
              class="dashboard-action-btn mb-1"
              @click="clearPoolSelection"
            >
              Clear Selection
            </b-button>
          </div>
        </div>

        <!-- Pool loading -->
        <div v-if="poolBusy" class="pool-empty-state text-center py-4">
          <b-spinner variant="primary" class="mb-2" />
          <p class="text-muted mb-0 small">Loading candidates...</p>
        </div>

        <!-- Pool Table -->
        <div v-else-if="filteredReportsCandidates.length > 0" class="pool-table-wrap d-flex flex-column">
          <div class="pool-table-scroll overflow-auto">
          <b-table-simple
            :key="reportsTableKey"
            hover
            bordered
            class="mb-0 pool-table h6 table-bordered"
          >
            <b-thead class="thead-light">
              <b-tr>
                <b-th v-if="canAssignCandidates" class="sticky-top border-right-0" style="width: 36px"></b-th>
                <b-th class="h6 mb-0 sticky-top" :class="{ 'border-left-0': canAssignCandidates }">CANDIDATE / POSITION</b-th>
                <b-th class="h6 mb-0 sticky-top d-none d-lg-table-cell">RECRUITER</b-th>
                <b-th class="h6 mb-0 sticky-top">STATUS</b-th>
                <b-th class="h6 mb-0 sticky-top d-none d-lg-table-cell">CONTACT</b-th>
                <b-th class="h6 mb-0 sticky-top d-none d-md-table-cell">DOCS</b-th>
                <b-th v-if="showPoolActionsColumn" class="sticky-top" style="width: 44px"></b-th>
              </b-tr>
            </b-thead>
            <b-tbody>
              <b-tr
                v-for="item in pagedPoolCandidates"
                :key="item.id || item.cid"
                @click="onPoolRowClicked(item)"
              >
                <b-td v-if="canAssignCandidates" class="align-middle border-right-0">
                  <b-form-checkbox
                    v-model="item.selected"
                    @change="onPoolSelectionChange(item)"
                    @click.stop
                  />
                </b-td>
                <b-td class="align-middle" :class="{ 'border-left-0': canAssignCandidates }">
                  <div class="d-flex align-items-center h6 mb-0">
                    <div class="candidate-info">
                      <router-link
                        v-if="canOpenCandidateProfile"
                        :to="{
                          name: 'apps-candidates-view',
                          params: {
                            id: item.id || item.cid,
                            jobId: item.job_id || item.jid || 'null'
                          },
                          query: {
                            position_name: item.position_name || '',
                            client: item.client || selectedClientName,
                            recruiter: item.assignedTo || item.recruiter || '',
                            locations: item.locations || '',
                            position_type: item.position_type || '',
                            vertical: item.vertical || '',
                            category: item.category || '',
                            payroll: item.payroll || ''
                          }
                        }"
                        class="text-decoration-none"
                        @click.stop
                      >
                        <div class="candidate-name font-weight-bold text-primary h6 mb-0">
                          {{ item.candidate_name }}
                        </div>
                      </router-link>
                      <div
                        v-else
                        class="candidate-name font-weight-bold h6 mb-0"
                      >
                        {{ item.candidate_name }}
                      </div>
                      <router-link
                        v-if="canOpenJobProfile"
                        :to="{
                          name: 'apps-view-job',
                          params: { id: item.job_id || item.jid || 'null' }
                        }"
                        class="text-decoration-none"
                        @click.stop
                      >
                        <div class="candidate-position text-primary h6 mb-0">
                          {{ item.position_name || 'N/A' }}
                        </div>
                      </router-link>
                      <div
                        v-else
                        class="candidate-position text-muted h6 mb-0"
                      >
                        {{ item.position_name || 'N/A' }}
                      </div>
                    </div>
                  </div>
                </b-td>
                <b-td class="align-middle d-none d-lg-table-cell">
                  <b-avatar
                    v-if="item.assignedTo"
                    v-b-tooltip.hover
                    :text="getInitials(getRecruiterName(item.assignedTo))"
                    :title="getRecruiterName(item.assignedTo)"
                    :aria-label="getRecruiterName(item.assignedTo)"
                    :size="36"
                    class="recruiter-initials-avatar"
                  />
                  <span v-else class="text-muted h6 mb-0">Unassigned</span>
                </b-td>
                <b-td class="align-middle">
                  <div class="status-cell h6 mb-0">
                    <span
                      :class="['status-badge', 'h6', 'mb-0', getStatusBadgeClass(item.candidateStatus)]"
                    >
                      {{ item.candidateStatus === 'Offer Generated' ? 'Offer Generated' : (item.candidateStatus || 'N/A') }}
                    </span>
                    <template v-if="hasOfferLetter(item)">
                      <span
                        v-if="isOfferLetterEmailSent(item)"
                        class="status-badge status-badge--offer-sent h6 mb-0"
                        :title="getOfferGenerationCountTitle(item)"
                      >
                        Offer Sent<span v-if="getOfferGenerationCount(item) > 1" class="offer-gen-count"> ×{{ getOfferGenerationCount(item) }}</span>
                      </span>
                      <template v-else>
                        <span
                          class="status-badge status-badge--offer-generated h6 mb-0"
                          :title="getOfferGenerationCountTitle(item)"
                        >
                          Offer Generated<span v-if="getOfferGenerationCount(item) > 1" class="offer-gen-count"> ×{{ getOfferGenerationCount(item) }}</span>
                        </span>
                        <span class="status-badge status-badge--not-sent h6 mb-0">
                          Not Sent
                        </span>
                      </template>
                    </template>
                  </div>
                </b-td>
                <b-td class="align-middle d-none d-lg-table-cell">
                  <div class="contact-info h6 mb-0">
                    <div class="phone h6 mb-0">{{ item.contact_no || 'N/A' }}</div>
                    <div class="email text-muted h6 mb-0">{{ item.email || 'N/A' }}</div>
                  </div>
                </b-td>
                <b-td class="align-middle d-none d-md-table-cell">
                  <div class="document-status h6 mb-0">
                    <div class="d-flex flex-wrap">
                      <b-badge
                        :variant="hasAadhaarDocument(item) ? 'success' : 'danger'"
                        class="mr-1 mb-1 doc-badge h6"
                      >
                        aadhaar
                      </b-badge>
                      <b-badge
                        v-for="docType in ['pan', 'photo', 'bankProof']"
                        :key="docType"
                        :variant="(item.documents && item.documents[docType]) ? 'success' : 'danger'"
                        class="mr-1 mb-1 doc-badge h6"
                      >
                        {{ docType }}
                      </b-badge>
                    </div>
                    <b-badge
                      :class="['doc-badge', 'h6', 'mb-0', isBankAccountDetailsAvailable(item) ? 'account-details-yes' : 'account-details-no']"
                    >
                      Acct: {{ isBankAccountDetailsAvailable(item) ? 'Yes' : 'No' }}
                    </b-badge>
                  </div>
                </b-td>
                <b-td v-if="showPoolActionsColumn" class="align-middle text-center">
                  <!-- positionFixed escapes pool-table overflow clipping (same pattern as TaskListView). -->
                  <b-dropdown
                    v-if="hasPoolRowActions(item)"
                    variant="link"
                    toggle-class="text-decoration-none"
                    no-caret
                    right
                    boundary="window"
                    :popper-opts="{ positionFixed: true }"
                  >
                    <template #button-content>
                      <i class="feather icon-more-vertical"></i>
                    </template>

                    <b-dropdown-item
                      v-if="canAssignCandidates && !item.assignedTo"
                      @click="openAssignmentModal(item)"
                    >
                      <i class="feather icon-user-plus mr-2"></i>
                      Assign to Recruiter
                    </b-dropdown-item>

                    <b-dropdown-item
                      v-if="canGenerateOfferLetter && item.assignedTo &&
                           (item.candidateStatus === 'Shortlisted' || item.candidateStatus === 'Offered' || item.candidateStatus === 'Joined')
                           && !hasOfferLetter(item)"
                      @click="openOfferLetterModal(item)"
                    >
                      <i class="feather icon-file-text mr-2"></i>
                      Generate Offer Letter
                    </b-dropdown-item>

                    <b-dropdown-item
                      v-if="canManageBankDetails && item.assignedTo &&
                           item.candidateStatus !== 'Interview' &&
                           !(isBankAccountDetailsAvailable(item) && item.documents && item.documents.bankProof)"
                      @click="openBankDetailsModal(item)"
                    >
                      <i class="feather icon-upload mr-2"></i>
                      Upload Bank Proof / Add Bank Details
                    </b-dropdown-item>

                    <b-dropdown-item
                      v-if="canViewBankDetails && isBankAccountDetailsAvailable(item)"
                      @click="openViewAccountDetailsModal(item)"
                    >
                      <i class="feather icon-credit-card mr-2"></i>
                      View Account Details
                    </b-dropdown-item>

                    <template v-if="hasOfferLetter(item) && (canViewOffer || canGenerateOfferLetter || canDownloadOffer || canSendOfferLetterEmail)">
                      <b-dropdown-divider></b-dropdown-divider>
                      <b-dropdown-item
                        v-if="canViewOffer"
                        @click="viewOfferLetter(item)"
                      >
                        <i class="feather icon-eye mr-2"></i>
                        View Offer Letter
                      </b-dropdown-item>

                      <b-dropdown-item
                        v-if="canGenerateOfferLetter"
                        @click="regenerateOfferLetterFromActions(item)"
                      >
                        <i class="feather icon-refresh-cw mr-2"></i>
                        Regenerate Offer Letter
                      </b-dropdown-item>

                      <b-dropdown-item
                        v-if="canDownloadOffer"
                        @click="downloadOfferLetter(item)"
                      >
                        <i class="feather icon-download mr-2"></i>
                        Download Offer Letter
                      </b-dropdown-item>

                      <b-dropdown-item
                        v-if="canSendOfferLetterEmail"
                        @click="sendOfferLetterEmailFromActions(item)"
                      >
                        <i class="feather icon-mail mr-2"></i>
                        {{ isOfferLetterEmailSent(item) ? 'Resend Email' : 'Send Email' }}
                      </b-dropdown-item>
                    </template>
                  </b-dropdown>
                </b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>
          </div>

          <!-- Pool Pagination (outside scroll so it stays frozen at the bottom) -->
          <div class="pool-pagination border-top bg-white d-flex justify-content-between align-items-center flex-wrap px-2 py-2">
            <div class="pagination-info mb-1 mb-md-0">
              <span class="text-muted small">Showing {{ poolShowingStart }}- {{ poolShowingEnd }} of {{ filteredPoolTotalRows }}</span>
            </div>
            <b-pagination
              v-model="poolCurrentPage"
              :total-rows="filteredPoolTotalRows"
              :per-page="poolPerPage"
              :limit="5"
              size="sm"
              class="mb-0"
              @change="onPoolPageChange"
            />
          </div>
        </div>

        <!-- No Pool Data -->
        <div v-else class="pool-empty-state text-center py-4">
          <feather-icon icon="UsersIcon" size="36" class="text-muted mb-2" />
          <h6 class="text-muted mb-1">No candidates found</h6>
          <p class="text-muted mb-2 small">
            {{ hasActiveFilters
              ? 'Try adjusting filters or clearing the search.'
              : 'No candidates with Shortlisted, Offered, or Joined status.' }}
          </p>
          <b-button
            v-if="hasActiveFilters"
            variant="outline-primary"
            size="sm"
            class="dashboard-action-btn"
            @click="clearFilters"
          >
            <feather-icon icon="XIcon" size="14" class="mr-1" />
            Clear Filters
          </b-button>
        </div>
      </b-card-body>
    </b-card>

    <!-- Reports Section -->
    <section class="reports-section mb-2">
      <div class="reports-section__header d-flex justify-content-between align-items-start flex-wrap mb-2 pb-2">
        <div>
          <h5 class="mb-0 d-flex align-items-center">
            <feather-icon icon="BarChart2Icon" size="18" class="mr-1" />
            Reports
          </h5>
          <p class="text-muted mb-0 small">Candidate statistics</p>
        </div>
      </div>

      <b-row>
        <b-col lg="6" md="12" class="mb-2">
          <div class="report-panel">
            <h6 class="report-panel__title mb-2">
              <feather-icon icon="MapPinIcon" size="14" class="mr-1" />
              Location &amp; Role
            </h6>
            <div v-if="locationRoleWiseReport.length > 0" class="table-responsive">
              <b-table
                :items="locationRoleWiseReport"
                :fields="locationRoleWiseFields"
                hover
                small
                class="mb-0"
              >
                <template #cell(count)="row">
                  <b-badge variant="primary">{{ row.item.count }}</b-badge>
                </template>
              </b-table>
            </div>
            <p v-else class="text-center py-2 text-muted mb-0 small">No data available</p>
          </div>
        </b-col>

        <b-col lg="6" md="12" class="mb-2">
          <div class="report-panel">
            <h6 class="report-panel__title mb-2">
              <feather-icon icon="BriefcaseIcon" size="14" class="mr-1" />
              Job ID &amp; Role
            </h6>
            <div v-if="jobIdRoleWiseReport.length > 0" class="table-responsive">
              <b-table
                :items="jobIdRoleWiseReport"
                :fields="jobIdRoleWiseFields"
                hover
                small
                class="mb-0"
              >
                <template #cell(count)="row">
                  <b-badge variant="info">{{ row.item.count }}</b-badge>
                </template>
              </b-table>
            </div>
            <p v-else class="text-center py-2 text-muted mb-0 small">No data available</p>
          </div>
        </b-col>

        <b-col cols="12" class="mb-1">
          <div class="report-panel">
            <h6 class="report-panel__title mb-2">
              <feather-icon icon="ActivityIcon" size="14" class="mr-1" />
              By Status (Shortlisted, Offered, Joined)
            </h6>
            <div v-if="statusWiseReport.length > 0" class="table-responsive">
              <b-table
                :items="statusWiseReport"
                :fields="statusWiseFields"
                hover
                small
                class="mb-0"
              >
                <template #cell(status)="row">
                  <span :class="['status-badge', getStatusBadgeClass(row.item.status)]">
                    {{ row.item.status }}
                  </span>
                </template>
                <template #cell(count)="row">
                  <b-badge variant="success">{{ row.item.count }}</b-badge>
                </template>
                <template #cell(percentage)="row">
                  <span class="text-muted">{{ row.item.percentage }}%</span>
                </template>
              </b-table>
            </div>
            <p v-else class="text-center py-2 text-muted mb-0 small">No data available</p>
          </div>
        </b-col>
      </b-row>
    </section>

    <!-- Pool Bulk Assignment Modal -->
    <b-modal
      v-model="showPoolBulkModal"
      title="Bulk Assign Pool Candidates to POC"
      size="lg"
      @hidden="resetPoolBulkAssignmentForm"
    >
      <div class="bulk-assignment-info mb-3">
        <h6>{{ selectedPoolCandidates.length }} pool candidates selected for assignment</h6>
      </div>

      <b-form @submit.prevent="bulkAssignPoolCandidates">
        <b-form-group label="Select POC" label-for="pool-bulk-poc-select">
          <v-select
            v-model="poolBulkAssignmentForm.poc"
            :options="pocOptions"
            label="text"
            :reduce="option => option.value"
            placeholder="Select POC"
            required
          />
        </b-form-group>
      </b-form>

      <template #modal-footer>
        <b-button variant="secondary" @click="showPoolBulkModal = false">
          Cancel
        </b-button>
        <b-button 
          variant="primary" 
          @click="bulkAssignPoolCandidates"
          :disabled="!poolBulkAssignmentForm.poc"
        >
          Assign All Pool Candidates
        </b-button>
      </template>
    </b-modal>
    </b-container>
  </div>
</template>

<script>
import {
  BContainer,
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BAvatar,
  BSpinner,
  BTable,
  BTableSimple,
  BThead,
  BTbody,
  BTr,
  BTh,
  BTd,
  BPagination,
  BFormGroup,
  BFormSelect,
  BFormInput,
  BFormFile,
  BForm,
  BBadge,
  BDropdown,
  BDropdownItem,
  BDropdownDivider,
  BFormCheckbox,
  BProgress,
  BModal,
  BAlert,
  BFormDatepicker,
  BTabs,
  BTab,
  BInputGroup,
  BInputGroupAppend,
  VBTooltip,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import ability from '@/libs/acl/ability'
import {
  canAccessClientDashboard,
  canViewClientDashboardCandidates,
  canAssignClientDashboardCandidates,
  canViewOfferLetter,
  canGenerateOffer,
  canDownloadOfferLetter,
  canSendOfferEmail,
  canViewClientDashboardBankDetails,
  canManageClientDashboardBankDetails,
  canManageClientDashboardSettings,
} from '@/utils/client-dashboard-permissions'
import candidateService from '@/services/candidate-service'
import offerLetterService from '@/services/offer-letter-service'
import { extractOfferSignatureImageSrc } from '@/utils/offer-template-editor'
import clientDashboardService from '@/services/client-dashboard-service'
import jobService from '@/services/job-service'
import jobTimelineService from '@/services/job-timeline'
import mailService from '@/services/mail-service'
import userService from '@/services/user-service'
import adminConfigService from '@/services/admin-config-service'
import siteConfigService from '@/services/site-config-service'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { quillEditor } from 'vue-quill-editor'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'
import emailTemplates from '@/services/email-templates'

const CLIENT_DASHBOARD_FILTERS_KEY = 'clientDashboard_filters'

export default {
  name: 'UnifiedClientDashboard',
  props: {
    clientName: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      selectedClientName: null,
      loading: true,
      candidates: [],
      totalRows: 0,
      currentPage: 1,
      perPage: 25,
      
      // Summary counts
      totalCandidates: 0,
      profileSharedCount: 0,
      shortlistedCount: 0,
      offerGeneratedCount: 0,
      offerSentCount: 0,
      offerPendingCount: 0,
      offeredCount: 0,
      joinedCount: 0,
      
      // Document check toggle - read from localStorage for global effect
      // true = document check enabled (default), false = bypass allowed
      documentCheckEnabled: localStorage.getItem('offerLetterDocCheckEnabled') !== 'false',
      
      // Pagination information from API
      paginationInfo: {
        currentPage: 1,
        totalPages: 1,
        totalRecords: 0,
        limit: 25,
        hasNextPage: false,
        hasPreviousPage: false,
        nextPage: null,
        previousPage: null
      },
      
      // Table sorting
      sortBy: 'candidate_name',
      sortDesc: false,
      
      // Filters (input values - not applied until search is clicked)
      filters: {
        status: '', // Default to empty to show all pool statuses (Shortlisted, Interview, Offered, Joined)
        recruiter: '',
        poc: '',
        search: '',
        offerLetterStatus: '', // Filter for offer letter status: '', 'Generated', 'Sent', 'Pending'
        role: '', // Filter for role/position
        location: '' // Filter for preferred location (pref_location)
      },
      // Applied filters (only these are used for filtering)
      appliedFilters: {
        status: '',
        search: '',
        offerLetterStatus: '',
        role: '',
        location: ''
      },
      
      // Business and POC options
      pocOptions: [],
      pocFilterOptions: [],
      recruiterOptions: [],
      recruitersList: [], // Cache recruiters list for email BCC
      roleOptions: [], // Options for role/position filter
      locationOptions: [], // Options for location filter (from pref_location)
      stateOptions: [],
      siteOptions: [],
      reportingManagerOptions: [], // Reporting managers dropdown options
      
      // Assignment modals
      showPoolFiltersModal: false,
      showBusinessModal: false,
      showPOCModal: false,
      showOfferLetterModal: false,
      showViewOfferLetterModal: false,
      showPreviewOfferLetterAndDocumentsModal: false,
      showPostGenerationModal: false,
      showOfferLetterErrorModal: false,
      offerLetterErrorMessage: '',
      showBankDetailsModal: false,
      showViewAccountDetailsModal: false,
      selectedCandidate: null,
      selectedOfferLetter: null,
      selectedBankCandidate: null,
      selectedAccountDetailsCandidate: null,
      generatedOfferLetterHTML: '',
      generatedOfferLetterData: null,
      offerLetterPDFBase64: null, // Base64 PDF for display
      confirmSendEmail: false,
      emailSent: false,
      isGeneratingOfferLetter: false, // Loading state for Generate button
      
      // Email composition modal
      showSendEmailModal: false,
      isSendingEmail: false,
      emailForm: {
        to: '',
        cc: '',
        bcc: '',
        subject: '',
        body: '',
        attachOfferLetter: true
      },
      emailFormErrors: {
        subject: '',
        body: ''
      },
      quillEditorOptions: {
        theme: 'snow',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'],
            [{ 'align': [] }],
            ['link'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }]
          ]
        }
      },
      currentEmailCandidate: null, // Store candidate for email modal
      bankDetailsForm: {
        accountNumber: '',
        confirmAccountNumber: '',
        ifscCode: '',
        bankProofFile: null
      },
      assignmentForm: {
        poc: null
      },
      bulkAssignmentForm: {
        poc: null
      },
      offerLetterForm: {
        candidateName: '',
        emailId: '',
        joiningDate: '',
        offeredDate: '',
        site: 'assam',
        language: 'en',
        location: '',
        reportingManagerName: '',
        jobTitle: ''
      },
      jobTitleOptions: [], // Options for job title dropdown
      
      // Recruiter confirmation variables
      recruiterConfirmed: false,
      documentsVerified: false,
      offerDetailsConfirmed: false,
      sendEmail: false,
      
      // API error state
      apiError: null,
      
      
      // Loading progress
      loadingProgress: '',
      
      // Pool section
      reportsCandidates: [],
      reportsLoading: false,
      reportsError: null,
      reportsTableKey: 0,
      
      // Pool pagination
      poolCurrentPage: 1,
      poolPerPage: 25,
      poolTotalRows: 0,
      
      // Pool selection
      selectedPoolCandidates: [],
      showPoolBulkModal: false,
      poolBulkAssignmentForm: {
        poc: null
      },
      
      // Reports section
      selectedReportType: null,
      reportFilters: {
        startDate: null,
        endDate: null,
        status: []
      },
      generatedReports: [],
      showReportModal: false,
      selectedReport: null,
      activeReportTab: 0,
      
      // Bulk selection
      selectedCandidates: [],
      tableKey: 0,
      selectAll: false,
      
      // Options for filters
      statusOptions: [
        { value: '', text: 'All Status' },
        { value: 'Shortlisted', text: 'Shortlisted' },
        { value: 'Offer Accepted', text: 'Offer Accepted' },
        { value: 'Offered', text: 'Offered' },
        { value: 'Joined', text: 'Joined' },
        { value: 'NotOffered', text: 'Not Offered' }, 
        { value: 'Interview', text: 'Interview' },
        { value: 'No Show', text: 'No Show' },
        { value: 'OfferDrop', text: 'Offer Dropped' },
        { value: 'Call Back', text: 'Call Back' },
        { value: 'Not Answered', text: 'Not Answered' },
        { value: 'Not Interested', text: 'Not Interested' },
        { value: 'Not Reachable', text: 'Not Reachable' },
        { value: 'Not Relevant', text: 'Not Relevant' },
        { value: 'ProfileShared', text: 'Profile Shared' },
        { value: 'Rejected', text: 'Rejected' },
        { value: 'UploadedByRecruiter', text: 'Uploaded by Recruiter' },
        { value: 'UploadedBySourcer', text: 'Uploaded by Sourcer' }
      ],
      businessFilterOptions: [
        { value: '', text: 'All Business' },
        { value: 'Infra Industry', text: 'Infra Industry' }
      ],
      pocFilterOptions: [
        { value: '', text: 'All POC' }
      ],
      offerLetterStatusOptions: [
        { value: '', text: 'All Offer Letters' },
        { value: 'Generated', text: 'Offer Letter Generated' },
        { value: 'Sent', text: 'Offer Letter Sent' },
        { value: 'Pending', text: 'Offer Letter Pending' }
      ],
      
      // Report type options
      reportTypeOptions: [
        { value: 'location', text: 'Location Wise' },
        { value: 'status', text: 'Status Wise' },
        { value: 'recruiter', text: 'Recruiter Wise' },
        { value: 'position', text: 'Position Wise' }
      ],
      
      // Table fields
      tableFields: [
        { key: 'selection', label: '', thStyle: { width: '50px' } },
        { key: 'candidate_name', label: 'Candidate', sortable: true, thStyle: { width: '150px' } },
        { key: 'candidateStatus', label: 'Status', sortable: true, thStyle: { width: '120px' } },
        { key: 'position_name', label: 'Position', sortable: true, thStyle: { width: '200px' } },
        { key: 'assignedTo', label: 'Recruiter', sortable: true, thStyle: { width: '150px' } },
        { key: 'accountDetails', label: 'Account Details', sortable: false, thStyle: { width: '130px' } },
        { key: 'offerProcessStage', label: 'Offer Process Stage', sortable: true, thStyle: { width: '180px' } },
        { key: 'actions', label: 'Actions', thStyle: { width: '120px' } }
      ],
      offerLetterFilename: null
    }
  },
  components: {
    BContainer,
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BAvatar,
    BSpinner,
    BTable,
    BTableSimple,
    BThead,
    BTbody,
    BTr,
    BTh,
    BTd,
    BPagination,
    BFormGroup,
    BFormSelect,
    BFormInput,
    BFormFile,
    BForm,
    BBadge,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    BFormCheckbox,
    BProgress,
    BModal,
    BAlert,
    BFormDatepicker,
    BTabs,
    BTab,
    BInputGroup,
    BInputGroupAppend,
    vSelect,
    quillEditor
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  computed: {
    hasAccess() {
      return canAccessClientDashboard(ability.can.bind(ability))
    },
    canViewCandidates() {
      return canViewClientDashboardCandidates(ability.can.bind(ability))
    },
    canAssignCandidates() {
      return canAssignClientDashboardCandidates(ability.can.bind(ability))
    },
    canViewOffer() {
      return canViewOfferLetter(ability.can.bind(ability))
    },
    canGenerateOfferLetter() {
      return canGenerateOffer(ability.can.bind(ability))
    },
    canDownloadOffer() {
      return canDownloadOfferLetter(ability.can.bind(ability))
    },
    canSendOfferLetterEmail() {
      return canSendOfferEmail(ability.can.bind(ability))
    },
    canViewBankDetails() {
      return canViewClientDashboardBankDetails(ability.can.bind(ability))
    },
    canManageBankDetails() {
      return canManageClientDashboardBankDetails(ability.can.bind(ability))
    },
    canManageDashboardSettings() {
      return canManageClientDashboardSettings(ability.can.bind(ability))
    },
    canOpenCandidateProfile() {
      return ability.can('read', 'candidates') || ability.can('manage', 'candidates') || ability.can('manage', 'all')
    },
    canOpenJobProfile() {
      return ability.can('read', 'jobs') || ability.can('manage', 'jobs') || ability.can('manage', 'all')
    },
    summaryMetrics() {
      return [
        { label: 'Total', value: this.totalCandidates },
        { label: 'Profile Shared', value: this.profileSharedCount },
        { label: 'Shortlisted', value: this.shortlistedCount },
        { label: 'Offer Generated', value: this.offerGeneratedCount },
        { label: 'Offer Sent', value: this.offerSentCount },
        { label: 'Offer Pending', value: this.offerPendingCount },
        { label: 'Offered', value: this.offeredCount },
        { label: 'Joined', value: this.joinedCount }
      ]
    },
    poolBusy() {
      return this.loading || this.reportsLoading
    },
    pagedPoolCandidates() {
      const page = Math.max(1, Number(this.poolCurrentPage) || 1)
      const start = (page - 1) * this.poolPerPage
      return this.filteredReportsCandidates.slice(start, start + this.poolPerPage)
    },
    poolShowingStart() {
      if (!this.filteredPoolTotalRows) return 0
      return (Math.max(1, Number(this.poolCurrentPage) || 1) - 1) * this.poolPerPage + 1
    },
    poolShowingEnd() {
      return Math.min(this.poolCurrentPage * this.poolPerPage, this.filteredPoolTotalRows)
    },
    /** Hide kebab column entirely for pure read-only users with no offer/bank/assign rights. */
    showPoolActionsColumn() {
      return (
        this.canAssignCandidates
        || this.canGenerateOfferLetter
        || this.canManageBankDetails
        || this.canViewBankDetails
        || this.canViewOffer
        || this.canDownloadOffer
        || this.canSendOfferLetterEmail
      )
    },
    isEmailFormValid() {
      return this.emailForm.to && 
             this.emailForm.to.trim() &&
             this.emailForm.subject && 
             this.emailForm.subject.trim() &&
             this.emailForm.body && 
             this.emailForm.body.trim()
    },
    hasActiveFilters() {
      const hasStatusFilter = Array.isArray(this.appliedFilters.status) 
        ? (this.appliedFilters.status && this.appliedFilters.status.length > 0)
        : (this.appliedFilters.status && this.appliedFilters.status !== '')
      return hasStatusFilter ||
             this.appliedFilters.search ||
             this.appliedFilters.offerLetterStatus ||
             this.appliedFilters.role ||
             this.appliedFilters.location
    },
    // Filters-button badge: modal filters only (search lives in the header)
    hasActiveModalFilters() {
      const hasStatusFilter = Array.isArray(this.appliedFilters.status)
        ? (this.appliedFilters.status && this.appliedFilters.status.length > 0)
        : (this.appliedFilters.status && this.appliedFilters.status !== '')
      return !!(
        hasStatusFilter ||
        this.appliedFilters.offerLetterStatus ||
        this.appliedFilters.role ||
        this.appliedFilters.location
      )
    },
    hasDraftFilters() {
      const hasStatus = Array.isArray(this.filters.status)
        ? (this.filters.status && this.filters.status.length > 0)
        : (this.filters.status && this.filters.status !== '')
      return !!(
        hasStatus ||
        this.filters.search ||
        this.filters.offerLetterStatus ||
        this.filters.role ||
        this.filters.location
      )
    },

    filteredReportsCandidates() {
      let filtered = this.reportsCandidates

      // Filter by candidate status (handle both string and array)
      // Pool shows Shortlisted, Interview, Offered, and Joined by default
      const poolStatuses = ['Shortlisted', 'Offered', 'Joined']
      
      if (this.appliedFilters.status) {
        if (Array.isArray(this.appliedFilters.status)) {
          // If it's an array, filter by any matching status
          if (this.appliedFilters.status.length > 0) {
            filtered = filtered.filter(candidate => {
              return this.appliedFilters.status.includes(candidate.candidateStatus)
            })
          }
        } else {
          // If it's a string, filter by exact match
          filtered = filtered.filter(candidate => {
            return candidate.candidateStatus === this.appliedFilters.status
          })
        }
      } else {
        // If no status filter, show only pool statuses (Shortlisted, Interview, Offered, Joined)
        filtered = filtered.filter(candidate => {
          return poolStatuses.includes(candidate.candidateStatus)
        })
      }

      // Filter by offer letter status
      if (this.appliedFilters.offerLetterStatus) {
        filtered = filtered.filter(candidate => {
          // Check both offerDetails (legacy) and extra_details.offerLetter (new)
          let offerLetterData = candidate.offerDetails || null
          
          // Try to get from extra_details if not in offerDetails
          if (!offerLetterData && candidate.extra_details) {
            try {
              const extraDetails = typeof candidate.extra_details === 'string' 
                ? JSON.parse(candidate.extra_details) 
                : candidate.extra_details
              offerLetterData = extraDetails?.offerLetter || null
            } catch (error) {
              // If parsing fails, offerLetterData remains null
            }
          }
          
          const emailSent = offerLetterData?.emailSent === true || offerLetterData?.emailSent === 'true'
          const hasOfferLetter = offerLetterData && Object.keys(offerLetterData).length > 0

          switch (this.appliedFilters.offerLetterStatus) {
            case 'Generated':
              // Has offer letter but email not sent
              return hasOfferLetter && !emailSent
            case 'Sent':
              // Has offer letter and email sent
              return hasOfferLetter && emailSent
            case 'Pending':
              // No offer letter
              return !hasOfferLetter
            default:
              return true
          }
        })
      }

      // Filter by role/position
      if (this.appliedFilters.role) {
        filtered = filtered.filter(candidate => {
          const positionName = candidate.position_name || ''
          return positionName === this.appliedFilters.role
        })
      }

      // Filter by location (pref_location)
      if (this.appliedFilters.location) {
        filtered = filtered.filter(candidate => {
          const prefLocation = candidate.pref_location || ''
          return prefLocation === this.appliedFilters.location
        })
      }

      // Filter by search term
      if (this.appliedFilters.search && this.appliedFilters.search.trim()) {
        const searchTerm = this.appliedFilters.search.toLowerCase().trim()
        filtered = filtered.filter(candidate => {
          const name = (candidate.candidate_name || '').toLowerCase()
          const email = (candidate.email || '').toLowerCase()
          const phone = (candidate.contact_no || '').toLowerCase()
          const position = (candidate.position_name || '').toLowerCase()
          const id = (candidate.id || candidate.cid || '').toString().toLowerCase()
          
          return name.includes(searchTerm) || 
                 email.includes(searchTerm) || 
                 phone.includes(searchTerm) || 
                 position.includes(searchTerm) ||
                 id.includes(searchTerm)
        })
      }

      return filtered
    },

    filteredPoolTotalRows() {
      return this.filteredReportsCandidates.length
    },

    recruiterFilterOptions() {
      return [
        { value: '', text: 'All Recruiters' },
        ...this.recruiterOptions
      ]
    },
    isOfferLetterFormValid() {
      const hasRequiredFields = this.offerLetterForm.candidateName &&
             this.offerLetterForm.emailId &&
             this.offerLetterForm.joiningDate &&
             this.offerLetterForm.offeredDate &&
             this.offerLetterForm.site &&
             this.offerLetterForm.jobTitle &&
            this.offerLetterForm.location &&
            this.offerLetterForm.reportingManagerName
      
      const hasConfirmations = this.recruiterConfirmed &&
             this.documentsVerified &&
             this.offerDetailsConfirmed
      
      // const hasDocuments = !this.documentCheckEnabled || (this.selectedCandidate && this.checkDocumentsAvailable(this.selectedCandidate))
      
      // return hasRequiredFields && hasConfirmations && hasDocuments
      return hasRequiredFields && hasConfirmations 
    },
    
    joiningDateError() {
      if (!this.offerLetterForm.joiningDate) {
        return 'Joining date is required'
      }
      return null
    },

    locationError() {
      if (!this.offerLetterForm.location || !this.offerLetterForm.location.trim()) {
        return 'Location is required'
      }
      return null
    },

    locationState() {
      if (this.offerLetterForm.location && this.offerLetterForm.location.trim()) {
        return true
      }
      if (this.offerLetterForm.location === '' || (this.offerLetterForm.location && !this.offerLetterForm.location.trim())) {
        return false
      }
      return null
    },

    reportingManagerNameError() {
      if (!this.offerLetterForm.reportingManagerName || !this.offerLetterForm.reportingManagerName.trim()) {
        return 'Reporting manager name is required'
      }
      return null
    },

    reportingManagerNameState() {
      if (this.offerLetterForm.reportingManagerName && this.offerLetterForm.reportingManagerName.trim()) {
        return true
      }
      if (this.offerLetterForm.reportingManagerName === '' || (this.offerLetterForm.reportingManagerName && !this.offerLetterForm.reportingManagerName.trim())) {
        return false
      }
      return null
    },
    
    canPreviewOfferLetter() {
      return this.offerLetterForm.candidateName &&
             this.offerLetterForm.emailId &&
             this.offerLetterForm.joiningDate &&
             this.offerLetterForm.offeredDate &&
            this.offerLetterForm.site
    },
    
    validateJoiningDate() {
      // Validation is handled by the computed property joiningDateError
      // No toast messages here to avoid loops - validation feedback is shown in the form field itself
    },

    // Location Wise & Role Wise Report
    locationRoleWiseReport() {
      const groupedData = {}
      
      // Use reportsCandidates for the report
      this.reportsCandidates.forEach(candidate => {
        const location = candidate.locations || candidate.location || 'Unknown Location'
        const role = candidate.position_name || 'Unknown Role'
        const key = `${location} - ${role}`
        
        if (!groupedData[key]) {
          groupedData[key] = {
            location: location,
            role: role,
            count: 0
          }
        }
        groupedData[key].count++
      })
      
      return Object.values(groupedData)
        .sort((a, b) => {
          // Sort by location first, then by count
          if (a.location !== b.location) {
            return a.location.localeCompare(b.location)
          }
          return b.count - a.count
        })
    },

    // Job ID Wise & Role Wise Report
    jobIdRoleWiseReport() {
      const groupedData = {}
      
      // Use reportsCandidates for the report
      this.reportsCandidates.forEach(candidate => {
        const jobId = candidate.job_id || candidate.jid || 'Unknown Job ID'
        const role = candidate.position_name || 'Unknown Role'
        const key = `${jobId} - ${role}`
        
        if (!groupedData[key]) {
          groupedData[key] = {
            jobId: jobId,
            role: role,
            count: 0
          }
        }
        groupedData[key].count++
      })
      
      return Object.values(groupedData)
        .sort((a, b) => {
          // Sort by job ID first, then by count
          if (a.jobId !== b.jobId) {
            return a.jobId.toString().localeCompare(b.jobId.toString())
          }
          return b.count - a.count
        })
    },

    // Status Wise Report (Shortlisted, Offered, Joined)
    statusWiseReport() {
      const statusFilter = ['Shortlisted', 'Offered', 'Joined']
      const groupedData = {}
      let totalCount = 0
      
      // Use reportsCandidates for the report
      this.reportsCandidates.forEach(candidate => {
        const status = candidate.candidateStatus || 'Unknown Status'
        if (statusFilter.includes(status)) {
          if (!groupedData[status]) {
            groupedData[status] = {
              status: status,
              count: 0
            }
          }
          groupedData[status].count++
          totalCount++
        }
      })
      
      // Convert to array and add percentage
      return Object.values(groupedData)
        .map(item => ({
          ...item,
          percentage: totalCount > 0 ? Math.round((item.count / totalCount) * 100) : 0
        }))
        .sort((a, b) => {
          // Sort by status order: Shortlisted, Offered, Joined
          const order = { 'Shortlisted': 1, 'Offered': 2, 'Joined': 3 }
          return (order[a.status] || 99) - (order[b.status] || 99)
        })
    },

    // Table fields for reports
    locationRoleWiseFields() {
      return [
        { key: 'location', label: 'Location', sortable: true },
        { key: 'role', label: 'Role', sortable: true },
        { key: 'count', label: 'Count', sortable: true }
      ]
    },

    jobIdRoleWiseFields() {
      return [
        { key: 'jobId', label: 'Job ID', sortable: true },
        { key: 'role', label: 'Role', sortable: true },
        { key: 'count', label: 'Count', sortable: true }
      ]
    },

    statusWiseFields() {
      return [
        { key: 'status', label: 'Status', sortable: true },
        { key: 'count', label: 'Count', sortable: true },
        { key: 'percentage', label: 'Percentage', sortable: true }
      ]
    }
  },
  beforeDestroy() {
    this.revokeOfferLetterPdfDisplayUrl()
  },
  async mounted() {
    const rawClientName = this.clientName || this.$route.params.clientName || this.$route.query.clientName || ''
    this.selectedClientName = this.decodeClientName(rawClientName)

    if (!this.selectedClientName) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Client name is required',
          icon: 'AlertCircleIcon',
          variant: 'error',
        },
      }, { timeout: 3000 })
      this.$router.push({ name: 'dashboard-client-selection' })
      return
    }

    if (!this.hasAccess) {
      return
    }

    // Restore applied filters before first fetch (scoped to selected client)
    this.restoreFiltersFromSession()

    try {
      await this.loadBusinessAndPOCOptions()
      await this.loadSiteAndLanguageOptions()
      await this.loadRecruitersList()
      await this.loadReportingManagers()
      await this.loadCandidates()
      await this.loadReports()
      await this.generateDefaultReports()
    } catch (error) {
      console.error('Error in mounted hook:', error)
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Error',
          text: 'Failed to initialize dashboard: ' + (error.message || 'Unknown error'),
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      })
    }
  },
  methods: {
    decodeClientName(value) {
      if (value == null || value === '') return ''
      let str = String(value).replace(/\+/g, ' ')
      // Decode until stable (handles accidental double-encoding / %20 leftovers)
      for (let i = 0; i < 3; i += 1) {
        try {
          const decoded = decodeURIComponent(str)
          if (decoded === str) break
          str = decoded
        } catch (e) {
          break
        }
      }
      return str
    },
    goToClientSelection() {
      this.$router.push({ name: 'dashboard-client-selection' })
    },
    persistFiltersToSession() {
      try {
        sessionStorage.setItem(CLIENT_DASHBOARD_FILTERS_KEY, JSON.stringify({
          clientName: this.selectedClientName,
          filters: { ...this.filters },
          appliedFilters: { ...this.appliedFilters },
        }))
      } catch (e) { /* noop */ }
    },
    restoreFiltersFromSession() {
      try {
        const raw = sessionStorage.getItem(CLIENT_DASHBOARD_FILTERS_KEY)
        if (!raw) return
        const saved = JSON.parse(raw)
        if (!saved || typeof saved !== 'object') return
        // Only restore when stored for the same client (route param is source of truth)
        if (saved.clientName && saved.clientName !== this.selectedClientName) return
        if (saved.filters && typeof saved.filters === 'object') {
          this.filters = { ...this.filters, ...saved.filters }
        }
        if (saved.appliedFilters && typeof saved.appliedFilters === 'object') {
          this.appliedFilters = { ...this.appliedFilters, ...saved.appliedFilters }
        }
      } catch (e) { /* noop */ }
    },
    clearPersistedFilters() {
      try {
        sessionStorage.removeItem(CLIENT_DASHBOARD_FILTERS_KEY)
      } catch (e) { /* noop */ }
    },
    async refreshDashboard() {
      try {
        await this.loadCandidates()
        await this.loadReports()
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Refreshed',
            text: 'Dashboard data updated',
            icon: 'CheckIcon',
            variant: 'success',
          },
        }, { timeout: 2000 })
      } catch (error) {
        console.error('Error refreshing dashboard:', error)
      }
    },
    toggleDocumentCheck() {
      if (!this.canManageDashboardSettings) {
        this.documentCheckEnabled = localStorage.getItem('offerLetterDocCheckEnabled') !== 'false'
        this.showPermissionDeniedToast('change dashboard settings')
        return
      }
      localStorage.setItem('offerLetterDocCheckEnabled', this.documentCheckEnabled.toString())
    },
    showPermissionDeniedToast(action = 'perform this action') {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Access Denied',
          text: `You don't have permission to ${action}.`,
          icon: 'LockIcon',
          variant: 'warning',
        },
      })
    },

    /** Whether the pool row kebab should render any permitted items. */
    hasPoolRowActions(item) {
      if (!item) return false
      const status = item.candidateStatus
      const offerEligibleStatus = status === 'Shortlisted' || status === 'Offered' || status === 'Joined'
      const hasOffer = this.hasOfferLetter(item)
      const hasBankDetails = this.isBankAccountDetailsAvailable(item)
      const bankUploadComplete = hasBankDetails
        && item.documents
        && item.documents.bankProof

      if (this.canAssignCandidates && !item.assignedTo) return true
      if (
        this.canGenerateOfferLetter
        && item.assignedTo
        && offerEligibleStatus
        && !hasOffer
      ) return true
      if (
        this.canManageBankDetails
        && item.assignedTo
        && status !== 'Interview'
        && !bankUploadComplete
      ) return true
      if (this.canViewBankDetails && hasBankDetails) return true
      if (
        hasOffer
        && (this.canViewOffer || this.canGenerateOfferLetter || this.canDownloadOffer || this.canSendOfferLetterEmail)
      ) return true
      return false
    },
    async loadCandidates() {
      if (!this.canViewCandidates) {
        this.loading = false
        return
      }

      if (!this.selectedClientName) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Client name is required to load candidates',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        this.loading = false
        return
      }

      this.loading = true
      this.apiError = null
      try {
        let allCandidates = []
        let allLatestRecords = []
        let statusWiseCount = []
        let currentPage = 1
        const pageSize = 100
        let hasMorePages = true

        while (hasMorePages) {
          this.loadingProgress = `Loading page ${currentPage}...`

          const params = {
            page: currentPage,
            limit: pageSize,
            status: this.filters.status,
            assignedTo: this.filters.recruiter,
            search: this.filters.search,
          }

          const response = await clientDashboardService.getClientCandidates(this.selectedClientName, params)

          if (response.status === 'OK') {
            const candidates = Array.isArray(response.data) ? response.data : []
            const latestRecords = Array.isArray(response.latestRecords) ? response.latestRecords : candidates
            const pagination = response.pagination || {}

            if (currentPage === 1) {
              statusWiseCount = response.statusWise || []
            }

            allCandidates = [...allCandidates, ...candidates]
            allLatestRecords = [...allLatestRecords, ...latestRecords]

            hasMorePages = pagination.hasNextPage || (candidates.length === pageSize)
            currentPage++

            if (currentPage > 20) {
              break
            }
          } else {
            hasMorePages = false
          }
        }

        const combinedCandidates = [...allCandidates, ...allLatestRecords]

        const getCandidateKey = candidate => candidate.id || candidate.cid
        const uniqueCombinedCandidates = combinedCandidates.reduce((acc, candidate) => {
          const candidateId = getCandidateKey(candidate)
          if (!candidateId) {
            return acc
          }

          const existingIndex = acc.findIndex(c => getCandidateKey(c) === candidateId)
          if (existingIndex === -1) {
            acc.push(candidate)
          } else {
            const existing = acc[existingIndex]
            const candidateDate = new Date(candidate.updatedAt || candidate.createdAt || 0)
            const existingDate = new Date(existing.updatedAt || existing.createdAt || 0)
            if (candidateDate > existingDate) {
              acc[existingIndex] = candidate
            }
          }
          return acc
        }, [])

        this.candidates = uniqueCombinedCandidates.map(candidate => ({
          ...candidate,
          selected: false,
        }))

        this.totalRows = uniqueCombinedCandidates.length
        this.paginationInfo = {
          currentPage: 1,
          totalPages: 1,
          totalRecords: uniqueCombinedCandidates.length,
          limit: uniqueCombinedCandidates.length,
          hasNextPage: false,
          hasPreviousPage: false,
        }

        this.updateSummaryCountsFromStatusWise(statusWiseCount)
        this.updateRecruiterOptions(uniqueCombinedCandidates)
        this.updateRoleOptions(uniqueCombinedCandidates)
        this.updateLocationOptions(uniqueCombinedCandidates)
        this.tableKey++
        this.loadingProgress = ''
      } catch (error) {
        console.error('Error loading candidates:', error)
        this.candidates = []
        this.totalRows = 0
        this.apiError = error.message || 'Failed to load candidates'
        this.paginationInfo = {
          currentPage: 1,
          totalPages: 1,
          totalRecords: 0,
          limit: 25,
          hasNextPage: false,
          hasPreviousPage: false,
        }
        this.loadingProgress = ''
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error Loading Candidates',
            text: error.message || 'Failed to load candidates.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.loading = false
      }
    },

    // Helper function to extract offerDetails from candidate
    getOfferDetails(candidate) {
      if (candidate.offerDetails) {
        return candidate.offerDetails
      }
      
      if (candidate.extra_details) {
        try {
          const extraDetails = typeof candidate.extra_details === 'string' 
            ? JSON.parse(candidate.extra_details) 
            : candidate.extra_details
          return extraDetails?.offerLetter || null
        } catch (e) {
          console.warn('Error parsing extra_details for candidate:', candidate.id, e)
          return null
        }
      }
      
      return null
    },

    // Offer-letter KPIs only — never overwrite statusWise Total / Profile Shared / etc.
    // (Loaded candidates can be a pool subset; using candidates.length collapsed Total after offer generate.)
    updateSummaryCounts() {
      this.updateOfferLetterCounts()
    },

    updateOfferLetterCounts() {
      if (!this.candidates || this.candidates.length === 0) {
        this.offerGeneratedCount = 0
        this.offerSentCount = 0
        this.offerPendingCount = 0
        return
      }

      this.offerGeneratedCount = this.candidates.filter(c => this.hasOfferLetter(c)).length

      this.offerSentCount = this.candidates.filter(c => {
        const offerDetails = this.getOfferDetails(c)
        return offerDetails && (offerDetails.emailSent === true || offerDetails.emailSent === 'true')
      }).length

      // Offer Pending: Shortlisted / Joined / Offered without an offer letter
      this.offerPendingCount = this.candidates.filter(c => {
        const status = c.candidateStatus
        const hasValidStatus = status === 'Shortlisted' || status === 'Joined' || status === 'Offered'
        return hasValidStatus && !this.hasOfferLetter(c)
      }).length
    },

    updateSummaryCountsFromStatusWise(statusWiseData) {
      // Guard: empty statusWise must not wipe previously correct KPI totals
      if (!Array.isArray(statusWiseData) || statusWiseData.length === 0) {
        this.updateOfferLetterCounts()
        return
      }

      // Status KPIs always come from API statusWise (full client pool)
      this.totalCandidates = statusWiseData.reduce((total, status) => total + parseInt(status.count, 10), 0)
      this.profileSharedCount = parseInt(statusWiseData.find(s => s.candidateStatus === 'ProfileShared')?.count || 0, 10)
      this.shortlistedCount = parseInt(statusWiseData.find(s => s.candidateStatus === 'Shortlisted')?.count || 0, 10)
      // Include both 'Offered' and 'Offer Generated' in the offered count
      const offeredStatus = statusWiseData.find(s => s.candidateStatus === 'Offered')
      const offerGeneratedStatus = statusWiseData.find(s => s.candidateStatus === 'Offer Generated')
      this.offeredCount = parseInt(offeredStatus?.count || 0, 10) + parseInt(offerGeneratedStatus?.count || 0, 10)
      this.joinedCount = parseInt(statusWiseData.find(s => s.candidateStatus === 'Joined')?.count || 0, 10)

      // Offer-letter KPIs need candidate payload (emailSent / offerLetter)
      this.updateOfferLetterCounts()
    },

    updateRecruiterOptions(candidates) {
      // Extract unique recruiter names from candidates
      const recruiterSet = new Set()
      
      candidates.forEach(candidate => {
        // Use Employee Name first, then fallback to recruiter field, then assignedTo
        const recruiterName = candidate['Employee Name'] || candidate.recruiter || candidate.assignedTo
        if (recruiterName && recruiterName.trim()) {
          recruiterSet.add(recruiterName.trim())
        }
      })
      
      // Convert Set to array and create options
      this.recruiterOptions = Array.from(recruiterSet)
        .sort()
        .map(name => ({
          value: name,
          text: name
        }))
      
      
      // Update POC options after recruiter options are loaded
      this.updatePOCOptions()
    },

    updateRoleOptions(candidates) {
      // Extract unique position names/roles from candidates
      // Extract job title from position_name (e.g., "SHJ-1222 - ITI Electrician" -> "ITI Electrician")
      const extractJobTitle = (positionName) => {
        if (!positionName) return ''
        const parts = positionName.split(' - ')
        return parts.length > 1 ? parts.slice(1).join(' - ').trim() : positionName.trim()
      }
      
      const roleSet = new Set()
      
      candidates.forEach(candidate => {
        const positionName = candidate.position_name
        if (positionName && positionName.trim()) {
          // Extract job title (remove job ID prefix)
          const jobTitle = extractJobTitle(positionName)
          if (jobTitle) {
            roleSet.add(jobTitle)
          }
        }
      })
      
      // Convert Set to array and create options with "All Roles" as first option
      const roles = Array.from(roleSet).sort()
      this.roleOptions = [
        { value: '', text: 'All Roles' },
        ...roles.map(role => ({
          value: role,
          text: role
        }))
      ]
      
    },

    updateLocationOptions(candidates) {
      // Extract unique preferred locations from candidates (pref_location field)
      const locationSet = new Set()
      
      candidates.forEach(candidate => {
        const prefLocation = candidate.pref_location
        if (prefLocation && prefLocation.trim()) {
          locationSet.add(prefLocation.trim())
        }
      })
      
      // Convert Set to array and create options with "All Locations" as first option
      const locations = Array.from(locationSet).sort()
      this.locationOptions = [
        { value: '', text: 'All Locations' },
        ...locations.map(location => ({
          value: location,
          text: location
        }))
      ]
      
    },

    async loadReports() {
      try {
        this.reportsLoading = true
        this.reportsError = null
        
        
        // Call API with specific status filter for reports
        // Note: Search is now applied client-side via appliedFilters.search
        const params = {
          status: ['Shortlisted', 'Offered', 'Joined'],
          limit: 1000,
          search: '',
        }
        
        const response = await clientDashboardService.getClientCandidates(this.selectedClientName, params)
        
        if (response.status === 'ERROR') {
          this.reportsError = response.message || 'Failed to load reports data'
          this.reportsCandidates = []
          return
        }
        
        // The service returns response.data as an array of transformed candidates
        // response.latestRecords is also available but contains the same data
        const allCandidates = Array.isArray(response.data) ? response.data : []
        
        
        // Remove duplicates based on candidate ID (id or cid)
        const uniqueCandidates = allCandidates.reduce((acc, candidate) => {
          const candidateId = candidate.id || candidate.cid
          if (!candidateId) {
            // Skip candidates without ID
            console.warn('Candidate without ID found:', candidate)
            return acc
          }
          
          const existingIndex = acc.findIndex(c => (c.id || c.cid) === candidateId)
          if (existingIndex === -1) {
            acc.push(candidate)
          } else {
            // Keep the more recent record (with updatedAt or createdAt)
            const existing = acc[existingIndex]
            const candidateDate = new Date(candidate.updatedAt || candidate.createdAt || 0)
            const existingDate = new Date(existing.updatedAt || existing.createdAt || 0)
            if (candidateDate > existingDate) {
              acc[existingIndex] = candidate
            }
          }
          return acc
        }, [])
        
        
        // Update role options from the loaded candidates
        this.updateRoleOptions(uniqueCandidates)
        // Update location options from the loaded candidates
        this.updateLocationOptions(uniqueCandidates)
        
        // Filter candidates with Shortlisted, Interview, Offered, or Joined status
        // Note: All other filters (search, role, location, offer letter status) are now
        // applied client-side in the filteredReportsCandidates computed property using appliedFilters
        let reportsData = uniqueCandidates.filter(candidate => 
          candidate.candidateStatus === 'Shortlisted' || 
          candidate.candidateStatus === 'Offered' ||
          candidate.candidateStatus === 'Joined'
        )
        
        
        // Transform data for pool table
        this.reportsCandidates = reportsData.map(candidate => {
          // Extract job title from position_name (e.g., "SHJ-1222 - ITI Electrician" -> "ITI Electrician")
          const extractJobTitle = (positionName) => {
            if (!positionName) return ''
            const parts = positionName.split(' - ')
            return parts.length > 1 ? parts.slice(1).join(' - ').trim() : positionName.trim()
          }
          
          return {
            id: candidate.id || candidate.cid,
            job_id: candidate.job_id || candidate.jid,
            jid: candidate.jid || candidate.job_id, // Preserve jid for compatibility
            candidate_name: candidate.candidate_name,
            position_name: extractJobTitle(candidate.position_name),
            contact_no: candidate.contact_no,
            email: candidate.email,
            candidateStatus: candidate.candidateStatus,
            recruiter: candidate['Employee Name'] || candidate.recruiter || null,
            account_mgr: candidate.account_mgr,
            locations: candidate.locations,
            pref_location: candidate.pref_location, // Add pref_location for location filter
            position_type: candidate.position_type,
            payroll: candidate.payroll,
            category: candidate.category,
            vertical: candidate.vertical,
            client: candidate.client,
            source: candidate.source,
            status: candidate.status,
            rec_id: candidate.rec_id,
            sourcer_id: candidate.sourcer_id,
            mgr_id: candidate.mgr_id,
            sourcer: candidate.sourcer,
            attachments: candidate.attachments,
            selection_date: candidate.selection_date,
            offered_date: candidate.offered_date,
            offered_ctc: candidate.offered_ctc,
            joining_date: candidate.joining_date,
            createdAt: candidate.createdAt,
            updatedAt: candidate.updatedAt,
            business: candidate.business,
            extra_details: candidate.extra_details,
            offerProcess: candidate.offerProcess,
            documents: candidate.documents || {},
            assignedTo: candidate['Employee Name'] || candidate.recruiter || null,
            location: candidate.locations,
            selected: false,
            metadata: candidate.metadata || {},
            offerDetails: candidate.offerDetails || null, // Preserve offer details for filtering
            // Also populate offerDetails from extra_details.offerLetter if available
            ...(candidate.extra_details ? (() => {
              try {
                const extraDetails = typeof candidate.extra_details === 'string' 
                  ? JSON.parse(candidate.extra_details) 
                  : candidate.extra_details
                if (extraDetails?.offerLetter && !candidate.offerDetails) {
                  return { offerDetails: extraDetails.offerLetter }
                }
              } catch (error) {
                // If parsing fails, ignore
              }
              return {}
            })() : {})
          }
        })
        
        // Update pool pagination (will be updated by computed property)
        this.poolTotalRows = this.reportsCandidates.length
        
        // Reset to first page when search is applied
        if (this.filters.search && this.filters.search.trim()) {
          this.poolCurrentPage = 1
        }
        
        this.reportsTableKey++
        
        if (this.filters.search && this.filters.search.trim()) {
        }
        
      } catch (error) {
        console.error('Error loading reports:', error)
        this.reportsError = 'Failed to load reports data. Please try again.'
        this.reportsCandidates = []
      } finally {
        this.reportsLoading = false
      }
    },

    async loadBusinessAndPOCOptions() {
      try {
        // Load POC options - use recruiter options as POC options for now
        // POC options will be populated after recruiters are loaded
        this.pocOptions = []
        
        // Update filter options
        this.pocFilterOptions = [
          { value: '', text: 'All POC' },
          { value: 'null', text: 'Unassigned POC' },
          ...this.pocOptions
        ]
      } catch (error) {
        console.error('Error loading business and POC options:', error)
      }
    },

    async loadReportingManagers() {
      try {
        const response = await adminConfigService.getReportingManagersList()
        
        // Handle different response structures
        let reportingManagersList = []
        
        // Check if response is valid
        if (response) {
          // Case 1: Axios response object (response.status is HTTP status, response.data is the actual data)
          // Accept any 2xx status code (200, 201, etc.)
          if (response.status >= 200 && response.status < 300 && response.data) {
            // Try different data structures
            // Priority 1: response.data.data (nested data - this is what backend returns)
            if (response.data.data && Array.isArray(response.data.data)) {
              reportingManagersList = response.data.data
            }
            // Priority 2: response.data (direct array)
            else if (Array.isArray(response.data)) {
              reportingManagersList = response.data
            }
            // Priority 3: response.data.data.reportingManagersList
            else if (response.data.data && response.data.data.reportingManagersList && Array.isArray(response.data.data.reportingManagersList)) {
              reportingManagersList = response.data.data.reportingManagersList
            }
            // Priority 4: response.data.reportingManagersList
            else if (response.data.reportingManagersList && Array.isArray(response.data.reportingManagersList)) {
              reportingManagersList = response.data.reportingManagersList
            }
          }
          // Case 2: Direct data object (if service returns res.data instead of res)
          else if (response.data) {
            // Check for nested data structure
            if (response.data.data && Array.isArray(response.data.data)) {
              reportingManagersList = response.data.data
            }
            // Check for direct array
            else if (Array.isArray(response.data)) {
              reportingManagersList = response.data
            }
          }
          // Case 3: Response is direct array
          else if (Array.isArray(response)) {
            reportingManagersList = response
          }
        }
        
        
        if (!Array.isArray(reportingManagersList) || reportingManagersList.length === 0) {
          console.warn('No reporting managers data found or empty array')
          this.reportingManagerOptions = []
          return
        }
        
        // Parse title column to extract name and contact number
        // Expected format in title: "Manager Name - Contact Number" or "Manager Name (Contact Number)"
        this.reportingManagerOptions = reportingManagersList
          .map((manager) => {
            const title = manager.title || ''
            if (!title || !title.trim()) {
              console.warn('Manager with empty title:', manager)
              return null
            }
            
            // Try to parse different formats:
            // Format 1: "Manager Name - Contact Number"
            // Format 2: "Manager Name (Contact Number)"
            // Format 3: "Manager Name, Contact Number"
            let name = title.trim()
            let contactNumber = null
            
            // Try format: "Name - Contact"
            if (title.includes(' - ')) {
              const parts = title.split(' - ')
              name = parts[0].trim()
              contactNumber = parts.slice(1).join(' - ').trim()
            }
            // Try format: "Name (Contact)"
            else if (title.includes('(') && title.includes(')')) {
              const match = title.match(/^(.+?)\s*\((.+?)\)\s*$/)
              if (match) {
                name = match[1].trim()
                contactNumber = match[2].trim()
              }
            }
            // Try format: "Name, Contact"
            else if (title.includes(',')) {
              const parts = title.split(',')
              name = parts[0].trim()
              contactNumber = parts.slice(1).join(',').trim()
            }
            
            // Return dropdown option
            return {
              value: name, // Store just the name as value
              text: contactNumber ? `${name} (${contactNumber})` : name // Display with contact number if available
            }
          })
          .filter(option => option && option.value) // Filter out null/empty values
        
        
        if (this.reportingManagerOptions.length === 0) {
          console.warn('No valid reporting manager options after parsing')
          console.warn('Response structure:', JSON.stringify(response, null, 2))
          console.warn('reportingManagersList:', JSON.stringify(reportingManagersList, null, 2))
        } else {
          // Log first option structure for debugging
          if (this.reportingManagerOptions.length > 0) {
          }
        }
      } catch (error) {
        console.error('Error loading reporting managers:', error)
        this.reportingManagerOptions = []
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Warning',
            text: 'Failed to load reporting managers list. Please ensure data is configured in admin-config table.',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
      }
    },

    updatePOCOptions() {
      // Use recruiter options as POC options (can be changed to load from API if needed)
      this.pocOptions = this.recruiterOptions.map(recruiter => ({
        value: recruiter.value,
        text: recruiter.text
      }))
      
      // Update POC filter options
      this.pocFilterOptions = [
        { value: '', text: 'All POC' },
        { value: 'null', text: 'Unassigned POC' },
        ...this.pocOptions
      ]
    },

    getAssignedPOCName(candidate) {
      try {
        if (!candidate.extra_details) {
          return 'N/A'
        }
        
        const extraDetails = typeof candidate.extra_details === 'string' 
          ? JSON.parse(candidate.extra_details) 
          : candidate.extra_details
        
        if (extraDetails && extraDetails.poc) {
          // Remove emp_id from POC name if present (format: "emp_id - first_name last_name")
          const pocName = extraDetails.poc.replace(/^[A-Z]+-\d+\s*-\s*/, '').trim()
          return pocName || 'N/A'
        }
        
        return 'N/A'
      } catch (error) {
        console.error('Error parsing extra details:', error)
        return 'N/A'
      }
    },

    onPageChange(page) {
      this.currentPage = page
      this.loadCandidates()
    },

    onPageSizeChange() {
      this.currentPage = 1
      this.loadCandidates()
    },

    openPoolFilters() {
      this.showPoolFiltersModal = true
    },

    closePoolFilters() {
      this.showPoolFiltersModal = false
    },

    onHeaderSearchInput() {
      if (this._searchDebounceTimer) {
        clearTimeout(this._searchDebounceTimer)
      }
      this._searchDebounceTimer = setTimeout(() => {
        this.applyHeaderSearch()
      }, 300)
    },

    applyHeaderSearch() {
      if (this._searchDebounceTimer) {
        clearTimeout(this._searchDebounceTimer)
        this._searchDebounceTimer = null
      }
      const next = this.filters.search || ''
      if (this.appliedFilters.search === next) return
      this.appliedFilters.search = next
      this.currentPage = 1
      this.poolCurrentPage = 1
      this.persistFiltersToSession()
    },

    onSearchInput() {
      // Apply all modal filters (also syncs header search value)
      this.appliedFilters = {
        status: this.filters.status,
        search: this.filters.search,
        offerLetterStatus: this.filters.offerLetterStatus,
        role: this.filters.role,
        location: this.filters.location
      }
      
      this.currentPage = 1
      this.poolCurrentPage = 1
      this.persistFiltersToSession()
      
      // Reload both main candidates and pool/reports candidates
      this.loadCandidates()
      this.loadReports()
      this.closePoolFilters()
    },

    clearSearch() {
      if (this._searchDebounceTimer) {
        clearTimeout(this._searchDebounceTimer)
        this._searchDebounceTimer = null
      }
      this.filters.search = ''
      this.appliedFilters.search = ''
      this.currentPage = 1
      this.poolCurrentPage = 1
      this.persistFiltersToSession()
    },

    clearFilters() {
      if (this._searchDebounceTimer) {
        clearTimeout(this._searchDebounceTimer)
        this._searchDebounceTimer = null
      }
      // Reset to empty defaults (matches "All …" option values)
      this.filters.offerLetterStatus = ''
      this.filters.status = ''
      this.filters.role = ''
      this.filters.location = ''
      this.filters.search = ''

      this.appliedFilters.offerLetterStatus = ''
      this.appliedFilters.status = ''
      this.appliedFilters.role = ''
      this.appliedFilters.location = ''
      this.appliedFilters.search = ''

      this.poolCurrentPage = 1
      this.currentPage = 1
      this.clearPersistedFilters()

      this.loadCandidates()
      this.loadReports()
      this.closePoolFilters()
    },

    clearOfferLetterStatusFilter() {
      this.filters.offerLetterStatus = ''
      this.poolCurrentPage = 1
    },

    onRowClicked(item) {
      // Handle row click if needed
    },

    onHeadClicked(field) {
      // Handle header click for sorting
    },

    onSelectionChange(candidate) {
      if (candidate.selected) {
        if (!this.selectedCandidates.find(c => c.id === candidate.id)) {
          this.selectedCandidates.push(candidate)
        }
      } else {
        this.selectedCandidates = this.selectedCandidates.filter(c => c.id !== candidate.id)
      }
    },

    clearSelection() {
      this.selectedCandidates = []
      this.candidates.forEach(candidate => {
        candidate.selected = false
      })
      this.tableKey++
    },

    openAssignmentModal(candidate) {
      if (!this.canAssignCandidates) {
        this.showPermissionDeniedToast('assign candidates')
        return
      }
      this.selectedCandidate = candidate
      this.showBusinessModal = true
    },

    openBulkAssignmentModal() {
      if (!this.canAssignCandidates) {
        this.showPermissionDeniedToast('assign candidates')
        return
      }
      this.showPOCModal = true
    },

    async openOfferLetterModal(candidate) {
      if (!this.canGenerateOfferLetter) {
        this.showPermissionDeniedToast('generate offer letters')
        return
      }
      this.selectedCandidate = candidate
      // Check for existing offer letter data in extra_details.offerLetter or offerDetails
      let existingOfferLetter = candidate.offerDetails || null
      if (!existingOfferLetter && candidate.extra_details) {
        try {
          const extraDetails = typeof candidate.extra_details === 'string' 
            ? JSON.parse(candidate.extra_details) 
            : candidate.extra_details
          existingOfferLetter = extraDetails?.offerLetter || null
        } catch (error) {
          console.error('Error parsing extra_details for offer letter:', error)
        }
      }
      
      // Helper function to extract only the name from candidate name (removes ID prefix like "SHC-61399 - ")
      const extractCandidateName = (fullName) => {
        if (!fullName) return ''
        // Check if name contains " - " pattern (ID - NAME)
        const parts = fullName.split(' - ')
        if (parts.length > 1) {
          // Return everything after " - " (the actual name)
          return parts.slice(1).join(' - ').trim()
        }
        // If no " - " pattern, return the full name as is
        return fullName.trim()
      }
      
      // Pre-fill form with existing offer letter data or candidate data
      if (existingOfferLetter) {
        // Extract name from existing offer letter data or candidate name
        const existingName = existingOfferLetter.candidateName || candidate.candidate_name
        this.offerLetterForm.candidateName = extractCandidateName(existingName)
        this.offerLetterForm.emailId = existingOfferLetter.emailId || candidate.email
        this.offerLetterForm.joiningDate = existingOfferLetter.joiningDate || ''
        this.offerLetterForm.offeredDate = existingOfferLetter.offeredDate || new Date().toISOString().split('T')[0]
        this.offerLetterForm.site = existingOfferLetter.site || this.getDefaultOfferLetterSite()
        this.offerLetterForm.language = 'en'
        this.offerLetterForm.location = existingOfferLetter.location || ''
        this.offerLetterForm.reportingManagerName = existingOfferLetter.reportingManagerName || ''
        this.offerLetterForm.jobTitle = existingOfferLetter.jobTitle || ''
        
        // Check if email was already sent
        this.emailSent = existingOfferLetter.emailSent === true || existingOfferLetter.emailSent === 'true'
      } else {
      // Pre-fill form with candidate data
        this.offerLetterForm.candidateName = extractCandidateName(candidate.candidate_name)
      this.offerLetterForm.emailId = candidate.email
      this.offerLetterForm.offeredDate = new Date().toISOString().split('T')[0] // Today's date
      this.offerLetterForm.site = this.getDefaultOfferLetterSite()
      this.offerLetterForm.language = 'en'
        this.offerLetterForm.location = ''
        this.offerLetterForm.reportingManagerName = ''
        this.offerLetterForm.jobTitle = ''
        this.emailSent = false
      }
      
      // Pre-fill reporting manager name from extra_details if not already set
      if (!this.offerLetterForm.reportingManagerName && candidate.extra_details) {
        try {
          const extraDetails = typeof candidate.extra_details === 'string' 
            ? JSON.parse(candidate.extra_details) 
            : candidate.extra_details
          if (extraDetails && extraDetails.reportingManagerName) {
            this.offerLetterForm.reportingManagerName = extraDetails.reportingManagerName
          }
        } catch (error) {
          console.error('Error parsing extra_details for reporting manager:', error)
        }
      }
      
      // Fetch job titles for the selected site
      if (this.offerLetterForm.site) {
        await this.fetchJobTitleOptions(this.offerLetterForm.site)
      }
      
      this.showOfferLetterModal = true
    },

    async assignCandidate() {
      if (!this.canAssignCandidates) {
        this.showPermissionDeniedToast('assign candidates')
        return
      }
      if (!this.assignmentForm.poc) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validation Error',
            text: 'Please select a POC',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      try {
        const userData = JSON.parse(localStorage.getItem("userData"))
        const user = `[{"title" : "${userData.first_name} ${userData.last_name}", "emp_id": "${userData.emp_id}"}]`
        const username = `${userData.first_name} ${userData.last_name}`

        // Handle POC assignment using updateCandidateStatus with extra_details
        // Get existing extra_details or initialize empty object
        let existingExtraDetails = {}
        if (this.selectedCandidate.extra_details) {
          try {
            existingExtraDetails = typeof this.selectedCandidate.extra_details === 'string' 
              ? JSON.parse(this.selectedCandidate.extra_details) 
              : this.selectedCandidate.extra_details
          } catch (error) {
            console.error('Error parsing extra_details:', error)
            existingExtraDetails = {}
          }
        }
        
        // Prepare new assignment data
        const selectedPOC = this.pocOptions.find(p => p.value === this.assignmentForm.poc)
        const pocTitle = selectedPOC ? selectedPOC.text : this.assignmentForm.poc
        
        const newAssignmentData = {
          ...existingExtraDetails,
          poc: pocTitle,
          assignedBy: username,
          assignedAt: new Date().toISOString()
        }
        
        const payload = {
          candidateStatus: this.selectedCandidate.candidateStatus || 'Shortlisted',
          updatedBy: user,
          extra_details: newAssignmentData
        }
        
        const pocResponse = await candidateService.updateCandidateStatus(
          this.selectedCandidate.id || this.selectedCandidate.cid,
          this.selectedCandidate.job_id || this.selectedCandidate.jid,
          payload
        )
        
        if (pocResponse.status === 'OK') {
          // Update local state with POC info
          const candidateIndex = this.candidates.findIndex(c => c.id === this.selectedCandidate.id)
          if (candidateIndex !== -1) {
            this.candidates[candidateIndex].extra_details = newAssignmentData
          }
          
          const poolCandidateIndex = this.reportsCandidates.findIndex(c => c.id === this.selectedCandidate.id)
          if (poolCandidateIndex !== -1) {
            this.reportsCandidates[poolCandidateIndex].extra_details = newAssignmentData
          }
        }

        // Create timeline entry
        const timelineData = {
          title: `Candidate Id - ${this.selectedCandidate.id} - Assigned to POC via ${this.selectedClientName} Dashboard`,
          description: `CS-Assign to POC via ${this.selectedClientName} Dashboard`,
          type: "success",
          createdBy: username,
          jobId: this.selectedCandidate.job_id,
          candidate_id: this.selectedCandidate.id,
          candidateStatus: 'Shortlisted'
        }
        
        await jobTimelineService.create(timelineData)

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            text: 'Candidate assigned to POC successfully',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })

        this.showBusinessModal = false
        this.resetAssignmentForm()
      } catch (error) {
        console.error('Error assigning candidate:', error)
        const errorMessage = error.response?.data?.message || error.response?.status === 410 
          ? 'Candidate is already assigned to this job/recruiter' 
          : error.message || 'Failed to assign candidate'
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error.response?.status === 410 ? 'Warning' : 'Error',
            text: errorMessage,
            icon: 'AlertTriangleIcon',
            variant: error.response?.status === 410 ? 'warning' : 'danger',
          },
        })
      }
    },

    async bulkAssignCandidates() {
      if (!this.canAssignCandidates) {
        this.showPermissionDeniedToast('assign candidates')
        return
      }
      if (!this.bulkAssignmentForm.poc) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validation Error',
            text: 'Please select a POC',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      try {
        const userData = JSON.parse(localStorage.getItem("userData"))
        const user = `[{"title" : "${userData.first_name} ${userData.last_name}", "emp_id": "${userData.emp_id}"}]`
        const username = `${userData.first_name} ${userData.last_name}`

        // Handle POC assignment for all selected candidates
        const selectedPOC = this.pocOptions.find(p => p.value === this.bulkAssignmentForm.poc)
        const pocTitle = selectedPOC ? selectedPOC.text : this.bulkAssignmentForm.poc

        for (const candidate of this.selectedCandidates) {
          // Get existing extra_details or initialize empty object
          let existingExtraDetails = {}
          if (candidate.extra_details) {
            try {
              existingExtraDetails = typeof candidate.extra_details === 'string' 
                ? JSON.parse(candidate.extra_details) 
                : candidate.extra_details
            } catch (error) {
              console.error('Error parsing extra_details:', error)
              existingExtraDetails = {}
            }
          }
          
          const newAssignmentData = {
            ...existingExtraDetails,
            poc: pocTitle,
            assignedBy: username,
            assignedAt: new Date().toISOString()
          }
          
          const payload = {
            candidateStatus: candidate.candidateStatus || 'Shortlisted',
            updatedBy: user,
            extra_details: newAssignmentData
          }
          
          try {
            const pocResponse = await candidateService.updateCandidateStatus(
              candidate.id || candidate.cid,
              candidate.job_id || candidate.jid,
              payload
            )
            
            if (pocResponse.status === 'OK') {
              // Update local state
              const candidateIndex = this.candidates.findIndex(c => c.id === candidate.id)
              if (candidateIndex !== -1) {
                this.candidates[candidateIndex].extra_details = newAssignmentData
              }
            }
          } catch (error) {
            console.error(`Error assigning POC to candidate ${candidate.id}:`, error)
          }
        }

        // Create timeline entries
        for (const candidate of this.selectedCandidates) {
          const timelineData = {
            title: `Candidate Id - ${candidate.id} - Bulk Assigned to POC via ${this.selectedClientName} Dashboard`,
            description: `CS-Bulk Assign to POC via ${this.selectedClientName} Dashboard`,
            type: "success",
            createdBy: username,
            jobId: candidate.job_id,
            candidate_id: candidate.id,
            candidateStatus: 'Shortlisted'
          }
          
          await jobTimelineService.create(timelineData)
        }

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            text: `${this.selectedCandidates.length} candidates assigned to POC successfully`,
            icon: 'CheckIcon',
            variant: 'success',
          },
        })

        this.showPOCModal = false
        this.clearSelection()
        this.resetBulkAssignmentForm()
      } catch (error) {
        console.error('Error bulk assigning candidates:', error)
        const errorMessage = error.response?.data?.message || error.response?.status === 410 
          ? 'Some candidates may already be assigned. Please check individual candidates.' 
          : error.message || 'Failed to assign candidates'
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error.response?.status === 410 ? 'Warning' : 'Error',
            text: errorMessage,
            icon: 'AlertTriangleIcon',
            variant: error.response?.status === 410 ? 'warning' : 'danger',
          },
        })
      }
    },

    async generateOfferLetter() {
      if (!this.canGenerateOfferLetter) {
        this.showPermissionDeniedToast('generate offer letters')
        return
      }
      // Validate joining date
      if (!this.offerLetterForm.joiningDate) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validation Error',
            text: 'Please select a joining date before generating the offer letter',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }
      
      if (!this.isOfferLetterFormValid) {
        return
      }

      // Set loading state
      this.isGeneratingOfferLetter = true

      try {
        const userData = JSON.parse(localStorage.getItem("userData"))
        const username = `${userData.first_name} ${userData.last_name}`

        // Prepare offer details
        const offerDetails = {
          candidateName: this.offerLetterForm.candidateName,
          emailId: this.offerLetterForm.emailId,
          joiningDate: this.offerLetterForm.joiningDate,
          offeredDate: this.offerLetterForm.offeredDate,
          site: this.offerLetterForm.site,
          language: 'en',
          location: this.offerLetterForm.location,
          reportingManagerName: this.offerLetterForm.reportingManagerName,
          jobTitle: this.offerLetterForm.jobTitle
        }

        // Generate offer letter HTML (simplified - single call)
        const result = await offerLetterService.generateOfferLetterHTMLForDownload(
          this.selectedCandidate, 
          offerDetails, 
          this.offerLetterForm.site, 
          'en',
          this.offerLetterForm.jobTitle
        )
        
        if (result.success && result.html) {
          const user = `[{"title" : "${username}", "emp_id": "${userData.emp_id}"}]`
          let emailSentStatus = false
          let sentByUser = null

          // Email sending is now handled from post-generation modal only
          this.emailSent = false
          if (false && this.sendEmail && this.offerLetterForm.emailId) {
            try {
              await this.sendOfferLetterEmail(
                this.selectedCandidate,
                offerDetails,
                this.offerLetterForm.emailId
              )
              
              // Create timeline entry for email
              const emailTimelineData = {
                title: `Professional Offer Letter Sent via Email to Candidate Id - ${this.selectedCandidate.id}`,
                description: `CS-Professional Offer Letter Sent via Email via ${this.selectedClientName} Dashboard`,
                type: "success",
                createdBy: username,
                jobId: this.selectedCandidate.job_id,
                candidate_id: this.selectedCandidate.id,
                candidateStatus: 'Offer Generated'
              }
              
              await jobTimelineService.create(emailTimelineData)
              
              this.emailSent = true
              emailSentStatus = true
              sentByUser = user
              
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: 'Success',
                  text: `Offer letter sent successfully to ${this.offerLetterForm.emailId}`,
                  icon: 'MailIcon',
                  variant: 'success',
                },
              })
            } catch (emailError) {
              console.error('Error sending offer letter email:', emailError)
              // Continue even if email fails, but show warning
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: 'Warning',
                  text: 'Offer letter generated but email sending failed. You can try sending from the post-generation options.',
                  icon: 'AlertTriangleIcon',
                  variant: 'warning',
                },
              })
            }
          }

          // Prepare offer letter data with tracking information (declare outside try block for later use)
          let offerLetterData = {
            ...offerDetails,
            emailSent: emailSentStatus,
            generatedBy: user,
            sentBy: sentByUser,
            generatedAt: new Date().toISOString(),
            sentAt: emailSentStatus ? new Date().toISOString() : null,
            version: 1 // Initialize version number
          }

          // Save all offer letter data to candidate's extra_details
          try {
            // Get existing extra_details or initialize empty object
            let existingExtraDetails = {}
            const candidateExtraDetails = this.selectedCandidate.extra_details || {}
            try {
              existingExtraDetails = typeof candidateExtraDetails === 'string' 
                ? JSON.parse(candidateExtraDetails) 
                : candidateExtraDetails
            } catch (error) {
              console.error('Error parsing extra_details:', error)
              existingExtraDetails = {}
            }
            
            // Check if there's an existing offer letter to maintain version history
            let offerLetterHistory = existingExtraDetails.offerLetterHistory || []
            const existingOfferLetter = existingExtraDetails.offerLetter
            
            // If there's an existing offer letter, archive it to history before overwriting
            if (existingOfferLetter && Object.keys(existingOfferLetter).length > 0) {
              // Increment version number based on existing version or history length
              const currentVersion = existingOfferLetter.version || (offerLetterHistory.length + 1)
              offerLetterData.version = currentVersion + 1
              
              // Add previous version to history (keep last 10 versions)
              offerLetterHistory.unshift({
                ...existingOfferLetter,
                archivedAt: new Date().toISOString()
              })
              
              // Keep only last 10 versions in history
              if (offerLetterHistory.length > 10) {
                offerLetterHistory = offerLetterHistory.slice(0, 10)
              }
            }
            
            // Merge with existing extra_details, preserving other fields
            const mergedExtraDetails = {
              ...existingExtraDetails,
              offerLetter: offerLetterData,
              offerLetterHistory: offerLetterHistory, // Store version history
              reportingManagerName: this.offerLetterForm.reportingManagerName || existingExtraDetails.reportingManagerName
            }
            
            const currentStatus = this.selectedCandidate.candidateStatus || 'Offered'
            const statusUpdateData = {
              candidateStatus: currentStatus,
              extra_details: mergedExtraDetails,
              updatedBy: user
            }
            
            const response = await candidateService.updateCandidateStatus(
              this.selectedCandidate.id,
              this.selectedCandidate.job_id,
              statusUpdateData
            )
            
            if (response && (response.status === 'OK' || response.status === 200 || response.statusCode === 200)) {
              
              // Update local state
              const candidateIndex = this.candidates.findIndex(c => c.id === this.selectedCandidate.id)
              if (candidateIndex !== -1) {
                this.candidates[candidateIndex].extra_details = mergedExtraDetails
              }
              
              const poolCandidateIndex = this.reportsCandidates.findIndex(c => c.id === this.selectedCandidate.id)
              if (poolCandidateIndex !== -1) {
                this.reportsCandidates[poolCandidateIndex].extra_details = mergedExtraDetails
              }
            }
          } catch (extraDetailsError) {
            console.error('Error saving offer letter data to extra_details:', extraDetailsError)
            // Continue even if save fails, but log the error
          }

          // Store offer details locally (without changing candidate status)
          const offerDetailsWithTracking = {
            ...offerDetails,
            emailSent: emailSentStatus,
            generatedBy: user,
            sentBy: sentByUser,
            generatedAt: new Date().toISOString(),
            sentAt: emailSentStatus ? new Date().toISOString() : null,
            version: offerLetterData.version // Include version number
          }
          
          const candidateIndex = this.candidates.findIndex(c => c.id === this.selectedCandidate.id)
          if (candidateIndex !== -1) {
            this.candidates[candidateIndex].offerDetails = offerDetailsWithTracking
          }
          
          // Also update in pool/reports candidates if present
          const poolCandidateIndex = this.reportsCandidates.findIndex(c => c.id === this.selectedCandidate.id)
          if (poolCandidateIndex !== -1) {
            this.reportsCandidates[poolCandidateIndex].offerDetails = offerDetailsWithTracking
          }

          // Generate PDF from HTML and save to S3
          let pdfBase64 = null
          try {
            // Storage filename — never use ATS display labels ("SHC-369212 - NAME").
            this.offerLetterFilename = offerLetterService.buildGeneratedOfferLetterFilename({
              candidateName: offerDetails.candidateName || this.selectedCandidate.candidate_name,
              location: offerDetails.location,
            })
            
            const stampSrc = extractOfferSignatureImageSrc(result.html)
            const pdfResult = await offerLetterService.generatePDFFromHTML(
              result.html,
              this.offerLetterFilename,
              this.selectedCandidate.id,
              this.selectedCandidate.job_id,
              stampSrc
                ? { signature: stampSrc, signatureSrc: stampSrc, images: { signature: stampSrc } }
                : {},
            )
            
            // Prefer inline base64; service also falls back to s3Location URL.
            pdfBase64 = offerLetterService.extractPdfPayload(pdfResult)
            // Persist the exact generate-pdf key (e.g. OFFERLETTER-DALVIR-KUMAR-mohali.pdf).
            // Do not add OFFERLETTER/ or strip the OFFERLETTER- filename prefix.
            const exactGenerateKey = offerLetterService.captureGeneratePdfStorageKey(pdfResult)
            if (exactGenerateKey) {
              this.offerLetterFilename = exactGenerateKey
            }
          } catch (pdfError) {
            console.error('Error generating PDF:', pdfError)
            // Continue even if PDF generation fails; surface server reason when available
            const serverMsg = pdfError?.message
              || pdfError?.response?.data?.message
              || 'PDF generation failed'
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Warning',
                text: `Offer letter generated but PDF failed: ${serverMsg}. HTML is still available.`,
                icon: 'AlertTriangleIcon',
                variant: 'warning',
              },
            })
          }

          // Save offerLetterFilename to candidate_jobs extra_details if PDF was generated
          if (this.offerLetterFilename
            && !offerLetterService.isForbiddenOfferLetterMailKey(this.offerLetterFilename)) {
            try {
              // Get existing extra_details
              let existingExtraDetails = {}
              const candidateExtraDetails = this.selectedCandidate.extra_details || {}
              try {
                existingExtraDetails = typeof candidateExtraDetails === 'string' 
                  ? JSON.parse(candidateExtraDetails) 
                  : candidateExtraDetails
              } catch (error) {
                console.error('Error parsing extra_details:', error)
                existingExtraDetails = {}
              }
              
              const exactKey = this.offerLetterFilename
              const existingOffer = existingExtraDetails.offerLetter
                && typeof existingExtraDetails.offerLetter === 'object'
                ? existingExtraDetails.offerLetter
                : {}
              const updatedExtraDetails = {
                ...existingExtraDetails,
                offerLetterFilename: exactKey,
                s3Key: exactKey,
                offerLetterS3Key: exactKey,
                offerLetter: {
                  ...existingOffer,
                  s3Key: exactKey,
                  offerLetterFilename: exactKey,
                },
              }
              
              // Update candidate_jobs extra_details
              const statusUpdateData = {
                candidateStatus: this.selectedCandidate.candidateStatus || 'Offered',
                extra_details: updatedExtraDetails,
                updatedBy: user
              }
              
              await candidateService.updateCandidateStatus(
                this.selectedCandidate.id,
                this.selectedCandidate.job_id,
                statusUpdateData
              )
              
            } catch (saveFilenameError) {
              console.error('Error saving offer letter filename to extra_details:', saveFilenameError)
              // Continue even if save fails
            }
          }

          // Store generated offer letter data for post-generation actions
          this.generatedOfferLetterData = {
            candidate: this.selectedCandidate,
            offerDetails: {
              ...offerDetails,
              emailSent: emailSentStatus,
              generatedBy: user,
              sentBy: sentByUser
            },
            html: result.html,
            pdfBase64: pdfBase64, // Base64 PDF for display
            offerLetterFilename: this.offerLetterFilename, // exact generate-pdf key
            generatedS3Key: this.offerLetterFilename,
            s3Key: this.offerLetterFilename,
          }

          // Create timeline entry
          const timelineData = {
            title: `Professional Offer Letter Generated for Candidate Id - ${this.selectedCandidate.id}`,
            description: `CS-Professional Offer Letter Generated via ${this.selectedClientName} Dashboard`,
            type: "success",
            createdBy: username,
            jobId: this.selectedCandidate.job_id,
            candidate_id: this.selectedCandidate.id
          }
          
          await jobTimelineService.create(timelineData)

          // Reload candidates data to get updated extra_details with offerLetterFilename
          await this.loadCandidates()
          await this.loadReports()

          // Close the generation modal and show post-generation modal
          this.showOfferLetterModal = false
          this.showPostGenerationModal = true
          this.resetOfferLetterForm()
          this.updateSummaryCounts()
        } else {
          throw new Error(result.message || 'Failed to generate offer letter')
        }
      } catch (error) {
        console.error('Error generating offer letter:', error)
        
        // Close the generation modal
        this.showOfferLetterModal = false
        
        // Set error message
        this.offerLetterErrorMessage = error.message || 'An unexpected error occurred while generating the offer letter. Please check all fields and try again.'
        
        // Show error modal instead of success modal
        this.showOfferLetterErrorModal = true
        
        // Also show toast for additional feedback
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Failed to generate offer letter. Please check the error details.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        // Reset loading state
        this.isGeneratingOfferLetter = false
      }
    },

    resetAssignmentForm() {
      this.assignmentForm = {
        poc: null
      }
      this.selectedCandidate = null
    },

    resetBulkAssignmentForm() {
      this.bulkAssignmentForm = {
        poc: null
      }
    },

    resetOfferLetterForm() {
      this.offerLetterForm = {
        candidateName: '',
        emailId: '',
        joiningDate: '',
        offeredDate: '',
        site: 'assam',
        language: 'en',
        location: '',
        reportingManagerName: '',
        jobTitle: ''
      }
      this.jobTitleOptions = []
      this.recruiterConfirmed = false
      this.documentsVerified = false
      this.offerDetailsConfirmed = false
      this.sendEmail = false
      this.selectedCandidate = null
      this.isGeneratingOfferLetter = false // Reset loading state
    },

    // Handle site change - fetch job titles for selected site
    async onSiteChange(siteCode) {
      if (!siteCode) {
        this.jobTitleOptions = []
        this.offerLetterForm.jobTitle = ''
        return
      }
      await this.fetchJobTitleOptions(siteCode)
    },

    // Fetch job title options from configuration column for a given site
    async fetchJobTitleOptions(siteCode) {
      try {
        this.jobTitleOptions = []
        this.offerLetterForm.jobTitle = ''
        
        // Fetch all configurations for this site
        const response = await siteConfigService.getAllSiteConfigsByCode(siteCode)
        
        if (response.status === 'OK' && response.data && Array.isArray(response.data)) {
          // Extract unique job titles from configuration column
          const jobTitlesSet = new Set()
          
          response.data.forEach(config => {
            if (config.configuration) {
              try {
                let configData = config.configuration
                // Parse if it's a string
                if (typeof configData === 'string') {
                  configData = JSON.parse(configData)
                }
                
                // Extract jobTitle from configuration
                if (configData && configData.jobTitle) {
                  jobTitlesSet.add(configData.jobTitle)
                }
              } catch (error) {
                console.error('Error parsing configuration:', error)
              }
            }
          })
          
          // Convert Set to array of options
          this.jobTitleOptions = Array.from(jobTitlesSet).map(jobTitle => ({
            value: jobTitle,
            text: jobTitle
          }))
          
          // If only one option, auto-select it
          if (this.jobTitleOptions.length === 1) {
            this.offerLetterForm.jobTitle = this.jobTitleOptions[0].value
          }
        } else {
          console.warn('No configurations found for site:', siteCode)
          this.jobTitleOptions = []
        }
      } catch (error) {
        console.error('Error fetching job title options:', error)
        this.jobTitleOptions = []
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Failed to fetch job titles for selected site',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },

    resetPostGenerationModal() {
      this.showPostGenerationModal = false
      this.generatedOfferLetterData = null
      this.confirmSendEmail = false
      this.emailSent = false
    },

    openBankDetailsModal(candidate) {
      if (!this.canManageBankDetails) {
        this.showPermissionDeniedToast('manage bank details')
        return
      }
      this.selectedBankCandidate = candidate
      // Pre-fill form with existing values from either metadata or extra_details
      // Use values from whichever location has them, or empty if both are missing
      const accountNumber = this.getBankAccountNumber(candidate)
      const ifscCode = this.getBankIFSCCode(candidate)
      
      this.bankDetailsForm = {
        accountNumber: accountNumber || '',
        confirmAccountNumber: '',
        ifscCode: ifscCode || '',
        bankProofFile: null
      }
      this.showBankDetailsModal = true
    },

    resetBankDetailsModal() {
      this.showBankDetailsModal = false
      this.selectedBankCandidate = null
      this.bankDetailsForm = {
        accountNumber: '',
        confirmAccountNumber: '',
        ifscCode: '',
        bankProofFile: null
      }
    },

    openViewAccountDetailsModal(candidate) {
      if (!this.canViewBankDetails) {
        this.showPermissionDeniedToast('view bank details')
        return
      }
      this.selectedAccountDetailsCandidate = candidate
      this.showViewAccountDetailsModal = true
    },

    resetViewAccountDetailsModal() {
      this.showViewAccountDetailsModal = false
      this.selectedAccountDetailsCandidate = null
    },

    async copyToClipboard(text, label) {
      try {
        if (!text || text.trim() === '') {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error',
              text: `${label} is empty`,
              icon: 'AlertCircleIcon',
              variant: 'warning',
            },
          })
          return
        }

        // Use Clipboard API if available
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Copied!',
              text: `${label} copied to clipboard`,
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        } else {
          // Fallback for older browsers
          const textArea = document.createElement('textarea')
          textArea.value = text
          textArea.style.position = 'fixed'
          textArea.style.left = '-999999px'
          textArea.style.top = '-999999px'
          document.body.appendChild(textArea)
          textArea.focus()
          textArea.select()
          
          try {
            document.execCommand('copy')
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Copied!',
                text: `${label} copied to clipboard`,
                icon: 'CheckIcon',
                variant: 'success',
              },
            })
          } catch (err) {
            console.error('Fallback copy failed:', err)
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Error',
                text: 'Failed to copy to clipboard',
                icon: 'AlertCircleIcon',
                variant: 'danger',
              },
            })
          } finally {
            document.body.removeChild(textArea)
          }
        }
      } catch (error) {
        console.error('Error copying to clipboard:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Failed to copy to clipboard',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      }
    },

    handleBankProofFileChange(event) {
      const file = event.target.files ? event.target.files[0] : event
      if (file) {
        this.bankDetailsForm.bankProofFile = file
      }
    },

    preventPaste(event) {
      event.preventDefault()
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Copy-paste is not allowed',
          text: 'Please type the account number manually',
          icon: 'AlertTriangleIcon',
          variant: 'warning',
        },
      })
      return false
    },

    preventCopy(event) {
      event.preventDefault()
      return false
    },

    preventCut(event) {
      event.preventDefault()
      return false
    },

    async saveBankDetails() {
      if (!this.canManageBankDetails) {
        this.showPermissionDeniedToast('manage bank details')
        return
      }
      if (!this.selectedBankCandidate) return

      // Check which locations need account details
      const needsAccountNumberInMeta = this.needsAccountNumberInMetadata(this.selectedBankCandidate)
      const needsAccountNumberInExtra = this.needsAccountNumberInExtraDetails(this.selectedBankCandidate)
      const needsIFSCInMeta = this.needsIFSCCodeInMetadata(this.selectedBankCandidate)
      const needsIFSCInExtra = this.needsIFSCCodeInExtraDetails(this.selectedBankCandidate)
      
      // Get existing values from whichever location has them
      const existingAccountNumber = this.getBankAccountNumber(this.selectedBankCandidate)
      const existingIFSC = this.getBankIFSCCode(this.selectedBankCandidate)
      
      // Check if any fields need to be saved (missing in at least one location)
      const needsAnyAccountDetails = needsAccountNumberInMeta || needsAccountNumberInExtra || needsIFSCInMeta || needsIFSCInExtra
      
      // Determine what values to use: form value if provided, otherwise existing value from other location
      let accountNumberToUse = (this.bankDetailsForm.accountNumber && this.bankDetailsForm.accountNumber.trim()) || existingAccountNumber || ''
      let ifscToUse = (this.bankDetailsForm.ifscCode && this.bankDetailsForm.ifscCode.trim()) || existingIFSC || ''
      
      // Validate account number only if it's missing in BOTH locations and form doesn't have it
      if (needsAccountNumberInMeta && needsAccountNumberInExtra && !accountNumberToUse) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validation Error',
            text: 'Account Number is required',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      // Validate that account number and confirm account number match
      if (needsAccountNumberInMeta && needsAccountNumberInExtra && this.bankDetailsForm.accountNumber && this.bankDetailsForm.confirmAccountNumber) {
        if (this.bankDetailsForm.accountNumber !== this.bankDetailsForm.confirmAccountNumber) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Validation Error',
              text: 'Account numbers do not match. Please ensure both fields have the same value.',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          })
          return
        }
      }

      // Validate that confirm account number is provided when account number is provided
      if (needsAccountNumberInMeta && needsAccountNumberInExtra && this.bankDetailsForm.accountNumber && !this.bankDetailsForm.confirmAccountNumber) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validation Error',
            text: 'Please confirm the account number',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      // Validate IFSC only if it's missing in BOTH locations and form doesn't have it
      if (needsIFSCInMeta && needsIFSCInExtra && !ifscToUse) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validation Error',
            text: 'IFSC Code is required',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      // Validate IFSC format if we have a value
      let uppercaseIfsc = null
      if (ifscToUse) {
        uppercaseIfsc = ifscToUse.toUpperCase().trim()
        const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/
        if (!ifscRegex.test(uppercaseIfsc)) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Validation Error',
              text: 'Please enter a valid IFSC code (e.g., SBIN0001234)',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          })
          return
        }
      }

      // Check if there's anything to save (account details or bank proof file)
      if (!needsAnyAccountDetails && !this.bankDetailsForm.bankProofFile) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'No Changes',
            text: 'No bank details or file to save',
            icon: 'InfoIcon',
            variant: 'info',
          },
        })
        return
      }

      try {
        const userData = JSON.parse(localStorage.getItem("userData"))
        const username = `${userData.first_name} ${userData.last_name}`
        const user = `[{"title" : "${username}", "emp_id": "${userData.emp_id}"}]`

        // Update candidate with bank details - only save to locations that need them
        if (needsAnyAccountDetails) {
          // Based on CandidatesView.vue, metadata is passed as a field in the update data
          // Merge with existing metadata to preserve other fields
          const existingMetadata = this.selectedBankCandidate.candidateMetadata || 
                                  (this.selectedBankCandidate.metadata || {})
          
          // Get existing extra_details or initialize empty object
          let extraDetails = {}
          const existingExtraDetails = this.selectedBankCandidate.extra_details || {}
          try {
            extraDetails = typeof existingExtraDetails === 'string' 
              ? JSON.parse(existingExtraDetails) 
              : existingExtraDetails
          } catch (error) {
            console.error('Error parsing extra_details:', error)
            extraDetails = {}
          }
          
          // Build merged metadata - only add fields that are missing
          // Use accountNumberToUse and uppercaseIfsc which already have the correct values
          const mergedMetadata = { ...existingMetadata }
          if (needsAccountNumberInMeta && accountNumberToUse) {
            mergedMetadata.accountNumber = accountNumberToUse.trim()
          }
          if (needsIFSCInMeta && uppercaseIfsc) {
            mergedMetadata.ifscCode = uppercaseIfsc
          }
          
          // Build merged extra_details - only add fields that are missing
          const mergedExtraDetails = { ...extraDetails }
          if (needsAccountNumberInExtra && accountNumberToUse) {
            mergedExtraDetails.accountNumber = accountNumberToUse.trim()
          }
          if (needsIFSCInExtra && uppercaseIfsc) {
            mergedExtraDetails.ifscCode = uppercaseIfsc
          }
          

          // Use updateCandidateStatus with current status and metadata field (as per CandidatesView.vue pattern)
          // The API expects metadata as a field in the status update data
          // We need to include the current status to avoid changing it
          const currentStatus = this.selectedBankCandidate.candidateStatus || 'Shortlisted'
          const statusUpdateData = {
            candidateStatus: currentStatus, // Include current status to preserve it
            metadata: mergedMetadata,
            extra_details: mergedExtraDetails, // Also save to extra_details
            updatedBy: user
          }
          
          
          const response = await candidateService.updateCandidateStatus(
            this.selectedBankCandidate.id,
            this.selectedBankCandidate.job_id,
            statusUpdateData
          )
          
          
          // Check if the update was successful
          if (!response || (response.status !== 'OK' && response.status !== 200 && response.statusCode !== 200)) {
            const errorMsg = response?.message || response?.error || 'Failed to save bank details to database'
            console.error('API update failed. Full response:', response)
            throw new Error(errorMsg)
          }
        }
        
        // If bank proof file was uploaded, handle file upload to S3
        if (this.bankDetailsForm.bankProofFile && this.selectedBankCandidate) {
          try {
            // Validate file size (max 10MB)
            const maxFileSize = 10 * 1024 * 1024 // 10MB in bytes
            if (this.bankDetailsForm.bankProofFile.size > maxFileSize) {
              throw new Error(`File size exceeds the maximum limit of 10MB. Please compress the file or choose a smaller file.`)
            }

            const uploadFormData = new FormData()
            uploadFormData.append('bankFile', this.bankDetailsForm.bankProofFile)
            uploadFormData.append('id', this.selectedBankCandidate.id)

            const uploadResponse = await candidateService.uploadInfraCanBank(uploadFormData)
            
            // Check if uploadResponse exists and has a status property
            if (uploadResponse && uploadResponse.status === 200) {
              
              // Refresh candidate documents to get the updated S3 URL
              const documentsResponse = await candidateService.getCandidateDocuments(this.selectedBankCandidate.id)
              if (documentsResponse && documentsResponse.status === 200 && documentsResponse.data?.data?.documents) {
                // Update documents in local state
                const candidateIndex = this.candidates.findIndex(c => c.id === this.selectedBankCandidate.id)
                if (candidateIndex !== -1) {
                  this.candidates[candidateIndex].documents = documentsResponse.data.data.documents
                }
                
                // Update documents in pool candidates list
                const poolCandidateIndex = this.reportsCandidates.findIndex(c => c.id === this.selectedBankCandidate.id)
                if (poolCandidateIndex !== -1) {
                  this.reportsCandidates[poolCandidateIndex].documents = documentsResponse.data.data.documents
                }
                
              }
            } else {
              console.error('Bank proof upload failed:', uploadResponse)
              const errorMsg = uploadResponse?.data?.message || uploadResponse?.message || 'Failed to upload bank proof file'
              throw new Error(errorMsg)
            }
          } catch (uploadError) {
            console.error('Error uploading bank proof file:', uploadError)
            
            // Determine error message based on error type
            let errorMessage = 'Bank details saved, but bank proof upload failed. Please try uploading the document again.'
            
            // Check for CORS error
            if (uploadError.message && uploadError.message.includes('CORS')) {
              errorMessage = 'Bank details saved, but bank proof upload failed due to CORS policy. Please contact the administrator.'
            }
            // Check for 413 Request Entity Too Large
            else if (uploadError.response && uploadError.response.status === 413) {
              errorMessage = 'Bank details saved, but bank proof upload failed. File size is too large (max 10MB). Please compress the file or choose a smaller file.'
            }
            // Check for network errors (when response is undefined)
            else if (!uploadError.response && uploadError.message) {
              if (uploadError.message.includes('Network Error') || uploadError.message.includes('ERR_FAILED')) {
                errorMessage = 'Bank details saved, but bank proof upload failed due to network error. Please check your connection and try again.'
              } else {
                errorMessage = `Bank details saved, but bank proof upload failed: ${uploadError.message}`
              }
            }
            // Check for client-side validation error
            else if (uploadError.message && uploadError.message.includes('File size exceeds')) {
              errorMessage = `Bank details saved, but ${uploadError.message}`
            }
            // Check for API error response
            else if (uploadError.response && uploadError.response.data && uploadError.response.data.message) {
              errorMessage = `Bank details saved, but bank proof upload failed: ${uploadError.response.data.message}`
            }
            // Generic error message
            else if (uploadError.message) {
              errorMessage = `Bank details saved, but bank proof upload failed: ${uploadError.message}`
            }
            
            // Don't throw error here - bank details are already saved, just log the upload error
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Warning',
                text: errorMessage,
                icon: 'AlertTriangleIcon',
                variant: 'warning',
              },
            })
          }
        }

        // Update local state in candidates list (only if account details were saved)
        if (needsAnyAccountDetails && this.selectedBankCandidate) {
          const candidateIndex = this.candidates.findIndex(c => c.id === this.selectedBankCandidate.id)
          if (candidateIndex !== -1) {
            // Update metadata fields only if they were missing
            if (needsAccountNumberInMeta || needsIFSCInMeta) {
              if (!this.candidates[candidateIndex].candidateMetadata) {
                this.candidates[candidateIndex].candidateMetadata = {}
              }
              if (!this.candidates[candidateIndex].metadata) {
                this.candidates[candidateIndex].metadata = {}
              }
              
              if (needsAccountNumberInMeta && accountNumberToUse) {
                this.candidates[candidateIndex].candidateMetadata.accountNumber = accountNumberToUse.trim()
                this.candidates[candidateIndex].metadata.accountNumber = accountNumberToUse.trim()
                // Also set for backward compatibility
                this.candidates[candidateIndex].accountNumber = accountNumberToUse.trim()
                this.candidates[candidateIndex].account_number = accountNumberToUse.trim()
              }
              
              if (needsIFSCInMeta && uppercaseIfsc) {
                this.candidates[candidateIndex].candidateMetadata.ifscCode = uppercaseIfsc
                this.candidates[candidateIndex].metadata.ifscCode = uppercaseIfsc
                // Also set for backward compatibility
                this.candidates[candidateIndex].ifscCode = uppercaseIfsc
                this.candidates[candidateIndex].ifsc_code = uppercaseIfsc
                this.candidates[candidateIndex].ifsc = uppercaseIfsc
              }
            }
            
            // Update extra_details fields only if they were missing
            if (needsAccountNumberInExtra || needsIFSCInExtra) {
              if (!this.candidates[candidateIndex].extra_details) {
                this.candidates[candidateIndex].extra_details = {}
              }
              let existingExtraDetails = {}
              try {
                existingExtraDetails = typeof this.candidates[candidateIndex].extra_details === 'string'
                  ? JSON.parse(this.candidates[candidateIndex].extra_details)
                  : this.candidates[candidateIndex].extra_details
              } catch (error) {
                console.error('Error parsing extra_details in local state:', error)
                existingExtraDetails = {}
              }
              
              const updatedExtraDetails = { ...existingExtraDetails }
              if (needsAccountNumberInExtra && accountNumberToUse) {
                updatedExtraDetails.accountNumber = accountNumberToUse.trim()
              }
              if (needsIFSCInExtra && uppercaseIfsc) {
                updatedExtraDetails.ifscCode = uppercaseIfsc
              }
              
              this.candidates[candidateIndex].extra_details = updatedExtraDetails
            }
          }
          
          // Update local state in pool candidates list (reportsCandidates)
          if (this.selectedBankCandidate) {
            const poolCandidateIndex = this.reportsCandidates.findIndex(c => c.id === this.selectedBankCandidate.id)
            if (poolCandidateIndex !== -1) {
              // Update metadata fields only if they were missing
              if (needsAccountNumberInMeta || needsIFSCInMeta) {
                if (!this.reportsCandidates[poolCandidateIndex].candidateMetadata) {
                  this.reportsCandidates[poolCandidateIndex].candidateMetadata = {}
                }
                if (!this.reportsCandidates[poolCandidateIndex].metadata) {
                  this.reportsCandidates[poolCandidateIndex].metadata = {}
                }
                
                if (needsAccountNumberInMeta && accountNumberToUse) {
                  this.reportsCandidates[poolCandidateIndex].candidateMetadata.accountNumber = accountNumberToUse.trim()
                  this.reportsCandidates[poolCandidateIndex].metadata.accountNumber = accountNumberToUse.trim()
                  // Also set for backward compatibility
                  this.reportsCandidates[poolCandidateIndex].accountNumber = accountNumberToUse.trim()
                  this.reportsCandidates[poolCandidateIndex].account_number = accountNumberToUse.trim()
                }
                
                if (needsIFSCInMeta && uppercaseIfsc) {
                  this.reportsCandidates[poolCandidateIndex].candidateMetadata.ifscCode = uppercaseIfsc
                  this.reportsCandidates[poolCandidateIndex].metadata.ifscCode = uppercaseIfsc
                  // Also set for backward compatibility
                  this.reportsCandidates[poolCandidateIndex].ifscCode = uppercaseIfsc
                  this.reportsCandidates[poolCandidateIndex].ifsc_code = uppercaseIfsc
                  this.reportsCandidates[poolCandidateIndex].ifsc = uppercaseIfsc
                }
              }
              
              // Update extra_details fields only if they were missing
              if (needsAccountNumberInExtra || needsIFSCInExtra) {
                if (!this.reportsCandidates[poolCandidateIndex].extra_details) {
                  this.reportsCandidates[poolCandidateIndex].extra_details = {}
                }
                let existingPoolExtraDetails = {}
                try {
                  existingPoolExtraDetails = typeof this.reportsCandidates[poolCandidateIndex].extra_details === 'string'
                    ? JSON.parse(this.reportsCandidates[poolCandidateIndex].extra_details)
                    : this.reportsCandidates[poolCandidateIndex].extra_details
                } catch (error) {
                  console.error('Error parsing extra_details in pool candidates:', error)
                  existingPoolExtraDetails = {}
                }
                
                const updatedPoolExtraDetails = { ...existingPoolExtraDetails }
                if (needsAccountNumberInExtra && accountNumberToUse) {
                  updatedPoolExtraDetails.accountNumber = accountNumberToUse.trim()
                }
                if (needsIFSCInExtra && uppercaseIfsc) {
                  updatedPoolExtraDetails.ifscCode = uppercaseIfsc
                }
                
                this.reportsCandidates[poolCandidateIndex].extra_details = updatedPoolExtraDetails
              }
            }
          }
        }

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            text: 'Bank details saved successfully',
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        })

        this.resetBankDetailsModal()
        this.loadCandidates()
      } catch (error) {
        console.error('Error saving bank details:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Failed to save bank details. Please try again.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },

    extractPdfPayload(source) {
      return offerLetterService.extractPdfPayload(source)
    },

    async pdfPayloadToBlob(payload) {
      return offerLetterService.pdfPayloadToBlob(payload)
    },

    revokeOfferLetterPdfDisplayUrl() {
      if (this._offerLetterPdfObjectUrl) {
        URL.revokeObjectURL(this._offerLetterPdfObjectUrl)
        this._offerLetterPdfObjectUrl = null
      }
    },

    async setOfferLetterPdfDisplay(payload) {
      this.revokeOfferLetterPdfDisplayUrl()
      if (!payload) {
        this.offerLetterPDFBase64 = null
        return
      }
      // Chrome's built-in PDF viewer does not paint data:application/pdf in iframes
      // (blank gray box). Use a short blob: URL. This app has no frame-src CSP.
      this._offerLetterPdfObjectUrl = await offerLetterService.pdfPayloadToObjectUrl(payload)
      this.offerLetterPDFBase64 = this._offerLetterPdfObjectUrl
    },

    async openOfferLetterPdfInNewTab() {
      if (!this.offerLetterPDFBase64) return
      try {
        // Prefer blob: for window.open — large data: URLs hit browser length limits
        const blob = await offerLetterService.pdfPayloadToBlob(this.offerLetterPDFBase64)
        const url = URL.createObjectURL(blob)
        const opened = window.open(url, '_blank')
        if (!opened) {
          URL.revokeObjectURL(url)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Popup blocked',
              text: 'Allow popups to open the offer letter, or use Download PDF.',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          })
          return
        }
        setTimeout(() => URL.revokeObjectURL(url), 60_000)
      } catch (error) {
        console.error('Error opening offer letter PDF:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: error.message || 'Failed to open offer letter PDF',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },

    async downloadOfferLetterPDF() {
      if (!this.canDownloadOffer) {
        this.showPermissionDeniedToast('download offer letters')
        return
      }
      if (!this.generatedOfferLetterData) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Offer letter data not available for download',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return
      }

      try {
        // Get PDF from generation payload or S3 — no HTML generation
        let pdfPayload = null
        let offerLetterFilename = null

        // 1. Inline base64 / URL from generate-pdf (data.base64 or data.s3Location)
        pdfPayload = this.extractPdfPayload(this.generatedOfferLetterData)
        if (pdfPayload) {
          offerLetterFilename = this.generatedOfferLetterData.offerLetterFilename
          // If extract returned a path/key (not bytes/URL), load via API instead
          if (typeof pdfPayload === 'string'
            && /\.pdf$/i.test(pdfPayload.trim())
            && !/^(data:|blob:|https?:|\/\/)/i.test(pdfPayload.trim())
            && pdfPayload.trim().length < 512) {
            offerLetterFilename = pdfPayload.trim()
            pdfPayload = null
          }
        }
        // 2. Try to load from S3 if offerLetterFilename exists
        if (!pdfPayload && this.generatedOfferLetterData.offerLetterFilename) {
          offerLetterFilename = this.generatedOfferLetterData.offerLetterFilename
          const s3Key = offerLetterService.resolveOfferLetterS3Key(offerLetterFilename)
          const pdfResult = await jobService.getFileBase64(s3Key)
          pdfPayload = this.extractPdfPayload(pdfResult)
        }
        // 3. Try to load from candidate extra_details if available
        if (!pdfPayload && this.generatedOfferLetterData.candidate
          && this.generatedOfferLetterData.candidate.extra_details) {
          try {
            const extraDetails = typeof this.generatedOfferLetterData.candidate.extra_details === 'string'
              ? JSON.parse(this.generatedOfferLetterData.candidate.extra_details)
              : this.generatedOfferLetterData.candidate.extra_details

            if (extraDetails && (extraDetails.offerLetterFilename || extraDetails.offerLetterLocation)) {
              offerLetterFilename = extraDetails.offerLetterFilename || offerLetterFilename
              if (extraDetails.offerLetterLocation
                && /^(https?:|\/\/)/i.test(String(extraDetails.offerLetterLocation).trim())) {
                pdfPayload = extraDetails.offerLetterLocation
              } else if (extraDetails.offerLetterFilename) {
                const s3Key = offerLetterService.resolveOfferLetterS3Key(extraDetails.offerLetterFilename)
                const pdfResult = await jobService.getFileBase64(s3Key)
                pdfPayload = this.extractPdfPayload(pdfResult)
              }
            }
          } catch (error) {
            console.error('Error loading PDF from extra_details:', error)
          }
        }

        if (!pdfPayload) {
          throw new Error('Offer letter PDF not found in S3. Please ensure the offer letter was generated.')
        }

        const blob = await this.pdfPayloadToBlob(pdfPayload)
        const url = window.URL.createObjectURL(blob)

        const link = document.createElement('a')
        link.href = url
        const downloadName = (offerLetterFilename && String(offerLetterFilename).split('/').pop())
          || `Offer_Letter_${this.generatedOfferLetterData.candidate.candidate_name || this.generatedOfferLetterData.candidate.id}.pdf`
        link.setAttribute('download', downloadName)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        window.URL.revokeObjectURL(url)

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            text: 'Offer letter PDF downloaded successfully from S3',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        console.error('Error downloading offer letter PDF:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: error.message || 'Failed to download offer letter PDF from S3',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },

    async previewGeneratedOfferLetter() {
      if (!this.canViewOffer) {
        this.showPermissionDeniedToast('view offer letters')
        return
      }
      if (!this.generatedOfferLetterData) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Offer letter data not available for preview',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return
      }

      // Clear HTML - we only use PDF from S3
      this.generatedOfferLetterHTML = null
      this.revokeOfferLetterPdfDisplayUrl()
      this.offerLetterPDFBase64 = null
      
      // Try to load PDF from S3
      let offerLetterFilename = null
      let pdfPayload = null
      
      // 1. Use PDF from generation if available (just generated)
      if (this.generatedOfferLetterData.pdfBase64) {
        pdfPayload = this.generatedOfferLetterData.pdfBase64
        offerLetterFilename = this.generatedOfferLetterData.offerLetterFilename
      }
      // 2. Try to load from S3 using offerLetterFilename from generatedOfferLetterData
      else if (this.generatedOfferLetterData.offerLetterFilename) {
        offerLetterFilename = this.generatedOfferLetterData.offerLetterFilename
        try {
          const s3Key = offerLetterService.resolveOfferLetterS3Key(offerLetterFilename)
          const pdfResult = await jobService.getFileBase64(s3Key)
          pdfPayload = this.extractPdfPayload(pdfResult)
        } catch (error) {
          console.error('Error loading PDF from S3:', error)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error',
              text: 'Failed to load offer letter PDF from S3',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
          return
        }
      }
      // 3. Try to load from candidate extra_details if available
      else if (this.generatedOfferLetterData.candidate && this.generatedOfferLetterData.candidate.extra_details) {
        try {
          const extraDetails = typeof this.generatedOfferLetterData.candidate.extra_details === 'string'
            ? JSON.parse(this.generatedOfferLetterData.candidate.extra_details)
            : this.generatedOfferLetterData.candidate.extra_details
          
          if (extraDetails && extraDetails.offerLetterFilename) {
            offerLetterFilename = extraDetails.offerLetterFilename
            const s3Key = offerLetterService.resolveOfferLetterS3Key(extraDetails.offerLetterFilename)
            const pdfResult = await jobService.getFileBase64(s3Key)
            pdfPayload = this.extractPdfPayload(pdfResult)
          } else {
            throw new Error('offerLetterFilename not found in extra_details')
          }
        } catch (error) {
          console.error('Error loading PDF from extra_details:', error)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error',
              text: 'Failed to load offer letter PDF from S3. Please ensure the offer letter was generated.',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
          return
        }
      } else {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Offer letter PDF not found. Please generate the offer letter first.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return
      }

      try {
        await this.setOfferLetterPdfDisplay(pdfPayload)
      } catch (error) {
        console.error('Error preparing offer letter PDF preview:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: error.message || 'Failed to load offer letter PDF',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return
      }

      if (!this.offerLetterPDFBase64) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Failed to load offer letter PDF from S3',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return
      }

      this.selectedOfferLetter = {
        id: this.generatedOfferLetterData.candidate.id,
        candidate_name: this.generatedOfferLetterData.candidate.candidate_name,
        offerDetails: this.generatedOfferLetterData.offerDetails,
        offerLetterFilename: offerLetterFilename
      }
      
      // Close post-generation modal and open preview modal
      this.showPostGenerationModal = false
      this.showViewOfferLetterModal = true
    },

    async regenerateOfferLetter() {
      if (!this.canGenerateOfferLetter) {
        this.showPermissionDeniedToast('regenerate offer letters')
        return
      }
      if (!this.generatedOfferLetterData) {
        return
      }

      // Get the candidate and offer details from generated data
      const candidate = this.generatedOfferLetterData.candidate
      const offerDetails = this.generatedOfferLetterData.offerDetails

      // Pre-fill the form with the existing offer details
      this.selectedCandidate = candidate
      this.offerLetterForm.candidateName = offerDetails.candidateName || candidate.candidate_name
      this.offerLetterForm.emailId = offerDetails.emailId || candidate.email
      this.offerLetterForm.joiningDate = offerDetails.joiningDate || ''
      this.offerLetterForm.offeredDate = offerDetails.offeredDate || new Date().toISOString().split('T')[0]
      this.offerLetterForm.site = offerDetails.site || 'assam'
      this.offerLetterForm.language = 'en'
      this.offerLetterForm.location = offerDetails.location || ''
      this.offerLetterForm.reportingManagerName = offerDetails.reportingManagerName || ''
      this.offerLetterForm.jobTitle = offerDetails.jobTitle || ''

      // Pre-fill reporting manager name from extra_details if available
      if (candidate.extra_details) {
        try {
          const extraDetails = typeof candidate.extra_details === 'string' 
            ? JSON.parse(candidate.extra_details) 
            : candidate.extra_details
          if (extraDetails && extraDetails.reportingManagerName && !this.offerLetterForm.reportingManagerName) {
            this.offerLetterForm.reportingManagerName = extraDetails.reportingManagerName
          }
        } catch (error) {
          console.error('Error parsing extra_details for reporting manager:', error)
        }
      }

      // Fetch job titles for the selected site
      if (this.offerLetterForm.site) {
        await this.fetchJobTitleOptions(this.offerLetterForm.site)
      }

      // Close post-generation modal and open offer letter generation modal
      this.showPostGenerationModal = false
      this.showOfferLetterModal = true
      
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Form Pre-filled',
          text: 'Offer letter form has been pre-filled with previous values. You can modify and regenerate.',
          icon: 'InfoIcon',
          variant: 'info',
        },
      })
    },

    async regenerateOfferLetterFromActions(candidate) {
      if (!this.canGenerateOfferLetter) {
        this.showPermissionDeniedToast('regenerate offer letters')
        return
      }
      // Get offer letter data from extra_details or offerDetails
      const offerDetails = this.getOfferLetterData(candidate)
      
      if (!offerDetails) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'No offer letter data found to regenerate',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return
      }

      // Pre-fill the form with the existing offer details
      this.selectedCandidate = candidate
      this.offerLetterForm.candidateName = offerDetails.candidateName || candidate.candidate_name
      this.offerLetterForm.emailId = offerDetails.emailId || candidate.email
      this.offerLetterForm.joiningDate = offerDetails.joiningDate || ''
      this.offerLetterForm.offeredDate = offerDetails.offeredDate || new Date().toISOString().split('T')[0]
      this.offerLetterForm.site = offerDetails.site || 'assam'
      this.offerLetterForm.language = 'en'
      this.offerLetterForm.location = offerDetails.location || ''
      this.offerLetterForm.reportingManagerName = offerDetails.reportingManagerName || ''
      this.offerLetterForm.jobTitle = offerDetails.jobTitle || ''

      // Pre-fill reporting manager name from extra_details if available
      if (candidate.extra_details) {
        try {
          const extraDetails = typeof candidate.extra_details === 'string' 
            ? JSON.parse(candidate.extra_details) 
            : candidate.extra_details
          if (extraDetails && extraDetails.reportingManagerName && !this.offerLetterForm.reportingManagerName) {
            this.offerLetterForm.reportingManagerName = extraDetails.reportingManagerName
          }
        } catch (error) {
          console.error('Error parsing extra_details for reporting manager:', error)
        }
      }

      // Fetch job titles for the selected site
      if (this.offerLetterForm.site) {
        await this.fetchJobTitleOptions(this.offerLetterForm.site)
      }

      // Open offer letter generation modal
      this.showOfferLetterModal = true
      
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Form Pre-filled',
          text: 'Offer letter form has been pre-filled with previous values. You can modify and regenerate.',
          icon: 'InfoIcon',
          variant: 'info',
        },
      })
    },

    openSendEmailModal(candidate) {
      if (!this.canSendOfferLetterEmail) {
        this.showPermissionDeniedToast('send offer letters')
        return
      }
      // Store candidate for email modal
      this.currentEmailCandidate = candidate
      
      // Get offer letter data
      const offerDetails = this.getOfferLetterData(candidate)
      
      if (!offerDetails) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'No offer letter data found. Please generate the offer letter first.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return
      }

      // Pre-fill email form with candidate email
      this.emailForm.to = offerDetails.emailId || candidate.email || ''
      
      // Open modal - template will be loaded in @shown handler
      this.showSendEmailModal = true
    },

    openSendEmailModalFromPostGeneration() {
      if (!this.generatedOfferLetterData || !this.generatedOfferLetterData.candidate) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'No candidate data available',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return
      }
      
      this.openSendEmailModal(this.generatedOfferLetterData.candidate)
    },

    async sendOfferLetterEmailFromActions(candidate) {
      if (!this.canSendOfferLetterEmail) {
        this.showPermissionDeniedToast('send offer letters')
        return
      }
      this.openSendEmailModal(candidate)
    },

    loadEmailTemplate() {
      if (!this.currentEmailCandidate) {
        return
      }

      try {
        // Get offer letter data from extra_details
        const offerDetails = this.getOfferLetterData(this.currentEmailCandidate)
        if (!offerDetails) {
          return
        }

        // Extract variables from extra_details.offerDetails
        const candidateName = offerDetails.candidateName || this.currentEmailCandidate.candidate_name || 'Candidate'
        const jobTitle = this.currentEmailCandidate.position_name || offerDetails.positionName || 'Position'
        const location = offerDetails.location || 'Location'
        const reportingManager = offerDetails.reportingManagerName || 'N/A'
        const offerDate = offerDetails.offeredDate ? this.formatDate(offerDetails.offeredDate) : 'N/A'
        const joiningDate = offerDetails.joiningDate ? this.formatDate(offerDetails.joiningDate) : 'N/A'

        // Get recruiter email for BCC if available
        this.getRecruiterEmail(this.currentEmailCandidate).then(recruiterEmail => {
          if (recruiterEmail && !this.emailForm.bcc) {
            this.emailForm.bcc = recruiterEmail
          }
        }).catch(() => {
          // Ignore errors
        })

        // Load template from email-templates service
        const templateData = emailTemplates.offerLetterEmail({
          can_email: this.emailForm.to,
          to: this.emailForm.to,
          cc: this.emailForm.cc,
          bcc: this.emailForm.bcc,
          candidateName,
          jobTitle,
          location,
          reportingManager,
          offerDate,
          joiningDate
        })

        // Populate form with template data
        if (templateData.subject && !this.emailForm.subject) {
          this.emailForm.subject = templateData.subject
        }
        if (templateData.html) {
          // Replace placeholders in HTML template
          let htmlBody = templateData.html
          htmlBody = htmlBody.replace(/\{\{name\}\}/g, candidateName)
          htmlBody = htmlBody.replace(/\{\{location\}\}/g, location)
          
          // Set HTML content in quill editor - use setTimeout to ensure editor is fully initialized
          setTimeout(() => {
            this.emailForm.body = htmlBody
            // Also set directly to quill editor if ref is available
            if (this.$refs.quillEditor && this.$refs.quillEditor.quill) {
              const quill = this.$refs.quillEditor.quill
              const delta = quill.clipboard.convert(htmlBody)
              quill.setContents(delta, 'silent')
            }
          }, 300)
        }
      } catch (error) {
        console.error('Error loading email template:', error)
      }
    },

    async sendComposedEmail() {
      if (!this.canSendOfferLetterEmail) {
        this.showPermissionDeniedToast('send offer letters')
        return
      }
      // Validate form
      this.emailFormErrors = { subject: '', body: '' }
      
      if (!this.emailForm.to || !this.emailForm.to.trim()) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validation Error',
            text: 'To email is required',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return
      }

      if (!this.emailForm.subject || !this.emailForm.subject.trim()) {
        this.emailFormErrors.subject = 'Subject is required'
        return
      }
      
      if (!this.emailForm.body || !this.emailForm.body.trim()) {
        this.emailFormErrors.body = 'Email body is required'
        return
      }

      if (!this.currentEmailCandidate) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'No candidate data available',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return
      }

      this.isSendingEmail = true

      try {
        const candidate = this.currentEmailCandidate
        const offerDetails = this.getOfferLetterData(candidate)
        
        if (!offerDetails) {
          throw new Error('No offer letter data found')
        }

        const extraDetails = offerLetterService.parseExtraDetails(candidate.extra_details) || {}
        const generated = this.generatedOfferLetterData
        const generatedMatches = !!(generated && generated.candidate
          && String(generated.candidate.id) === String(candidate.id)
          && (!generated.candidate.job_id || !candidate.job_id
            || String(generated.candidate.job_id) === String(candidate.job_id)))

        let attachmentKey = ''
        if (this.emailForm.attachOfferLetter !== false) {
          const sessionKey = generatedMatches ? this.offerLetterFilename : null
          const generatedKey = generatedMatches
            ? (generated.generatedS3Key || generated.s3Key || generated.offerLetterFilename)
            : null
          attachmentKey = await offerLetterService.resolveVerifiedOfferLetterS3Key({
            candidate,
            extraDetails,
            sessionFilename: sessionKey && !offerLetterService.isForbiddenOfferLetterMailKey(sessionKey)
              ? sessionKey
              : null,
            generatedFilename: generatedKey && !offerLetterService.isForbiddenOfferLetterMailKey(generatedKey)
              ? generatedKey
              : null,
            getFileBase64: key => jobService.getFileBase64(key),
            fetchExtraDetails: async () => {
              const res = await candidateService.getCandidateCurrentStatus(
                candidate.id,
                candidate.job_id,
              )
              if (!res || res.status === 404 || res.status === 400 || res.status === 500) return null
              const row = res.data && typeof res.data === 'object' && !Array.isArray(res.data)
                ? res.data
                : res
              return offerLetterService.parseExtraDetails(row?.extra_details)
            },
          })
          if (!attachmentKey || offerLetterService.isForbiddenOfferLetterMailKey(attachmentKey)) {
            throw new Error(
              'Offer letter PDF was never uploaded to storage, so it cannot be attached. Generate the offer letter again, then send.',
            )
          }
          this.offerLetterFilename = attachmentKey
        }

        if (attachmentKey && offerLetterService.isForbiddenOfferLetterMailKey(attachmentKey)) {
          throw new Error(
            'Offer letter PDF was never uploaded to storage, so it cannot be attached. Generate the offer letter again, then send.',
          )
        }

        const mailFilename = attachmentKey
          ? (offerLetterService.toMailHrCompensatingKey(attachmentKey) || attachmentKey)
          : ''
        const mailData = emailTemplates.offerLetterEmail({
          can_email: this.emailForm.to,
          to: this.emailForm.to,
          cc: this.emailForm.cc || '',
          bcc: this.emailForm.bcc || '',
          subject: this.emailForm.subject,
          body: this.emailForm.body,
          candidateName: offerDetails.candidateName || candidate.candidate_name,
          jobTitle: candidate.position_name || offerDetails.positionName || 'Position',
          location: offerDetails.location || 'Location',
          reportingManager: offerDetails.reportingManagerName || 'N/A',
          offerDate: offerDetails.offeredDate ? this.formatDate(offerDetails.offeredDate) : 'N/A',
          joiningDate: offerDetails.joiningDate ? this.formatDate(offerDetails.joiningDate) : 'N/A',
          filename: mailFilename
        })
        const emailResponse = await mailService.mailHR(mailData)
        if (emailResponse && (emailResponse.status === 'OK' || emailResponse.status === 200 || emailResponse.statusCode === 200)) {
          // Update offer letter status to "Sent"
          const userData = JSON.parse(localStorage.getItem('userData'))
          const username = `${userData.first_name} ${userData.last_name}`
          const user = `[{"title" : "${username}", "emp_id": "${userData.emp_id}"}]`
          
          // Update extra_details with email sent status
          try {
            let existingExtraDetails = {}
            if (candidate.extra_details) {
              try {
                existingExtraDetails = typeof candidate.extra_details === 'string' 
                  ? JSON.parse(candidate.extra_details) 
                  : candidate.extra_details
              } catch (error) {
                console.error('Error parsing extra_details:', error)
                existingExtraDetails = {}
              }
            }

            // Update offer letter email sent status
            if (!existingExtraDetails.offerLetter) {
              existingExtraDetails.offerLetter = {}
            }
            existingExtraDetails.offerLetter.emailSent = true
            existingExtraDetails.offerLetter.sentAt = new Date().toISOString()
            existingExtraDetails.offerLetter.sentBy = username
            if (attachmentKey && !offerLetterService.isForbiddenOfferLetterMailKey(attachmentKey)) {
              existingExtraDetails.offerLetterFilename = attachmentKey
            }

            const statusUpdateData = {
              candidateStatus: candidate.candidateStatus || 'Offered',
              extra_details: existingExtraDetails,
              updatedBy: user
            }
            
            await candidateService.updateCandidateStatus(
              candidate.id,
              candidate.job_id,
              statusUpdateData
            )

            // Update local state
            const candidateIndex = this.candidates.findIndex(c => c.id === candidate.id)
            if (candidateIndex !== -1) {
              this.candidates[candidateIndex].extra_details = existingExtraDetails
            }
            
            const poolCandidateIndex = this.reportsCandidates.findIndex(c => c.id === candidate.id)
            if (poolCandidateIndex !== -1) {
              this.reportsCandidates[poolCandidateIndex].extra_details = existingExtraDetails
            }
          } catch (updateError) {
            console.error('Error updating email sent status:', updateError)
            // Continue even if update fails
          }

          // Create timeline entry
          const timelineData = {
            title: `Offer Letter Sent via Email to Candidate Id - ${candidate.id}`,
            description: `CS-Offer Letter Sent via Email to ${this.emailForm.to}`,
            type: "success",
            createdBy: username,
            jobId: candidate.job_id,
            candidate_id: candidate.id
          }
          
          await jobTimelineService.create(timelineData)

          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              text: `Offer letter sent successfully to ${this.emailForm.to}`,
              icon: 'MailIcon',
              variant: 'success',
            },
          })

          // Non-blocking: ask recruiter to update candidate status in ATS
          await this.notifyRecruiterOfferLetterShared(candidate)

          // Close modal and update counts
          this.resetEmailModal()
          this.updateSummaryCounts()
        } else {
          throw new Error(emailResponse?.message || emailResponse?.data?.message || 'Failed to send email')
        }
      } catch (error) {
        console.error('Error sending email:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: error.message || 'Failed to send email. Please try again.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.isSendingEmail = false
      }
    },

    resetEmailModal() {
      this.showSendEmailModal = false
      this.emailForm = {
        to: '',
        cc: '',
        bcc: '',
        subject: '',
        body: '',
        attachOfferLetter: true
      }
      this.emailFormErrors = {
        subject: '',
        body: ''
      }
      this.isSendingEmail = false
      this.currentEmailCandidate = null
    },

    async sendEmailFromPostGeneration() {
      if (!this.confirmSendEmail || !this.generatedOfferLetterData) {
        return
      }

      try {
        const { candidate, offerDetails } = this.generatedOfferLetterData
        
        await this.sendOfferLetterEmail(
          candidate,
          offerDetails,
          offerDetails.emailId
        )

        // Update timeline entry
        const userData = JSON.parse(localStorage.getItem("userData"))
        const username = `${userData.first_name} ${userData.last_name}`
        const user = `[{"title" : "${username}", "emp_id": "${userData.emp_id}"}]`
        
        const timelineData = {
          title: `Professional Offer Letter Sent via Email to Candidate Id - ${candidate.id}`,
          description: `CS-Professional Offer Letter Sent via Email via Intellismart Dashboard`,
          type: "success",
          createdBy: username,
          jobId: candidate.job_id,
          candidate_id: candidate.id
        }
        
        await jobTimelineService.create(timelineData)

        // Update offer letter data in extra_details
        try {
          // Get existing extra_details
          let existingExtraDetails = {}
          const candidateExtraDetails = candidate.extra_details || {}
          try {
            existingExtraDetails = typeof candidateExtraDetails === 'string' 
              ? JSON.parse(candidateExtraDetails) 
              : candidateExtraDetails
          } catch (error) {
            console.error('Error parsing extra_details:', error)
            existingExtraDetails = {}
          }
          
          // Update offer letter data with email sent status (preserve version and history)
          const existingOfferLetter = existingExtraDetails.offerLetter || offerDetails
          const updatedOfferLetterData = {
            ...existingOfferLetter,
              emailSent: true,
              sentBy: user,
              sentAt: new Date().toISOString()
            // Version is preserved from existingOfferLetter
          }
          
          const mergedExtraDetails = {
            ...existingExtraDetails,
            offerLetter: updatedOfferLetterData,
            // Preserve offerLetterHistory if it exists
            offerLetterHistory: existingExtraDetails.offerLetterHistory || []
          }
          
          const currentStatus = candidate.candidateStatus || 'Offered'
          const statusUpdateData = {
            candidateStatus: currentStatus,
            extra_details: mergedExtraDetails,
            updatedBy: user
          }
          
          const response = await candidateService.updateCandidateStatus(
            candidate.id,
            candidate.job_id,
            statusUpdateData
          )
          
          if (response && (response.status === 'OK' || response.status === 200 || response.statusCode === 200)) {
            
            // Update local state
            const candidateIndex = this.candidates.findIndex(c => c.id === candidate.id)
            if (candidateIndex !== -1) {
              this.candidates[candidateIndex].extra_details = mergedExtraDetails
            }
            
            const poolCandidateIndex = this.reportsCandidates.findIndex(c => c.id === candidate.id)
            if (poolCandidateIndex !== -1) {
              this.reportsCandidates[poolCandidateIndex].extra_details = mergedExtraDetails
            }
          }
        } catch (extraDetailsError) {
          console.error('Error updating offer letter email status in extra_details:', extraDetailsError)
          // Continue even if update fails
        }

        // Update local offer details with email sent status
        this.generatedOfferLetterData.offerDetails = {
          ...this.generatedOfferLetterData.offerDetails,
          emailSent: true,
          sentBy: user,
          sentAt: new Date().toISOString()
        }

        // Mark email as sent
        this.emailSent = true
        
        // Don't close modal, just show success and disable send button
        this.confirmSendEmail = false
      } catch (error) {
        console.error('Error sending email from post-generation modal:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Failed to send email. Please try again.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },

    resetViewOfferLetterModal() {
      this.showViewOfferLetterModal = false
      this.selectedOfferLetter = null
      this.generatedOfferLetterHTML = ''
      this.revokeOfferLetterPdfDisplayUrl()
      this.offerLetterPDFBase64 = null
    },

    resetViewOfferLetter() {
      this.showViewOfferLetterModal = false
      this.selectedOfferLetter = null
      this.generatedOfferLetterHTML = ''
      this.revokeOfferLetterPdfDisplayUrl()
      this.offerLetterPDFBase64 = null
    },

    resetPreviewOfferLetterAndDocuments() {
      this.showPreviewOfferLetterAndDocumentsModal = false
      this.selectedOfferLetter = null
      this.generatedOfferLetterHTML = ''
      this.revokeOfferLetterPdfDisplayUrl()
      this.offerLetterPDFBase64 = null
    },

    async previewOfferLetterAndDocuments(candidate) {
      try {
        this.selectedOfferLetter = candidate
        this.showPreviewOfferLetterAndDocumentsModal = true
        
        // Clear HTML - we only use PDF from S3
        this.generatedOfferLetterHTML = null
        this.revokeOfferLetterPdfDisplayUrl()
        this.offerLetterPDFBase64 = null
        
        // Get PDF from S3 using offerLetterFilename from extra_details
        let offerLetterFilename = null
        
        if (candidate.extra_details) {
          try {
            const extraDetails = typeof candidate.extra_details === 'string'
              ? JSON.parse(candidate.extra_details)
              : candidate.extra_details
            
            if (extraDetails && extraDetails.offerLetterFilename) {
              offerLetterFilename = extraDetails.offerLetterFilename
              
              const s3Key = offerLetterService.resolveOfferLetterS3Key(offerLetterFilename)
              const pdfResult = await jobService.getFileBase64(s3Key)
              const pdfPayload = this.extractPdfPayload(pdfResult)
              if (pdfPayload) {
                await this.setOfferLetterPdfDisplay(pdfPayload)
              } else {
                throw new Error('PDF file not found in S3')
              }
            } else {
              throw new Error('offerLetterFilename not found in extra_details')
            }
          } catch (error) {
            console.error('Error loading PDF from S3:', error)
            throw new Error('Failed to load offer letter PDF from S3. Please ensure the offer letter was generated.')
          }
        } else {
          throw new Error('extra_details not found. Please ensure the offer letter was generated.')
        }
        
        if (!this.offerLetterPDFBase64) {
          throw new Error('Failed to load offer letter PDF from S3')
        }
      } catch (error) {
        console.error('Error previewing offer letter and documents:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: error.message || 'Failed to load offer letter PDF from S3',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        // Close modal on error
        this.showPreviewOfferLetterAndDocumentsModal = false
      }
    },

    viewCandidateDetails(candidate) {
      // Navigate to candidate details page
      // Route requires both id and jobId params
      this.$router.push({
        name: 'apps-candidates-view',
        params: { 
          id: candidate.id,
          jobId: candidate.job_id || candidate.jid || 'null'
        },
        query: {
          position_name: (() => {
            // Extract job title from position_name (e.g., "SHJ-1222 - ITI Electrician" -> "ITI Electrician")
            const positionName = candidate.position_name || ''
            if (!positionName) return ''
            const parts = positionName.split(' - ')
            return parts.length > 1 ? parts.slice(1).join(' - ').trim() : positionName.trim()
          })(),
          client: candidate.client || selectedClientName,
          recruiter: candidate.assignedTo || '',
          locations: candidate.locations || '',
          position_type: candidate.position_type || '',
          vertical: candidate.vertical || '',
          category: candidate.category || '',
          payroll: candidate.payroll || ''
        }
      })
    },

    getDefaultOfferLetterSite() {
      return this.siteOptions.length > 0 ? this.siteOptions[0].value : ''
    },

    async loadSiteAndLanguageOptions() {
      try {
        // Load site options filtered by current client dashboard
        // (must match offer_table_configs.client — backend uses ILIKE %client%)
        this.siteOptions = await offerLetterService.getSiteOptions(this.selectedClientName)
        
        // Language is always English, no need to load options
      } catch (error) {
        console.error('Error loading site and language options:', error)
        const detail = (error && error.message) || 'Unknown error'
        const clientHint = this.selectedClientName
          ? ` (client filter: ${this.selectedClientName})`
          : ''
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: detail.includes('client:')
              ? detail
              : `Failed to load site options${clientHint}: ${detail}`,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        // Set empty arrays on error - user will need to ensure database is configured
        this.siteOptions = []
        this.languageOptions = []
      }
    },

    getStatusVariant(status) {
      const variants = {
        'Shortlisted': 'warning',
        'Offered': 'info',
        'Offer Generated': 'info',
        'Joined': 'success',
        'NotOffered': 'danger',
        'OfferDrop': 'secondary'
      }
      return variants[status] || 'secondary'
    },

    getStatusBadgeClass(status) {
      const classes = {
        Shortlisted: 'status-badge--shortlisted',
        Offered: 'status-badge--offered',
        'Offer Generated': 'status-badge--offer-generated',
        Joined: 'status-badge--joined',
        NotOffered: 'status-badge--not-offered',
        OfferDrop: 'status-badge--offer-drop',
      }
      return classes[status] || 'status-badge--default'
    },

    getInitials(name) {
      if (!name) return 'N/A'
      return name.split(' ').map(n => n[0]).join('').toUpperCase()
    },

    getOfferStageProgress(candidate) {
      const stages = {
        'Shortlisted': 25,
        'Offered': 50,
        'Offer Accepted': 75,
        'Joined': 100
      }
      return stages[candidate.candidateStatus] || 0
    },

    getOfferStageText(candidate) {
      const stages = {
        'Shortlisted': 'Initial Review',
        'Offered': 'Offer Extended',
        'Offer Accepted': 'Offer Accepted',
        'Joined': 'Successfully Joined'
      }
      return stages[candidate.candidateStatus] || 'Pending'
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      return `${day}-${month}-${year}`
    },

    checkDocumentsAvailable(candidate) {
      // Check if candidate has all required documents (bankProof is optional - can check account number/IFSC instead)
      if (candidate.documents) {
        const requiredDocs = ['pan'] // bankProof is optional
        const hasRequiredDocs = requiredDocs.every(doc => 
          candidate.documents[doc] && 
          candidate.documents[doc].trim() !== '' &&
          candidate.documents[doc] !== null
        )
        
        // Check Aadhaar: either legacy single file OR new front/back
        const hasAadhaar = this.hasAadhaarDocument(candidate)
        
        return hasRequiredDocs && hasAadhaar
      }
      
      // Fallback: if no documents field, assume not available
      return false
    },
    

    hasAadhaarDocument(candidate) {
      if (!candidate || !candidate.documents) return false
      
      // New format: both front and back required
      if (candidate.documents.aadhaar_front || candidate.documents.aadhaar_back) {
        return true
      }
      
      // Legacy format: single file (treat as front, back optional)
      if (candidate.documents.aadhaar) {
        return true
      }
      
      return false
    },

    isBankAccountDetailsAvailable(candidate) {
      if (!candidate) return false
      
      // Check if account number and IFSC are available in either metadata or extra_details
      const accountNumberInMetadata = this.getAccountNumberFromMetadata(candidate)
      const ifscCodeInMetadata = this.getIFSCCodeFromMetadata(candidate)
      const accountNumberInExtraDetails = this.getAccountNumberFromExtraDetails(candidate)
      const ifscCodeInExtraDetails = this.getIFSCCodeFromExtraDetails(candidate)
      
      // Check if both account number and IFSC exist in at least one location
      const hasInMetadata = accountNumberInMetadata && 
                           accountNumberInMetadata.toString().trim() !== '' && 
                           ifscCodeInMetadata && 
                           ifscCodeInMetadata.toString().trim() !== ''
      
      const hasInExtraDetails = accountNumberInExtraDetails && 
                                accountNumberInExtraDetails.toString().trim() !== '' && 
                                ifscCodeInExtraDetails && 
                                ifscCodeInExtraDetails.toString().trim() !== ''
      
      return hasInMetadata || hasInExtraDetails
    },

    // Helper methods to get account details from metadata
    getAccountNumberFromMetadata(candidate) {
      if (!candidate) return null
      return (candidate.candidateMetadata && candidate.candidateMetadata.accountNumber) ||
             candidate.accountNumber || 
             candidate.account_number || 
             (candidate.metadata && candidate.metadata.accountNumber) ||
             (candidate.bankDetails && candidate.bankDetails.accountNumber) ||
             null
    },

    getIFSCCodeFromMetadata(candidate) {
      if (!candidate) return null
      return (candidate.candidateMetadata && candidate.candidateMetadata.ifscCode) ||
             candidate.ifscCode || 
             candidate.ifsc_code || 
             candidate.ifsc ||
             (candidate.metadata && candidate.metadata.ifscCode) ||
             (candidate.bankDetails && candidate.bankDetails.ifscCode) ||
             null
    },

    // Helper methods to get account details from extra_details
    getAccountNumberFromExtraDetails(candidate) {
      if (!candidate) return null
      try {
        const extraDetails = typeof candidate.extra_details === 'string' 
          ? JSON.parse(candidate.extra_details) 
          : candidate.extra_details
        return (extraDetails && extraDetails.accountNumber) || null
      } catch (error) {
        return null
      }
    },

    getIFSCCodeFromExtraDetails(candidate) {
      if (!candidate) return null
      try {
        const extraDetails = typeof candidate.extra_details === 'string' 
          ? JSON.parse(candidate.extra_details) 
          : candidate.extra_details
        return (extraDetails && extraDetails.ifscCode) || null
      } catch (error) {
        return null
      }
    },

    // Check which location needs account number
    needsAccountNumberInMetadata(candidate) {
      const accountNumber = this.getAccountNumberFromMetadata(candidate)
      return !accountNumber || accountNumber.toString().trim() === ''
    },

    needsAccountNumberInExtraDetails(candidate) {
      const accountNumber = this.getAccountNumberFromExtraDetails(candidate)
      return !accountNumber || accountNumber.toString().trim() === ''
    },

    // Check which location needs IFSC code
    needsIFSCCodeInMetadata(candidate) {
      const ifscCode = this.getIFSCCodeFromMetadata(candidate)
      return !ifscCode || ifscCode.toString().trim() === ''
    },

    needsIFSCCodeInExtraDetails(candidate) {
      const ifscCode = this.getIFSCCodeFromExtraDetails(candidate)
      return !ifscCode || ifscCode.toString().trim() === ''
    },

    getBankAccountNumber(candidate) {
      if (!candidate) return ''
      // Check metadata first, then extra_details
      const fromMetadata = this.getAccountNumberFromMetadata(candidate)
      if (fromMetadata) return fromMetadata
      return this.getAccountNumberFromExtraDetails(candidate) || ''
    },

    getBankIFSCCode(candidate) {
      if (!candidate) return ''
      // Check metadata first, then extra_details
      const fromMetadata = this.getIFSCCodeFromMetadata(candidate)
      if (fromMetadata) return fromMetadata
      return this.getIFSCCodeFromExtraDetails(candidate) || ''
    },

    async previewOfferLetter() {
      try {
        // Prepare offer details for preview
        const offerDetails = {
          candidateName: this.offerLetterForm.candidateName,
          emailId: this.offerLetterForm.emailId,
          joiningDate: this.offerLetterForm.joiningDate,
          offeredDate: this.offerLetterForm.offeredDate,
          site: this.offerLetterForm.site,
          language: 'en',
          location: this.offerLetterForm.location,
          reportingManagerName: this.offerLetterForm.reportingManagerName
        }
        
        // Generate preview HTML
        this.generatedOfferLetterHTML = await offerLetterService.generateOfferLetterHTML(
          this.selectedCandidate, 
          offerDetails, 
          this.offerLetterForm.site, 
          'en',
          this.offerLetterForm.jobTitle,
        )
        
        // Show preview modal
        this.showViewOfferLetterModal = true
        this.selectedOfferLetter = this.selectedCandidate
        
      } catch (error) {
        console.error('Error previewing offer letter:', error)
        this.$toast({
          title: 'Error',
          text: 'Failed to preview offer letter',
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        })
      }
    },

    // Helper method to check if offer letter exists in extra_details
    hasOfferLetter(candidate) {
      // Check offerDetails first (legacy)
      if (candidate.offerDetails && Object.keys(candidate.offerDetails).length > 0) {
        return true
      }
      
      // Check extra_details for offerLetter or offerLetterFilename
      if (candidate.extra_details) {
        try {
          const extraDetails = typeof candidate.extra_details === 'string' 
            ? JSON.parse(candidate.extra_details) 
            : candidate.extra_details
          
          // Check for offerLetterFilename (indicates PDF was generated and saved to S3)
          if (extraDetails?.offerLetterFilename) {
            return true
          }
          
          // Check for offerLetter object (legacy)
          if (extraDetails?.offerLetter && Object.keys(extraDetails.offerLetter).length > 0) {
            return true
          }
        } catch (error) {
          console.error('Error parsing extra_details in hasOfferLetter:', error)
          return false
        }
      }
      
      return false
    },

    // Helper method to get offer letter data from extra_details
    getOfferLetterData(candidate) {
      // Try offerDetails first (legacy)
      if (candidate.offerDetails && Object.keys(candidate.offerDetails).length > 0) {
        return candidate.offerDetails
      }
      
      // Try extra_details.offerLetter
      if (candidate.extra_details) {
        try {
          const extraDetails = typeof candidate.extra_details === 'string' 
            ? JSON.parse(candidate.extra_details) 
            : candidate.extra_details
          return extraDetails?.offerLetter || null
        } catch (error) {
          console.error('Error parsing extra_details for offer letter:', error)
          return null
        }
      }
      
      return null
    },

    // Helper method to check if email was sent
    isOfferLetterEmailSent(candidate) {
      const offerLetterData = this.getOfferLetterData(candidate)
      if (!offerLetterData) return false
      
      return offerLetterData.emailSent === true || offerLetterData.emailSent === 'true'
    },

    // Times offer letter was generated (extra_details.offerLetter.version or history length + 1)
    getOfferGenerationCount(candidate) {
      if (!this.hasOfferLetter(candidate)) return 0

      const offerData = this.getOfferLetterData(candidate)
      const version = offerData?.version
      if (version != null && Number.isFinite(Number(version)) && Number(version) >= 1) {
        return Number(version)
      }

      if (candidate.extra_details) {
        try {
          const extraDetails = typeof candidate.extra_details === 'string'
            ? JSON.parse(candidate.extra_details)
            : candidate.extra_details
          const historyLen = Array.isArray(extraDetails?.offerLetterHistory)
            ? extraDetails.offerLetterHistory.length
            : 0
          if (historyLen > 0) return historyLen + 1
        } catch (error) {
          console.error('Error parsing extra_details for offer generation count:', error)
        }
      }

      return 1
    },

    getOfferGenerationCountTitle(candidate) {
      const count = this.getOfferGenerationCount(candidate)
      if (count <= 1) return 'Offer letter generated once'
      return `Offer letter generated ${count} times`
    },

    async viewOfferLetter(candidate) {
      if (!this.canViewOffer) {
        this.showPermissionDeniedToast('view offer letters')
        return
      }
      try {
        this.selectedOfferLetter = candidate
        this.showViewOfferLetterModal = true
        
        // Clear HTML - we only use PDF from S3
        this.generatedOfferLetterHTML = null
        this.revokeOfferLetterPdfDisplayUrl()
        this.offerLetterPDFBase64 = null

        // After Regenerate, prefer the bytes just returned — same S3 key can
        // still serve the previous 157-page object from cache.
        const generated = this.generatedOfferLetterData
        const sameGeneratedCandidate = !!(generated && generated.candidate
          && String(generated.candidate.id) === String(candidate.id)
          && (!generated.candidate.job_id || !candidate.job_id
            || String(generated.candidate.job_id) === String(candidate.job_id)))
        if (sameGeneratedCandidate && generated.pdfBase64) {
          await this.setOfferLetterPdfDisplay(generated.pdfBase64)
          if (this.offerLetterPDFBase64) return
        }
        
        // Get PDF from S3 using offerLetterFilename from extra_details
        let offerLetterFilename = null
        
        if (candidate.extra_details) {
          try {
            const extraDetails = typeof candidate.extra_details === 'string'
              ? JSON.parse(candidate.extra_details)
              : candidate.extra_details
            
            if (extraDetails && extraDetails.offerLetterFilename) {
              offerLetterFilename = extraDetails.offerLetterFilename
              
              const s3Key = offerLetterService.resolveOfferLetterS3Key(offerLetterFilename)
              const pdfResult = await jobService.getFileBase64(s3Key)
              const pdfPayload = this.extractPdfPayload(pdfResult)
              if (pdfPayload) {
                await this.setOfferLetterPdfDisplay(pdfPayload)
              } else {
                throw new Error('PDF file not found in S3')
              }
            } else {
              throw new Error('offerLetterFilename not found in extra_details')
            }
          } catch (error) {
            console.error('Error loading PDF from S3:', error)
            throw new Error('Failed to load offer letter PDF from S3. Please ensure the offer letter was generated.')
          }
        } else {
          throw new Error('extra_details not found. Please ensure the offer letter was generated.')
        }
        
        if (!this.offerLetterPDFBase64) {
          throw new Error('Failed to load offer letter PDF from S3')
        }
      } catch (error) {
        console.error('Error viewing offer letter:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
          title: 'Error',
            text: error.message || 'Failed to load offer letter PDF from S3',
          icon: 'AlertTriangleIcon',
          variant: 'danger',
          },
        })
        // Close modal on error
        this.showViewOfferLetterModal = false
      }
    },

    async downloadOfferLetter(candidate) {
      if (!this.canDownloadOffer) {
        this.showPermissionDeniedToast('download offer letters')
        return
      }
      try {
        // Get PDF from S3 only - no HTML generation
        let offerLetterFilename = null
        let pdfBase64 = null
        
        // 1. Check if offerLetterFilename is directly on candidate object (from selectedOfferLetter)
        if (candidate.offerLetterFilename) {
          offerLetterFilename = candidate.offerLetterFilename
        }
        // 2. Check extra_details for offerLetterFilename
        else if (candidate.extra_details) {
          try {
            const extraDetails = typeof candidate.extra_details === 'string'
              ? JSON.parse(candidate.extra_details)
              : candidate.extra_details
            
            if (extraDetails && extraDetails.offerLetterFilename) {
              offerLetterFilename = extraDetails.offerLetterFilename
            }
          } catch (parseError) {
            console.error('Error parsing extra_details:', parseError)
          }
        }
        
        if (!offerLetterFilename) {
          throw new Error('Offer letter filename not found. Please ensure the offer letter was generated.')
        }
        
        const s3Key = offerLetterService.resolveOfferLetterS3Key(offerLetterFilename)
        const pdfResult = await jobService.getFileBase64(s3Key)
        pdfBase64 = this.extractPdfPayload(pdfResult)
        if (!pdfBase64) {
          throw new Error('PDF file not found in S3')
        }

        const blob = await this.pdfPayloadToBlob(pdfBase64)
        const url = window.URL.createObjectURL(blob)

        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', (offerLetterFilename && String(offerLetterFilename).split('/').pop()) || `Offer_Letter_${candidate.candidate_name || candidate.id}.pdf`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        window.URL.revokeObjectURL(url)
          
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
            text: 'Offer letter PDF downloaded successfully from S3',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
      } catch (error) {
        console.error('Error downloading offer letter:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: error.message || 'Failed to download offer letter PDF from S3',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },

    async downloadOfferLetterFromModal() {
      if (!this.selectedCandidate || !this.isOfferLetterFormValid || !this.offerLetterForm.joiningDate) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validation Error',
            text: 'Please fill all required fields before downloading',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      try {
        const offerDetails = {
          candidateName: this.offerLetterForm.candidateName,
          emailId: this.offerLetterForm.emailId,
          joiningDate: this.offerLetterForm.joiningDate,
          offeredDate: this.offerLetterForm.offeredDate,
          site: this.offerLetterForm.site,
          language: 'en',
          location: this.offerLetterForm.location,
          reportingManagerName: this.offerLetterForm.reportingManagerName
        }
        
        const result = await offerLetterService.generateOfferLetterHTMLForDownload(
          this.selectedCandidate, 
          offerDetails, 
          this.offerLetterForm.site, 
          'en',
          this.offerLetterForm.jobTitle
        )
        
        if (result.success && result.html) {
          // Create a blob from the HTML
          const blob = new Blob([result.html], { type: 'text/html' })
          const url = window.URL.createObjectURL(blob)
          
          // Create a temporary link and trigger download
          const link = document.createElement('a')
          link.href = url
          const fileName = `Offer_Letter_${this.offerLetterForm.candidateName || this.selectedCandidate.id}_${new Date().toISOString().split('T')[0]}.html`
          link.setAttribute('download', fileName)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          
          // Clean up the URL
          window.URL.revokeObjectURL(url)
          
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              text: 'Offer letter downloaded successfully',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        } else {
          throw new Error(result.message || 'Failed to generate offer letter')
        }
      } catch (error) {
        console.error('Error downloading offer letter:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Failed to download offer letter',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },

    async sendOfferLetterEmail(candidate, offerDetails, candidateEmail) {
      try {
        // Generate offer letter HTML
        const result = await offerLetterService.generateOfferLetterHTMLForDownload(
          candidate,
          offerDetails,
          offerDetails.site || 'assam',
          offerDetails.language || 'en',
          offerDetails.jobTitle || null
        )

        if (!result.success || !result.html) {
          throw new Error('Failed to generate offer letter for email')
        }

        // Get recruiter email for BCC
        const recruiterEmail = await this.getRecruiterEmail(candidate)
        const bccEmails = recruiterEmail ? [recruiterEmail] : []

        // Prepare email data - simple body without offer letter content
        const emailSubject = `Offer Letter - ${offerDetails.candidateName} - ${candidate.position_name || 'Position'}`
        const emailBody = `
          <html>
          <body style="font-family: Arial, sans-serif; line-height: 1.6;">
            <p>Dear ${offerDetails.candidateName},</p>
            <p><strong>Congratulations!</strong> <p>
            <p>We are pleased to offer you the position. Please find the attached offer letter for your review.</p>
            <p>If you have any questions, please feel free to contact us.</p>
            <br>
            <p>Best regards,<br>
            HR Team<br>
            Sattva Human Private Limited</p>
          </body>
          </html>
        `

        // Prepare FormData for attachment
        const formData = new FormData()
        
        // Add email fields
        formData.append('to', candidateEmail)
        formData.append('fromName', 'HR Team')
        formData.append('fromMail', 'hr@sattvahuman.com')
        formData.append('subject', emailSubject)
        formData.append('html', emailBody)
        if (bccEmails.length > 0) {
          formData.append('bcc', bccEmails.join(','))
        }
        
        // Convert HTML to Blob and attach as file (backend will handle PDF conversion if needed)
        const htmlBlob = new Blob([result.html], { type: 'text/html' })
        const fileName = `Offer_Letter_${offerDetails.candidateName.replace(/\s+/g, '_')}.html`
        formData.append('file', htmlBlob, fileName)
        formData.append('attachmentName', fileName.replace('.html', '.pdf'))

        // Send email with attachment using mailService
        const emailResponse = await mailService.mailWithAttachment(formData)
        
        if (emailResponse && emailResponse.status === 200) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              text: `Offer letter sent successfully to ${candidateEmail}`,
              icon: 'MailIcon',
              variant: 'success',
            },
          })
          // Non-blocking: ask recruiter to update candidate status in ATS
          await this.notifyRecruiterOfferLetterShared(candidate)
          return { success: true }
        } else {
          throw new Error(emailResponse?.data?.message || 'Failed to send email')
        }
      } catch (error) {
        console.error('Error sending offer letter email:', error)
        throw error
      }
    },

    downloadCandidateResume(candidate) {
      // Navigate to candidate details page where resume can be downloaded
      this.$router.push({
        name: 'candidates-view',
        params: { id: candidate.id }
      })
    },

    clearStatusFilter() {
      this.filters.status = []
      this.loadCandidates()
    },

    clearRecruiterFilter() {
      this.filters.recruiter = ''
      this.loadCandidates()
    },

    getRecruiterName(recruiterValue) {
      if (!recruiterValue) {
        return 'Unassigned'
      }
      
      // If it's already a name (not an ID), return as is
      if (!recruiterValue.startsWith('SH-')) {
        return recruiterValue
      }
      
      // If it's an ID, try to find the corresponding name in recruiterOptions first
      const recruiterFromOptions = this.recruiterOptions.find(r => r.value === recruiterValue)
      if (recruiterFromOptions) {
        return recruiterFromOptions.text
      }
      
      // If not found in options, try to find in recruitersList (loaded from API)
      if (this.recruitersList && this.recruitersList.length > 0) {
        const recruiterFromList = this.recruitersList.find(r => r.emp_id === recruiterValue)
        if (recruiterFromList) {
          // Return title or name from recruiter object
          return recruiterFromList.title || recruiterFromList.name || recruiterFromList.first_name + ' ' + recruiterFromList.last_name || recruiterValue
        }
      }
      
      // If still not found, return the ID as fallback
      return recruiterValue
    },

    getInitials(name) {
      if (!name) return '??'
      const words = name.trim().split(' ')
      if (words.length >= 2) {
        return (words[0][0] + words[1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    },

    async loadRecruitersList() {
      try {
        const response = await userService.getAllRecruitersList()
        if (response && response.data && Array.isArray(response.data)) {
          this.recruitersList = response.data
        } else if (response && Array.isArray(response)) {
          this.recruitersList = response
        }
      } catch (error) {
        console.error('Error loading recruiters list:', error)
        // Continue without recruiters list - BCC will be empty
      }
    },

    async getRecruiterEmail(candidate) {
      try {
        const recruiterId = candidate.rec_id || candidate.recruiter_id

        if (!this.recruitersList || this.recruitersList.length === 0) {
          await this.loadRecruitersList()
        }

        if (recruiterId) {
          const recruiter = this.recruitersList.find(r => r.emp_id === recruiterId)
          if (recruiter && recruiter.email) {
            return recruiter.email
          }
        }

        // Fallback: match by display name when rec_id is missing
        const recruiterName = (candidate.assignedTo || candidate.recruiter || '').trim().toLowerCase()
        if (recruiterName && this.recruitersList && this.recruitersList.length > 0) {
          const byName = this.recruitersList.find(r => {
            const full = `${r.first_name || ''} ${r.last_name || ''}`.trim().toLowerCase()
            const title = (r.title || r.name || '').trim().toLowerCase()
            return full === recruiterName || title === recruiterName
          })
          if (byName && byName.email) {
            return byName.email
          }
        }

        return null
      } catch (error) {
        console.error('Error getting recruiter email:', error)
        return null
      }
    },

    buildCandidateProfileLink(candidate) {
      if (!candidate || !candidate.id) return null
      const jobId = candidate.job_id || candidate.jid || 'null'
      // Hash mode router — absolute URL for email clients
      return `${window.location.origin}/#/apps/candidates/view/${candidate.id}/${jobId}`
    },

    /**
     * After offer letter is sent to the candidate, notify the recruiter to
     * update ATS status. Never throws; soft-warns on failure; skips if no email.
     */
    async notifyRecruiterOfferLetterShared(candidate) {
      try {
        if (!candidate) return

        const recruiterEmail = await this.getRecruiterEmail(candidate)
        if (!recruiterEmail) {
          return
        }

        const resolvedName = this.getRecruiterName(
          candidate.assignedTo || candidate.recruiter || candidate.rec_id
        )
        const recruiterName = resolvedName && resolvedName !== 'Unassigned'
          ? resolvedName
          : 'Recruiter'
        const profileLink = this.buildCandidateProfileLink(candidate)

        const mailData = emailTemplates.offerLetterSharedRecruiterMail({
          rec_email: recruiterEmail,
          rec_name: recruiterName,
          can_id: candidate.id,
          can_fname: candidate.candidate_name || 'Candidate',
          position_name: candidate.position_name || 'Position',
          client: candidate.client || this.selectedClientName || '',
          candidateViewLink: profileLink,
        })

        const emailResponse = await mailService.mailHR(mailData)
        if (!(emailResponse && (emailResponse.status === 'OK' || emailResponse.status === 200 || emailResponse.statusCode === 200))) {
          throw new Error(emailResponse?.message || emailResponse?.data?.message || 'Failed to notify recruiter')
        }
      } catch (error) {
        console.error('Error notifying recruiter of offer letter shared:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Warning',
            text: 'Offer letter was sent to the candidate, but the recruiter notification email could not be sent.',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
      }
    },

    // Pool-specific methods
    onPoolRowClicked(item) {
      // Handle pool row click if needed
    },

    onPoolSelectionChange(candidate) {
      if (candidate.selected) {
        if (!this.selectedPoolCandidates.find(c => c.id === candidate.id)) {
          this.selectedPoolCandidates.push(candidate)
        }
      } else {
        this.selectedPoolCandidates = this.selectedPoolCandidates.filter(c => c.id !== candidate.id)
      }
    },

    clearPoolSelection() {
      this.selectedPoolCandidates = []
      this.reportsCandidates.forEach(candidate => {
        candidate.selected = false
      })
      this.reportsTableKey++
    },

    openPoolBulkAssignmentModal() {
      if (!this.canAssignCandidates) {
        this.showPermissionDeniedToast('assign candidates')
        return
      }
      this.showPoolBulkModal = true
    },

    resetPoolBulkAssignmentForm() {
      this.poolBulkAssignmentForm = {
        poc: null
      }
    },

    async bulkAssignPoolCandidates() {
      if (!this.canAssignCandidates) {
        this.showPermissionDeniedToast('assign candidates')
        return
      }
      if (!this.poolBulkAssignmentForm.poc) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validation Error',
            text: 'Please select a POC',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      try {
        const userData = JSON.parse(localStorage.getItem("userData"))
        const user = `[{"title" : "${userData.first_name} ${userData.last_name}", "emp_id": "${userData.emp_id}"}]`
        const username = `${userData.first_name} ${userData.last_name}`

        // Handle POC assignment for all selected pool candidates
        const selectedPOC = this.pocOptions.find(p => p.value === this.poolBulkAssignmentForm.poc)
        const pocTitle = selectedPOC ? selectedPOC.text : this.poolBulkAssignmentForm.poc

        for (const candidate of this.selectedPoolCandidates) {
          // Get existing extra_details or initialize empty object
          let existingExtraDetails = {}
          if (candidate.extra_details) {
            try {
              existingExtraDetails = typeof candidate.extra_details === 'string' 
                ? JSON.parse(candidate.extra_details) 
                : candidate.extra_details
            } catch (error) {
              console.error('Error parsing extra_details:', error)
              existingExtraDetails = {}
            }
          }
          
          const newAssignmentData = {
            ...existingExtraDetails,
            poc: pocTitle,
            assignedBy: username,
            assignedAt: new Date().toISOString()
          }
          
          const payload = {
            candidateStatus: candidate.candidateStatus || 'Shortlisted',
            updatedBy: user,
            extra_details: newAssignmentData
          }
          
          try {
            const pocResponse = await candidateService.updateCandidateStatus(
              candidate.id || candidate.cid,
              candidate.job_id || candidate.jid,
              payload
            )
            
            if (pocResponse.status === 'OK') {
              // Update local state
              const candidateIndex = this.reportsCandidates.findIndex(c => c.id === candidate.id)
              if (candidateIndex !== -1) {
                this.reportsCandidates[candidateIndex].extra_details = newAssignmentData
              }
            }
          } catch (error) {
            console.error(`Error assigning POC to pool candidate ${candidate.id}:`, error)
          }
        }

        // Create timeline entries
        for (const candidate of this.selectedPoolCandidates) {
          const timelineData = {
            title: `Candidate Name - ${candidate.candidate_name} - Bulk Assigned to POC via ${this.selectedClientName} Pool`,
            description: `CS-Bulk Assign to POC via ${this.selectedClientName} Pool`,
            type: "success",
            createdBy: username,
            jobId: candidate.job_id,
            candidate_id: candidate.id,
            candidateStatus: 'Shortlisted'
          }
          
          await jobTimelineService.create(timelineData)
        }

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            text: `${this.selectedPoolCandidates.length} pool candidates assigned to POC successfully`,
            icon: 'CheckIcon',
            variant: 'success',
          },
        })

        this.showPoolBulkModal = false
        this.clearPoolSelection()
        this.resetPoolBulkAssignmentForm()
      } catch (error) {
        console.error('Error bulk assigning pool candidates:', error)
        const errorMessage = error.response?.data?.message || error.response?.status === 410 
          ? 'Some pool candidates may already be assigned. Please check individual candidates.' 
          : error.message || 'Failed to assign pool candidates to POC'
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error.response?.status === 410 ? 'Warning' : 'Error',
            text: errorMessage,
            icon: 'AlertTriangleIcon',
            variant: error.response?.status === 410 ? 'warning' : 'danger',
          },
        })
      }
    },

    onPoolPageChange(page) {
      this.poolCurrentPage = page
    },

    // Reports methods
    selectReportType(type) {
      this.selectedReportType = type
    },

    clearReportFilters() {
      this.reportFilters = {
        startDate: null,
        endDate: null,
        status: []
      }
      this.selectedReportType = null
    },

    getReportTypeName() {
      const names = {
        locations: 'Locations Wise',
        status: 'Status Wise',
        assignedTo: 'Assigned To Wise',
        position_name: 'Position Name Wise'
      }
      return names[this.selectedReportType] || 'Report'
    },

    getReportIcon(type) {
      const icons = {
        location: 'feather icon-map-pin',
        status: 'feather icon-activity',
        recruiter: 'feather icon-users',
        position: 'feather icon-briefcase'
      }
      return icons[type] || 'feather icon-file-text'
    },

    getReportTabTitle(report) {
      const typeNames = {
        location: 'Location',
        status: 'Status',
        recruiter: 'Recruiter',
        position: 'Position'
      }
      return typeNames[report.type] || 'Report'
    },

    getReportFields(type) {
      const fields = {
        location: [
          { key: 'location', label: 'Location', sortable: true },
          { key: 'count', label: 'Count', sortable: true },
          { key: 'percentage', label: 'Percentage', sortable: true }
        ],
        status: [
          { key: 'status', label: 'Status', sortable: true },
          { key: 'count', label: 'Count', sortable: true },
          { key: 'percentage', label: 'Percentage', sortable: true }
        ],
        recruiter: [
          { key: 'recruiter', label: 'Recruiter', sortable: true },
          { key: 'count', label: 'Count', sortable: true },
          { key: 'percentage', label: 'Percentage', sortable: true }
        ],
        position: [
          { key: 'position', label: 'Position', sortable: true },
          { key: 'count', label: 'Count', sortable: true },
          { key: 'percentage', label: 'Percentage', sortable: true }
        ]
      }
      return fields[type] || []
    },

    async generateSelectedReport(suppressToast = false) {
      if (!this.selectedReportType) return

      try {
        this.reportsLoading = true
        this.reportsError = null

        // Ensure we have the main candidates data loaded
        if (this.candidates.length === 0) {
          await this.loadCandidates()
        }

        // Get ALL candidates data for report generation (not just pool data)
        let allCandidates = this.candidates
        
        // If main candidates data is still not available, try pool data as fallback
        if (allCandidates.length === 0 && this.reportsCandidates.length > 0) {
          allCandidates = this.reportsCandidates
        }
        
        
        // console.log('Report generation debug:', {
        //   selectedReportType: this.selectedReportType,
        //   reportsCandidatesCount: this.reportsCandidates.length,
        //   candidatesCount: this.candidates.length,
        //   allCandidatesCount: allCandidates.length,
        //   reportFilters: this.reportFilters,
        //   sampleCandidate: allCandidates[0]
        // })
        
        // If no data is available, show error but don't block default report generation
        if (allCandidates.length === 0) {
          // For default reports, we'll generate empty reports to show the structure
          if (!this.reportFilters.startDate && !this.reportFilters.endDate && (!this.reportFilters.status || this.reportFilters.status.length === 0)) {
            const emptyReport = {
              id: Date.now(),
              type: this.selectedReportType,
              title: `${this.getReportTypeName()} Report - ${new Date().toLocaleDateString()}`,
              data: [],
              totalRecords: 0,
              generatedAt: new Date().toISOString(),
              filters: { ...this.reportFilters }
            }
            this.generatedReports.unshift(emptyReport)
            this.activeReportTab = 0
            return
          } else {
            this.reportsError = 'No candidate data available. Please ensure all candidate data is loaded.'
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'No Data',
                text: 'No candidate data available for report generation. Please refresh the data.',
                icon: 'AlertTriangleIcon',
                variant: 'warning',
              },
            })
            return
          }
        }
        
        // Filter candidates based on report filters
        let filteredCandidates = allCandidates
        
        if (this.reportFilters.status && this.reportFilters.status.length > 0) {
          filteredCandidates = filteredCandidates.filter(candidate => 
            this.reportFilters.status.includes(candidate.candidateStatus)
          )
        }

        if (this.reportFilters.startDate) {
          filteredCandidates = filteredCandidates.filter(candidate => {
            if (!candidate.createdAt) return false
            const candidateDate = new Date(candidate.createdAt)
            const startDate = new Date(this.reportFilters.startDate)
            return candidateDate >= startDate
          })
        }

        if (this.reportFilters.endDate) {
          filteredCandidates = filteredCandidates.filter(candidate => {
            if (!candidate.createdAt) return false
            const candidateDate = new Date(candidate.createdAt)
            const endDate = new Date(this.reportFilters.endDate)
            // Set end date to end of day for inclusive filtering
            endDate.setHours(23, 59, 59, 999)
            return candidateDate <= endDate
          })
        }

        // Generate report data based on type
        const reportData = this.generateReportData(this.selectedReportType, filteredCandidates)
        
        
        // Create report object
        const report = {
          id: Date.now(),
          type: this.selectedReportType,
          title: `${this.getReportTypeName()} Report - ${new Date().toLocaleDateString()}`,
          data: reportData,
          totalRecords: reportData.length,
          generatedAt: new Date().toISOString(),
          filters: { ...this.reportFilters }
        }

        // Remove old reports of the same type and add new report
        this.generatedReports = this.generatedReports.filter(r => r.type !== this.selectedReportType)
        this.generatedReports.unshift(report)
        
        // Set active tab to the newly generated report
        this.activeReportTab = 0

        // Only show toast if not suppressed (e.g., when called from generateAllReports)
        if (!suppressToast) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              text: `${this.getReportTypeName()} report generated successfully`,
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        }

      } catch (error) {
        console.error('Error generating report:', error)
        this.reportsError = 'Failed to generate report. Please try again.'
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Failed to generate report',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.reportsLoading = false
      }
    },

    generateReportData(type, candidates) {
      const totalCandidates = candidates.length
      const groupedData = {}


      // Group candidates by the selected criteria
      candidates.forEach(candidate => {
        let key = ''
        
        switch (type) {
          case 'location':
            key = candidate.locations || candidate.location || 'Unknown Location'
            break
          case 'status':
            key = candidate.candidateStatus || 'Unknown Status'
            break
          case 'recruiter':
            key = candidate.assignedTo || candidate.recruiter || 'Unassigned'
            break
          case 'position':
            key = candidate.position_name || 'Unknown Position'
            break
        }

        if (!groupedData[key]) {
          groupedData[key] = 0
        }
        groupedData[key]++
      })


      // Convert to array format with percentages
      return Object.entries(groupedData)
        .map(([key, count]) => ({
          [type === 'location' ? 'location' : 
           type === 'status' ? 'status' : 
           type === 'recruiter' ? 'recruiter' : 'position']: key,
          count,
          percentage: totalCandidates > 0 ? Math.round((count / totalCandidates) * 100) : 0
        }))
        .sort((a, b) => b.count - a.count)
    },

    async generateDefaultReports() {
      try {
        this.reportsLoading = true
        this.reportsError = null
        
        // Use already loaded data (data is loaded in mounted() before this is called)
        
        // Only reload if data is missing
        if (this.candidates.length === 0) {
          await this.loadCandidates()
        }
        if (this.reportsCandidates.length === 0) {
          await this.loadReports()
        }
        
        // Clear existing reports
        this.generatedReports = []
        this.activeReportTab = 0
        
        // Clear any filters for default reports
        const originalFilters = { ...this.reportFilters }
        this.reportFilters = {
          startDate: null,
          endDate: null,
          status: []
        }
        
        const reportTypes = ['location', 'status', 'recruiter', 'position']
        
        
        for (const type of reportTypes) {
          this.selectedReportType = type
          await this.generateSelectedReport(true) // Suppress individual toasts
          // Small delay between reports
          await new Promise(resolve => setTimeout(resolve, 300))
        }
        
        // Restore original filters
        this.reportFilters = originalFilters
        this.selectedReportType = null
        
        // Show single success toast for all default reports
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            text: `Default reports generated successfully (${reportTypes.length} reports)`,
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        
      } catch (error) {
        console.error('Error generating default reports:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            // text: 'Failed to generate default reports',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.reportsLoading = false
      }
    },

    async generateAllReports() {
      try {
        this.reportsLoading = true
        // Clear existing reports
        this.generatedReports = []
        this.activeReportTab = 0
        
        const reportTypes = ['location', 'status', 'recruiter', 'position']
        
        for (const type of reportTypes) {
          this.selectedReportType = type
          await this.generateSelectedReport(true) // Suppress individual toasts
          // Small delay between reports
          await new Promise(resolve => setTimeout(resolve, 500))
        }
        
        this.selectedReportType = null
        
        // Show single success toast for all reports
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            text: `All reports generated successfully (${reportTypes.length} reports)`,
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        console.error('Error generating all reports:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Failed to generate some reports',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.reportsLoading = false
      }
    },

    viewReport(report) {
      this.selectedReport = report
      this.showReportModal = true
    },

    closeReportModal() {
      this.showReportModal = false
      this.selectedReport = null
    },

    downloadReport(report) {
      try {
        // Create CSV content
        const headers = this.getReportFields(report.type).map(field => field.label).join(',')
        const rows = report.data.map(item => {
          const values = this.getReportFields(report.type).map(field => {
            const key = field.key
            return item[key] || ''
          })
          return values.join(',')
        })
        
        const csvContent = [headers, ...rows].join('\n')
        
        // Create and download file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', `${report.title.replace(/\s+/g, '_')}.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            text: 'Report downloaded successfully',
            icon: 'DownloadIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        console.error('Error downloading report:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Failed to download report',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    }
  },

}
</script>

<style scoped>
.client-dashboard {
  --cd-primary: #00ac99;
  --cd-border: #e3e6f0;
  --cd-surface: #f8fafb;
  --cd-text: #5e5873;
  --cd-muted: #6e6b7b;
  padding: 0.75rem 0.75rem 1.25rem;
}

@media (min-width: 768px) {
  .client-dashboard {
    padding: 0.875rem 1rem 1.5rem;
  }
}

.access-denied-container {
  min-height: 360px;
}

.access-denied-content {
  max-width: 380px;
}

.access-denied-icon {
  color: #6c757d;
}

.dashboard-page-header {
  gap: 0.5rem 0.75rem;
  border-bottom: 1px solid var(--cd-border);
}

.dashboard-page-header__actions {
  gap: 0.35rem;
}

.dashboard-page-header__admin {
  padding: 0.25rem 0.6rem;
  background: var(--cd-surface);
  border: 1px solid var(--cd-border);
  border-radius: 6px;
}

.dashboard-action-btn {
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.dashboard-loading {
  background: var(--cd-surface);
  border: 1px solid var(--cd-border);
  border-radius: 8px;
}

.metrics-strip {
  width: 100%;
  gap: 0.4rem;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 0.15rem;
}

.metric-pill {
  flex: 1 1 0;
  min-width: 110px;
  min-height: 64px;
  background: #fff;
  border: 1px solid var(--cd-border);
  border-radius: 6px;
  padding: 0.65rem 0.7rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.metric-pill__value {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--cd-primary);
  line-height: 1.15;
  text-align: center;
}

.metric-pill__label {
  font-size: 0.7rem;
  font-weight: 500;
  color: var(--cd-muted);
  margin-top: 0.15rem;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  white-space: nowrap;
  text-align: center;
}

.candidates-card {
  border: none;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.bulk-actions {
  background: #f8f9fa;
  padding: 0.5rem 0.65rem;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.selection-indicator {
  display: flex;
  align-items: center;
}

.selection-count {
  font-weight: 500;
  color: #495057;
}

.table-controls {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.search-container {
  position: relative;
}

.search-section {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid #e9ecef;
}

.search-section .form-group {
  margin-bottom: 0;
}

/* Pool filters (modal) */
.pool-toolbar__label {
  display: block;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--cd-muted, #6e6b7b);
  margin-bottom: 0.15rem;
}

.pool-header__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.35rem;
  min-width: 0;
  max-width: 100%;
}

.pool-header__search {
  position: relative;
  width: min(220px, 46vw);
  min-width: 120px;
  max-width: 260px;
  flex: 1 1 140px;
}

.pool-header__search-input--clearable {
  padding-right: 1.75rem;
}

.pool-header__search-clear {
  position: absolute;
  top: 50%;
  right: 0.35rem;
  z-index: 5;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--cd-muted, #6e6b7b);
  line-height: 1;
  cursor: pointer;
}

.pool-header__search-clear:hover,
.pool-header__search-clear:focus {
  color: var(--cd-text, #5e5873);
  outline: none;
}

@media (max-width: 575.98px) {
  .pool-header__actions {
    width: 100%;
    margin-top: 0.35rem;
  }

  .pool-header__search {
    width: 100%;
    max-width: none;
    flex: 1 1 auto;
  }
}

.pool-filters-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none !important;
  box-shadow: none !important;
  background: transparent;
  color: var(--cd-primary, #00ac99) !important;
  font-weight: 600;
  text-decoration: none !important;
  padding: 0.35rem 0.55rem;
  border-radius: 6px;
  min-height: auto;
  flex-shrink: 0;

  &:hover,
  &:focus {
    background: rgba(0, 172, 153, 0.08);
    color: var(--cd-primary, #00ac99) !important;
    text-decoration: none !important;
  }

  &:active {
    background: rgba(0, 172, 153, 0.12);
  }
}

.pool-filters-btn__dot {
  position: absolute;
  top: 0.2rem;
  right: 0.2rem;
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 50%;
  background: var(--cd-primary, #00ac99);
  box-shadow: none;
}

.pool-filters-modal {
  .modal-title {
    font-size: 1rem;
    font-weight: 650;
    color: var(--cd-text, #5e5873);
  }

  .modal-body {
    padding: 0.75rem 1rem 1rem;
  }
}

.pool-filters-modal__field {
  margin-bottom: 0.65rem;
}

.pool-card {
  border: 1px solid var(--cd-border);
  border-radius: 8px;
}

.pool-header {
  background: #fff;
  border-bottom: 1px solid var(--cd-border);
}

.pool-count-badge {
  font-weight: 600;
}

.pool-table-scroll {
  max-height: min(65vh, 640px);
  border-top: 1px solid var(--cd-border, #e3e6f0);
}

.pool-table.table-bordered,
.pool-table.table-bordered th,
.pool-table.table-bordered td {
  border-color: var(--cd-border, #e3e6f0);
}

.pool-table.table-bordered th.border-right-0,
.pool-table.table-bordered td.border-right-0 {
  border-right: 0 !important;
}

.pool-table.table-bordered th.border-left-0,
.pool-table.table-bordered td.border-left-0 {
  border-left: 0 !important;
}

.pool-table ::v-deep thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: #f3f2f7 !important;
  color: #5e5873 !important;
  font-weight: 600;
  font-size: inherit;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  white-space: nowrap;
  padding: 0.4rem 0.55rem;
}

.pool-table ::v-deep tbody td {
  padding: 0.35rem 0.55rem;
  vertical-align: middle;
}

.pool-table ::v-deep tbody tr:hover {
  background-color: rgba(0, 172, 153, 0.04);
}

.doc-badge {
  font-weight: 600;
  padding: 0.2rem 0.35rem !important;
}

.pool-pagination {
  border-top: 1px solid var(--cd-border, #e3e6f0);
  background: #fff;
}

.pool-empty-state {
  padding: 1.75rem 0.75rem;
}

.recruiter-initials-avatar {
  background-color: #e8f5f3 !important;
  color: #2a7a6e !important;
  font-weight: 600;
  cursor: default;
}

.status-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.15rem;
}

.status-badge {
  display: inline-block;
  font-weight: 600;
  padding: 0.25rem 0.45rem;
  width: fit-content;
  white-space: nowrap;
  border: none;
  border-radius: 4px;
  line-height: 1.2;
}

/* Candidate Pool status pills — light fill, colored text, no border */
.status-badge--shortlisted {
  background-color: #fff8e6;
  color: #9a7b0a;
}

.status-badge--offered {
  background-color: #eef5fb;
  color: #3b6d99;
}

.status-badge--offer-generated {
  background-color: #f0eefb;
  color: #5b52a0;
}

.offer-gen-count {
  font-weight: 500;
  opacity: 0.85;
}

.status-badge--offer-sent {
  background-color: #e8f5f3;
  color: #007a8a;
}

.status-badge--not-sent {
  background-color: #fef3ec;
  color: #b85c38;
}

.status-badge--joined {
  background-color: #eef8f0;
  color: #2d7a46;
}

.status-badge--not-offered {
  background-color: #fdf2f2;
  color: #c0392b;
}

.status-badge--offer-drop {
  background-color: #f3f5f7;
  color: #5e6e82;
}

.status-badge--default {
  background-color: #f3f5f7;
  color: #6e6b7b;
}

/* Offer generation flow */
.offer-flow-steps {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  flex-wrap: wrap;
  padding: 0.4rem 0.55rem;
  background: #f3f2f7;
  border-radius: 6px;
}

.offer-flow-step {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  color: #b9b9c3;
  font-size: 0.75rem;
  font-weight: 500;
}

.offer-flow-step--active {
  color: #5e5873;
}

.offer-flow-step__num {
  width: 1.2rem;
  height: 1.2rem;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  font-weight: 700;
  background: #e7e7e8;
  color: inherit;
}

.offer-flow-step--active .offer-flow-step__num {
  background: #00ac99;
  color: #fff;
}

.offer-flow-step-divider {
  width: 1rem;
  height: 2px;
  background: #d8d6de;
  margin: 0 0.1rem;
}

.offer-candidate-banner {
  padding: 0.5rem 0.65rem;
  background: rgba(0, 172, 153, 0.06);
  border: 1px solid rgba(0, 172, 153, 0.2);
  border-radius: 6px;
}

.offer-section-title {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #5e5873;
}

.reports-section {
  background: #fff;
  border: 1px solid var(--cd-border, #e3e6f0);
  border-radius: 8px;
  padding: 0.65rem 0.75rem 0.35rem;
}

.reports-section__header {
  border-bottom: 1px solid var(--cd-border, #e3e6f0);
}

.report-panel {
  background: var(--cd-surface, #f8fafb);
  border: 1px solid var(--cd-border, #e3e6f0);
  border-radius: 6px;
  padding: 0.55rem 0.65rem;
  height: 100%;
}

.report-panel__title {
  font-size: 0.8rem;
  font-weight: 600;
  color: #5e5873;
  display: flex;
  align-items: center;
}

@media (max-width: 991.98px) {
  .dashboard-page-header__actions {
    width: 100%;
  }

  .dashboard-page-header__actions .btn {
    flex: 1 1 auto;
  }

  .metric-pill {
    flex: 0 0 118px;
    max-width: none;
  }

  .pool-table {
    min-width: 640px;
  }
}

@media (max-width: 575.98px) {
  .client-dashboard {
    padding: 0.5rem 0.5rem 1rem;
  }

  .dashboard-action-btn {
    min-height: 42px;
  }

  .pool-filters-modal .form-control,
  .pool-filters-modal .custom-select,
  .pool-filters-modal .btn {
    min-height: 40px;
  }

  .metric-pill {
    flex: 0 0 110px;
    padding: 0.55rem 0.55rem;
  }

  .pool-table {
    min-width: 520px;
  }
}

/* Full-width friendly modals on small screens */
.client-dashboard ::v-deep .cd-modal .modal-dialog {
  margin: 0.5rem;
  max-width: calc(100% - 1rem);
}

@media (max-width: 575.98px) {
  .client-dashboard ::v-deep .cd-modal .modal-dialog {
    margin: 0.35rem;
    max-width: calc(100% - 0.7rem);
  }
}

.candidates-table {
  border-radius: 8px;
  overflow: hidden;
}

/* Table header styling - sober light colors */
.candidates-table ::v-deep .table thead th {
  background-color: #f8f9fa !important;
  color: #495057 !important;
  font-weight: 600;
  border-bottom: 1px solid #dee2e6;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
  padding: 0.75rem 1rem;
}

.candidates-table ::v-deep .table thead th:hover {
  background-color: #f1f3f5 !important;
}

/* Pool table header styling - sober light colors */
.intellismart-dashboard ::v-deep .table thead th {
  background-color: #f8f9fa !important;
  color: #495057 !important;
  font-weight: 600;
  border-bottom: 1px solid #dee2e6;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
  padding: 0.75rem 1rem;
}

.intellismart-dashboard ::v-deep .table thead th:hover {
  background-color: #f1f3f5 !important;
}

/* Enhanced Table Controls Styling */
.enhanced-table-controls {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid #e9ecef;
}

.controls-header h6 {
  font-weight: 600;
  color: #495057;
}

.modern-select {
  border-radius: 6px;
  border: 1px solid #ced4da;
  transition: all 0.2s ease;
}

.modern-select:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.modern-input {
  border-radius: 6px;
  border: 1px solid #ced4da;
  transition: all 0.2s ease;
}

.modern-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.modern-vselect {
  border-radius: 6px;
}

.modern-vselect .vs__dropdown-toggle {
  border-radius: 6px;
  border: 1px solid #ced4da;
  transition: all 0.2s ease;
}

.modern-vselect .vs__dropdown-toggle:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.clear-filters-btn {
  border-radius: 6px;
  font-weight: 500;
}

.active-filters {
  background: #e3f2fd;
  border-radius: 8px;
  padding: 0.75rem;
  border: 1px solid #bbdefb;
}

.filter-tags .badge {
  cursor: pointer;
  transition: all 0.2s ease;
}

.filter-tags .badge:hover {
  opacity: 0.8;
  transform: scale(1.05);
}

.form-label {
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.input-group-text {
  background: #f8f9fa;
  border: 1px solid #ced4da;
  color: #6c757d;
}

.search-container .input-group {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  overflow: hidden;
}

.filter-row {
  background: white;
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid #e9ecef;
}

.candidate-info a:hover .candidate-name,
.candidate-info a:hover .candidate-id,
.candidate-info a:hover .candidate-position {
  text-decoration: underline;
}

.candidate-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.candidate-name {
  font-weight: 500;
  color: #495057;
  line-height: 1.25;
}

.candidate-position {
  font-weight: 500;
  line-height: 1.25;
  margin-top: 0.15rem;
  word-break: break-word;
}

.candidate-id {
  font-size: 0.75rem;
}

.position-info a:hover .position-name,
.position-info a:hover .job-id {
  text-decoration: underline;
}

.position-info {
  display: flex;
  flex-direction: column;
}

.position-name {
  font-weight: 500;
  color: #495057;
}

.position-details {
  font-size: 0.75rem;
}

.assigned-info {
  display: flex;
  align-items: center;
}

.assigned-user {
  display: flex;
  align-items: center;
}

.offer-stage {
  min-width: 120px;
}

.action-buttons {
  display: flex;
  justify-content: center;
}

.pagination-info {
  color: #6c757d;
  font-size: 0.875rem;
}

/* Offer Letter Styling */
.offer-letter-header {
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 1rem;
}

.offer-letter-content {
  max-height: 70vh;
  overflow-y: auto;
}

/* Document Validation Styling */
.document-validation {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid #e9ecef;
}

.document-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.5rem;
}

.document-item {
  padding: 0.5rem;
  background: white;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}

.recruiter-confirmation-strip {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.35rem;
  padding: 0.5rem 0.75rem;
  background: rgba(0, 172, 153, 0.04);
  border: 1px solid rgba(0, 172, 153, 0.22);
  border-radius: 6px;
}

.recruiter-confirmation-strip__label {
  margin: 0;
  line-height: 1.2;
}

.recruiter-confirmation-strip__checks {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.recruiter-confirmation-strip .custom-control {
  position: relative;
  display: block;
  margin: 0;
  min-height: 1.25rem;
  padding-left: 1.5rem;
  font-size: 0.8rem;
  line-height: 1.35;
}

.recruiter-confirmation-strip .custom-control-label {
  position: static;
  margin-bottom: 0;
  vertical-align: top;
}

.recruiter-confirmation-strip .custom-control-label::before,
.recruiter-confirmation-strip .custom-control-label::after {
  top: 0.1rem;
  left: -1.5rem;
}

.letter-body {
  background: #fff;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.letter-header h4 {
  font-weight: 700;
  letter-spacing: 2px;
}

.letter-content {
  font-size: 14px;
  line-height: 1.8;
}

.offer-details {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  border-left: 4px solid #007bff;
}

.terms-conditions {
  background: #fff3cd;
  padding: 1.5rem;
  border-radius: 8px;
  border-left: 4px solid #ffc107;
}

.letter-footer {
  border-top: 1px solid #e9ecef;
  padding-top: 1rem;
  margin-top: 2rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .table-controls {
    flex-direction: column;
    gap: 1rem;
  }
  
  .table-controls > div {
    width: 100%;
  }
  
  .search-container {
    width: 100%;
  }
  
  .search-container .input-group {
    width: 100%;
  }
  
  .letter-body {
    padding: 1rem;
  }
  
  .offer-letter-content {
    max-height: 60vh;
  }

  .document-grid {
    grid-template-columns: 1fr;
  }
}

/* Pool section styling */
.pool-card {
  border: 1px solid #e3e6f0;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(34, 41, 47, 0.06);
  margin-top: 0.5rem;
  overflow: hidden;
}

.pool-header {
  background: #fff;
  color: #5e5873;
  border-bottom: 1px solid #e3e6f0;
  padding: 1rem 1.15rem;
}

.pool-header h5 {
  color: #5e5873;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.pool-header .text-muted {
  color: #6e6b7b !important;
}

/* Reports (legacy hooks; template uses flattened panels) */
.reports-card {
  border: 1px solid #e3e6f0;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(34, 41, 47, 0.06);
}

.report-card {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  height: 100%;
}

.report-card-header {
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  padding: 0.85rem 1rem;
}

.report-card-header h6 {
  color: #5e5873;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
}

/* Account Details Badge - Sober Light Colors */
.account-details-yes {
  background-color: #d4edda !important;
  color: #155724 !important;
  border: 1px solid #c3e6cb;
  font-weight: 500;
  font-size: 0.75rem !important;
  padding: 0.125rem 0.5rem !important;
}

.account-details-no {
  background-color: #f8d7da !important;
  color: #721c24 !important;
  border: 1px solid #f5c6cb;
  font-weight: 500;
  font-size: 0.75rem !important;
  padding: 0.125rem 0.5rem !important;
}

.account-details-badge {
  margin-top: 0.25rem;
}

/* Documents Preview Section */
.documents-preview-section {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  border: 1px solid #e9ecef;
}

.document-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.document-card {
  background: white;
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.document-card-missing {
  background: #fee;
  border: 2px solid #f5c6cb;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.2);
}

.document-card-missing .document-card-header {
  color: #721c24;
}

.document-card-header {
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
}

.document-preview {
  margin-top: 0.5rem;
}

.document-missing-message {
  margin-top: 0.75rem;
  padding: 0.5rem;
  background: rgba(220, 53, 69, 0.1);
  border-radius: 4px;
  border-left: 3px solid #dc3545;
}

/* Document badge styling for missing documents in table */
.document-badge-missing {
  background-color: #dc3545 !important;
  border-color: #dc3545 !important;
  color: white !important;
}

/* Report type selection */
.report-type-selection {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  border: 1px solid #e9ecef;
}

.report-type-card {
  background: white;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 100%;
}

.report-type-card:hover {
  border-color: #007bff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.15);
}

.report-type-card.active {
  border-color: #28a745;
  background: #f8fff9;
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.15);
}

.report-icon {
  font-size: 2rem;
  color: #6c757d;
  margin-bottom: 1rem;
}

.report-type-card.active .report-icon {
  color: #28a745;
}

.report-type-card h6 {
  margin-bottom: 0.5rem;
  font-weight: 600;
}

/* Report filters */
.report-filters {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  border: 1px solid #e9ecef;
}

/* Generated reports */
.generated-reports {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  border: 1px solid #e9ecef;
}

/* Report tabs */
.report-tabs {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.report-tabs .nav-tabs {
  border-bottom: 2px solid #e9ecef;
  background: #f8f9fa;
  border-radius: 8px 8px 0 0;
  margin: 0;
}

.report-tabs .nav-tabs .nav-link {
  border: none;
  border-radius: 0;
  padding: 1rem 1.5rem;
  color: #6c757d;
  font-weight: 500;
  transition: all 0.3s ease;
}

.report-tabs .nav-tabs .nav-link:hover {
  border-color: transparent;
  background: #e9ecef;
  color: #495057;
}

.report-tabs .nav-tabs .nav-link.active {
  background: white;
  color: #28a745;
  border-bottom: 3px solid #28a745;
  font-weight: 600;
}

.report-tabs .tab-content {
  padding: 1.5rem;
  background: white;
  border-radius: 0 0 8px 8px;
}

/* Report data section */
.report-data-section {
  background: white;
  border-radius: 8px;
  padding: 1rem;
}

.report-data-table {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.report-data-table .table {
  margin-bottom: 0;
}

.report-data-table .table th {
  background: #f8f9fa;
  border-top: none;
  font-weight: 600;
  color: #495057;
  padding: 1rem;
}

.report-data-table .table td {
  vertical-align: middle;
  padding: 0.75rem 1rem;
}

.report-card {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1rem;
  transition: all 0.3s ease;
}

.report-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.report-header {
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: 1rem;
}

.report-title {
  font-weight: 600;
  color: #495057;
  flex: 1;
}

.report-actions {
  display: flex;
  gap: 0.5rem;
}

.report-stats {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.stat-label {
  font-size: 0.875rem;
  color: #6c757d;
}

.stat-value {
  font-weight: 600;
  color: #495057;
}

/* Report preview modal */
.report-preview-header {
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 1rem;
}

.report-table {
  border-radius: 8px;
  overflow: hidden;
}

.report-table .table {
  margin-bottom: 0;
}

.report-table .table th {
  background: #f8f9fa;
  border-top: none;
  font-weight: 600;
  color: #495057;
}

.report-table .table td {
  vertical-align: middle;
}

/* Progress bar styling */
.progress {
  background-color: #e9ecef;
  border-radius: 4px;
}

.progress-bar {
  background-color: #007bff;
  border-radius: 4px;
  transition: width 0.3s ease;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .report-type-card {
    margin-bottom: 1rem;
  }
  
  .report-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  
  .report-actions {
    width: 100%;
    justify-content: flex-end;
  }
}

/* Bank proof file name wrapping */
.file-name-badge {
  max-width: 100%;
  word-break: break-word;
  white-space: normal;
  display: inline-block;
  overflow-wrap: break-word;
}

.file-selected-badge {
  max-width: 100%;
  word-break: break-word;
  white-space: normal;
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
}

.file-selected-text {
  word-break: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
}

.file-name-text {
  word-break: break-all;
  overflow-wrap: break-word;
  max-width: 100%;
  display: inline-block;
}

/* Actions dropdown uses positionFixed; keep menu above table chrome */
.client-dashboard ::v-deep .pool-table .dropdown-menu {
  z-index: 1050;
}
</style>